import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import SouthEastRoundedIcon from '@mui/icons-material/SouthEastRounded';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PatternGlossaryDrawer from './PatternGlossaryDrawer';
import PatternLearningDrawer from './PatternLearningDrawer';
import { getPatternTitle, managedPatternOptions } from './patternTutorialData';

const TELEGRAM_CHAT_ID_STORAGE_PREFIX = 'ecotoneai_pattern_telegram_chat_id_';
const PATTERN_SCANNER_COMPACT_PRO_STORAGE_KEY = 'ecotoneai_pattern_scanner_compact_pro';
const TELEGRAM_NUMERIC_CHAT_ID_PATTERN = /^-?\d{5,}$/;
const TELEGRAM_USERNAME_CHAT_ID_PATTERN = /^@[A-Za-z][A-Za-z0-9_]{4,31}$/;
const DEFAULT_ANNOTATED_IMAGE_CANDLES = 120;
const ANNOTATED_IMAGE_CANDLE_OPTIONS = [DEFAULT_ANNOTATED_IMAGE_CANDLES, 80];

const defaultSingleState = {
  symbol: 'TAO',
  exchange: 'BINANCE',
  timeframe: '4H',
  candles: 450,
  imageCandles: DEFAULT_ANNOTATED_IMAGE_CANDLES,
};

const defaultFilters = {
  universe: 'top100',
  radarTimeframe: defaultSingleState.timeframe,
  radarSortMode: 'GLOBAL',
  patternType: '',
  minScore: 65,
  minConviction: 0,
  advancedSignalsOnly: false,
  minConfidence: 0,
  minPumpProbability: 0,
  bullishOnly: false,
  bearishOnly: false,
  topMoversOnly: false,
  sector: '',
};

const fallbackSingle = {
  symbol: 'TAO',
  exchange: 'BINANCE',
  timeframe: '4H',
  patternDetected: 'CUP_AND_HANDLE',
  confidence: 0.84,
  pumpProbability: 76,
  dumpProbability: 17,
  fakeoutRisk: 28,
  breakoutLevel: 164.25,
  targetPrice: 181.6,
  stopInvalidation: 156.4,
  riskRewardRatio: 2.21,
  targetConfidence: 0.78,
  volumeConfirmation: true,
  volumeConfirmationScore: 0.82,
  breakoutVolumeRatio: 2.1,
  volumeTrend: 'CONTRACTING',
  volumeComment: 'Breakout valide par un volume 2.1x superieur a la moyenne.',
  fakeoutRiskAdjusted: 0.22,
  divergenceType: 'HIDDEN_BULLISH',
  divergenceStrength: 0.81,
  momentumState: 'BULLISH_ACCELERATION',
  momentumStrength: 0.76,
  candlestickPattern: 'HAMMER',
  candlestickStrength: 0.72,
  convictionScore: 84,
  signalFusionState: 'HIGH_CONVICTION_SETUP',
  aiNarrative: 'Convergence haussiere multi-signal detectee: structure de continuation, volume propre, divergence cachee et chandelier tactique de reprise.',
  convergenceSignals: [
    'Structure Cup And Handle',
    'Volume confirme la structure',
    'Divergence hidden bullish',
    'Momentum bullish acceleration',
    'Chandelier Hammer',
  ],
  timeToResolution: '12-24h',
  aiSummary: 'Base arrondie puis handle court, avec contexte de continuation haussiere encore propre.',
  analysisSource: 'MOCK_FALLBACK',
  patternScore: 81,
  direction: 'BULLISH',
  analysisTimestamp: null,
  imagePath: '',
  imageGeneratedAt: null,
  imageVersion: '',
};

const fallbackScanner = [
  {
    symbol: 'TAO',
    name: 'Bittensor',
    exchange: 'BINANCE',
    timeframe: '4H',
    detectedPattern: 'CUP_AND_HANDLE',
    confidence: 0.84,
    pumpProbability: 76,
    dumpProbability: 17,
    fakeoutRisk: 28,
    breakoutLevel: 164.25,
    targetPrice: 181.6,
    stopInvalidation: 156.4,
    riskRewardRatio: 2.21,
    targetConfidence: 0.78,
    volumeConfirmation: true,
    volumeConfirmationScore: 0.82,
    breakoutVolumeRatio: 2.1,
    volumeTrend: 'CONTRACTING',
    volumeComment: 'Breakout valide par un volume 2.1x superieur a la moyenne.',
    fakeoutRiskAdjusted: 0.22,
    divergenceType: 'HIDDEN_BULLISH',
    divergenceStrength: 0.81,
    momentumState: 'BULLISH_ACCELERATION',
    momentumStrength: 0.76,
    candlestickPattern: 'HAMMER',
    candlestickStrength: 0.72,
    convictionScore: 84,
    signalFusionState: 'HIGH_CONVICTION_SETUP',
    aiNarrative: 'Convergence haussiere multi-signal detectee: structure de continuation, volume propre, divergence cachee et chandelier tactique de reprise.',
    convergenceSignals: [
      'Structure Cup And Handle',
      'Volume confirme la structure',
      'Divergence hidden bullish',
      'Momentum bullish acceleration',
      'Chandelier Hammer',
    ],
    scoreGlobal: 88,
    multiTimeframeScore: 86,
    multiTimeframeDetail: {
      alignmentScore: 82,
      convictionScore: 84,
      volumeAlignmentScore: 78,
      dominantDirection: 'BULLISH',
      dominantTimeframe: '1D',
      timeframes: [
        { timeframe: '1H', patternScore: 79, convictionScore: 76, confidence: 0.81, direction: 'BULLISH', volumeTrend: 'EXPANDING', volumeConfirmation: true },
        { timeframe: '4H', patternScore: 84, convictionScore: 84, confidence: 0.84, direction: 'BULLISH', volumeTrend: 'CONTRACTING', volumeConfirmation: true },
        { timeframe: '1D', patternScore: 87, convictionScore: 82, confidence: 0.78, direction: 'BULLISH', volumeTrend: 'EXPANDING', volumeConfirmation: true },
      ],
    },
    priorityRank: 1,
    bullish: true,
    bearish: false,
    aiSummary: 'Base arrondie, handle court, breakout encore defendable.',
    analysisTimestamp: null,
  },
  {
    symbol: 'INJ',
    name: 'Injective',
    exchange: 'BINANCE',
    timeframe: '4H',
    detectedPattern: 'DOUBLE_BOTTOM',
    confidence: 0.79,
    pumpProbability: 71,
    dumpProbability: 20,
    fakeoutRisk: 33,
    breakoutLevel: 29.45,
    targetPrice: 32.1,
    stopInvalidation: 27.98,
    riskRewardRatio: 1.8,
    targetConfidence: 0.74,
    volumeConfirmation: true,
    volumeConfirmationScore: 0.68,
    breakoutVolumeRatio: 1.4,
    volumeTrend: 'EXPANDING',
    volumeComment: 'Participation correcte, confirmation encore exploitable.',
    fakeoutRiskAdjusted: 0.29,
    divergenceType: 'REGULAR_BULLISH',
    divergenceStrength: 0.64,
    momentumState: 'WEAK_BULLISH_MOMENTUM',
    momentumStrength: 0.61,
    candlestickPattern: 'PIERCING_LINE',
    candlestickStrength: 0.58,
    convictionScore: 71,
    signalFusionState: 'CONFIRMED_CONVERGENCE',
    aiNarrative: 'La convergence haussiere reste exploitable, mais le momentum demande encore une relance plus nette.',
    convergenceSignals: [
      'Structure Double Bottom',
      'Divergence regular bullish',
      'Momentum weak bullish momentum',
      'Chandelier Piercing Line',
    ],
    scoreGlobal: 84,
    multiTimeframeScore: 78,
    multiTimeframeDetail: {
      alignmentScore: 74,
      convictionScore: 76,
      volumeAlignmentScore: 69,
      dominantDirection: 'BULLISH',
      dominantTimeframe: '4H',
      timeframes: [
        { timeframe: '1H', patternScore: 72, convictionScore: 68, confidence: 0.73, direction: 'BULLISH', volumeTrend: 'EXPANDING', volumeConfirmation: true },
        { timeframe: '4H', patternScore: 78, convictionScore: 71, confidence: 0.79, direction: 'BULLISH', volumeTrend: 'EXPANDING', volumeConfirmation: true },
        { timeframe: '1D', patternScore: 80, convictionScore: 74, confidence: 0.7, direction: 'BULLISH', volumeTrend: 'FLAT', volumeConfirmation: false },
      ],
    },
    priorityRank: 2,
    bullish: true,
    bearish: false,
    aiSummary: 'Support defendu a deux reprises avec reprise de momentum.',
    analysisTimestamp: null,
  },
  {
    symbol: 'BTC',
    name: 'Bitcoin',
    exchange: 'BINANCE',
    timeframe: '4H',
    detectedPattern: 'HEAD_AND_SHOULDERS',
    confidence: 0.71,
    pumpProbability: 31,
    dumpProbability: 68,
    fakeoutRisk: 39,
    breakoutLevel: 101800,
    targetPrice: 97800,
    stopInvalidation: 103250,
    riskRewardRatio: 2.31,
    targetConfidence: 0.69,
    volumeConfirmation: false,
    volumeConfirmationScore: 0.39,
    breakoutVolumeRatio: 0.92,
    volumeTrend: 'DIVERGING',
    volumeComment: 'Divergence prix-volume, cassure fragile.',
    fakeoutRiskAdjusted: 0.47,
    divergenceType: 'REGULAR_BEARISH',
    divergenceStrength: 0.67,
    momentumState: 'WEAK_BEARISH_MOMENTUM',
    momentumStrength: 0.57,
    candlestickPattern: 'SHOOTING_STAR',
    candlestickStrength: 0.63,
    convictionScore: 58,
    signalFusionState: 'BUILDING_CONVERGENCE',
    aiNarrative: 'La lecture baissiere se construit, mais le volume diverge encore trop pour parler de pleine conviction.',
    convergenceSignals: [
      'Structure Head And Shoulders',
      'Divergence regular bearish',
      'Momentum weak bearish momentum',
      'Chandelier Shooting Star',
    ],
    scoreGlobal: 72,
    multiTimeframeScore: 64,
    multiTimeframeDetail: {
      alignmentScore: 68,
      convictionScore: 63,
      volumeAlignmentScore: 51,
      dominantDirection: 'BEARISH',
      dominantTimeframe: '4H',
      timeframes: [
        { timeframe: '1H', patternScore: 61, convictionScore: 57, confidence: 0.66, direction: 'BEARISH', volumeTrend: 'DIVERGING', volumeConfirmation: false },
        { timeframe: '4H', patternScore: 67, convictionScore: 58, confidence: 0.71, direction: 'BEARISH', volumeTrend: 'DIVERGING', volumeConfirmation: false },
        { timeframe: '1D', patternScore: 65, convictionScore: 62, confidence: 0.63, direction: 'BEARISH', volumeTrend: 'FLAT', volumeConfirmation: false },
      ],
    },
    priorityRank: 3,
    bullish: false,
    bearish: true,
    aiSummary: 'Structure de sommet plus fragile, neckline a surveiller.',
    analysisTimestamp: null,
  },
];

const emptyScannerProgress = {
  running: false,
  completed: 0,
  total: 0,
  percent: 0,
  currentSymbol: '',
  readyRows: 0,
  errorMessage: '',
};

const CALIBRATION_TIMEFRAMES = ['1H', '4H', '1D'];

function formatConfidence(value) {
  const normalized = value > 1 ? value / 100 : value;
  return Math.round(normalized * 100);
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function directionColor(direction) {
  if (direction === 'BULLISH') return '#1f7a4d';
  if (direction === 'BEARISH') return '#aa3d33';
  return '#8d6c16';
}

function directionSoftBackground(direction) {
  if (direction === 'BULLISH') return 'rgba(31,122,77,0.12)';
  if (direction === 'BEARISH') return 'rgba(170,61,51,0.12)';
  return 'rgba(141,108,22,0.12)';
}

function directionLabel(direction) {
  if (direction === 'BULLISH') return 'Haussier';
  if (direction === 'BEARISH') return 'Baissier';
  return 'Neutre';
}

function directionHeadline(direction) {
  if (direction === 'BULLISH') return 'HAUSSIER ↑';
  if (direction === 'BEARISH') return 'BAISSIER ↓';
  return 'NEUTRE →';
}

function deriveRowDirection(row) {
  if (row?.bullish) {
    return 'BULLISH';
  }
  if (row?.bearish) {
    return 'BEARISH';
  }
  return 'NEUTRAL';
}

function normalizeAnnotatedImageCandles(value) {
  const normalizedValue = Number(value);
  return ANNOTATED_IMAGE_CANDLE_OPTIONS.includes(normalizedValue)
    ? normalizedValue
    : DEFAULT_ANNOTATED_IMAGE_CANDLES;
}

function buildScannerQuery(filters, singleState, options = {}) {
  const query = new URLSearchParams();
  const focusSymbol = typeof options.focusSymbol === 'string'
    ? options.focusSymbol
    : null;
  const radarTimeframe = normalizeUiText(filters?.radarTimeframe).toUpperCase() || normalizeUiText(singleState?.timeframe).toUpperCase() || '4H';
  query.set('universe', filters.universe);
  query.set('exchange', singleState.exchange);
  query.set('timeframe', radarTimeframe);
  query.set('minConfidence', String(filters.minConfidence));
  query.set('minPumpProbability', String(filters.minPumpProbability));
  query.set('bullishOnly', String(filters.bullishOnly));
  query.set('bearishOnly', String(filters.bearishOnly));
  query.set('topMoversOnly', String(filters.topMoversOnly));
  if (filters.sector) {
    query.set('sector', filters.sector);
  }
  if (focusSymbol) {
    query.set('focusSymbol', String(focusSymbol).toUpperCase());
  }
  if (options.forceRefresh) {
    query.set('forceRefresh', 'true');
  }
  return query.toString();
}

function formatDateTime(value) {
  if (!value) {
    return 'Données indisponibles';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('fr-FR');
}

function formatPriceLevel(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  const digits = Math.abs(value) >= 100 ? 2 : Math.abs(value) >= 1 ? 4 : Math.abs(value) >= 0.1 ? 5 : 6;
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

function loadCompactProPreference() {
  try {
    return localStorage.getItem(PATTERN_SCANNER_COMPACT_PRO_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function formatRatio(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatRiskRewardTooltip(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'R/R (Risk/Reward): ratio entre gain potentiel et perte potentielle. Plus il est eleve, plus le setup est favorable.';
  }

  const ratio = formatRatio(value);
  return `R/R ${ratio}: pour 1 unite de risque, le gain potentiel est d environ ${ratio} unites.`;
}

function formatPercentMove(target, reference) {
  if (
    typeof target !== 'number'
    || Number.isNaN(target)
    || typeof reference !== 'number'
    || Number.isNaN(reference)
    || reference === 0
  ) {
    return 'n/a';
  }

  const delta = ((target - reference) / reference) * 100;
  return `${delta > 0 ? '+' : ''}${delta.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;
}

function formatNormalizedScore(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

async function fetchPatternImageAsBlob(analysis) {
  const options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const { forceSymbolLookup = false, signal } = options;
  if (!analysis?.imagePath && !forceSymbolLookup && !analysis?.symbol) {
    return null;
  }
  const query = new URLSearchParams();
  if (!forceSymbolLookup && analysis?.imagePath) {
    query.set('imagePath', analysis.imagePath);
  }
  if (analysis.symbol) {
    query.set('symbol', analysis.symbol);
  }
  if (analysis.exchange) {
    query.set('exchange', analysis.exchange);
  }
  if (analysis.timeframe) {
    query.set('timeframe', analysis.timeframe);
  }
  if (analysis.imageVersion) {
    query.set('version', analysis.imageVersion);
  }
  if (analysis.imageGeneratedAt) {
    query.set('generatedAt', analysis.imageGeneratedAt);
  }
  if (analysis.analysisTimestamp) {
    query.set('analysisTimestamp', analysis.analysisTimestamp);
  }
  const url = `/api/pattern/image?${query.toString()}`;
  try {
    const response = await fetch(url, signal ? { signal } : undefined);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return blob;
  } catch (error) {
    return null;
  }
}

const PATTERN_HISTORY_LIMIT = 12;

function resolveUniverseSizeValue(universe) {
  const match = String(universe || '').toLowerCase().match(/(\d+)/);
  return match ? Number(match[1]) : 100;
}

function formatAnalysisSource(source) {
  const normalized = (source || '').trim().toUpperCase();

  if (normalized === 'PYTHON_FASTAPI') {
    return 'Python FastAPI';
  }
  if (normalized === 'JAVA_FALLBACK') {
    return 'Fallback Java';
  }
  if (normalized === 'SCANNER_ROW') {
    return 'Capture scanner';
  }
  if (normalized === 'MOCK_FALLBACK') {
    return 'Démo locale';
  }
  return source || 'n/a';
}

function formatReplayCandleSourceLabel(source) {
  const normalized = (source || '').trim().toUpperCase();

  if (normalized === 'BINANCE_LIVE') {
    return 'Binance live';
  }
  if (normalized === 'BINANCE_STORED') {
    return 'Stock Binance';
  }
  if (normalized === 'BINANCE_LIVE'.replace('_', '')) {
    return 'Binance';
  }
  return source || 'n/a';
}

function toReplaySparkline(result) {
  const candles = Array.isArray(result?.candles) ? result.candles : [];
  return candles
    .map((candle) => {
      const close = candle?.closePrice;
      if (typeof close === 'number') {
        return close;
      }
      const parsed = Number(close);
      return Number.isFinite(parsed) ? parsed : null;
    })
    .filter((value) => value != null);
}

function formatReplayLatestPrice(result) {
  const sparkline = toReplaySparkline(result);
  if (!sparkline.length) {
    return 'n/a';
  }

  const latest = sparkline[sparkline.length - 1];
  return formatPriceLevel(latest);
}

function replaySparklineColor(result) {
  const sparkline = toReplaySparkline(result);
  if (sparkline.length < 2) {
    return '#214f77';
  }
  return sparkline[sparkline.length - 1] >= sparkline[0] ? '#1f7a4d' : '#aa3d33';
}

function formatInsightScore(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return `${Math.round(value)}/100`;
}

function resolveInsightPalette(state, score) {
  if (state === 'STRONG' || state === 'ALIGNED' || score >= 75) {
    return {
      tone: '#1f7a4d',
      background: 'rgba(31,122,77,0.12)',
      surface: 'linear-gradient(180deg, rgba(240,250,244,0.96) 0%, rgba(255,255,255,0.98) 100%)',
      border: 'rgba(31,122,77,0.24)',
    };
  }
  if (state === 'MIXED' || score >= 55) {
    return {
      tone: '#8d6c16',
      background: 'rgba(141,108,22,0.12)',
      surface: 'linear-gradient(180deg, rgba(255,248,234,0.98) 0%, rgba(255,255,255,0.98) 100%)',
      border: 'rgba(141,108,22,0.22)',
    };
  }
  if (state === 'INCOMPLETE') {
    return {
      tone: '#5f6f75',
      background: 'rgba(95,111,117,0.12)',
      surface: 'linear-gradient(180deg, rgba(246,248,249,0.98) 0%, rgba(255,255,255,0.98) 100%)',
      border: 'rgba(95,111,117,0.18)',
    };
  }
  return {
    tone: '#aa3d33',
    background: 'rgba(170,61,51,0.12)',
    surface: 'linear-gradient(180deg, rgba(252,241,239,0.98) 0%, rgba(255,255,255,0.98) 100%)',
    border: 'rgba(170,61,51,0.22)',
  };
}

function compactReplayOverlayLabel(value) {
  const source = firstNonEmptyText(value);
  if (!source) {
    return '';
  }

  let compact = localizeTradeLabel(source)
    .replace(/^État de cassure haussière\s*/i, 'Cassure ')
    .replace(/^État de cassure baissière\s*/i, 'Cassure ')
    .replace(/^État d’expansion\s*/i, 'Expansion ')
    .replace(/^État de cassure\s*/i, 'Cassure ')
    .replace(/^Qualité\s*/i, 'Q ')
    .replace(/^Authenticité\s*/i, 'Auth ')
    .replace(/\s+/g, ' ')
    .trim();

  if (compact.length > 22) {
    compact = `${compact.slice(0, 21)}…`;
  }
  return compact;
}

function buildReplayOverlayBadges(result) {
  const analysis = normalizePatternAnalysis(result?.analysis);
  if (!analysis) {
    return [];
  }

  const diagnostics = extractPatternDiagnostics(analysis);
  const stateLabel = firstNonEmptyText(
    diagnostics?.validation?.compressionStateLabel,
    diagnostics?.validation?.breakoutStateLabel,
    diagnostics?.tradeQualityLabel,
    diagnostics?.confidenceLabel,
  );
  const volumeScore = analysis.volumeConfirmationScore;
  const volumeStrong = analysis.volumeConfirmation || (isFiniteNumber(volumeScore) && volumeScore >= 0.6);

  const overlays = [];

  if (stateLabel) {
    overlays.push({
      label: compactReplayOverlayLabel(stateLabel),
      tone: '#17323c',
      background: 'rgba(255,255,255,0.84)',
    });
  }

  overlays.push({
    label: isFiniteNumber(volumeScore) ? `Vol ${formatNormalizedScore(volumeScore)}` : volumeStrong ? 'Vol validé' : 'Vol faible',
    tone: volumeStrong ? '#1f7a4d' : '#8d6c16',
    background: volumeStrong ? 'rgba(31,122,77,0.14)' : 'rgba(141,108,22,0.14)',
  });

  return overlays.slice(0, 2);
}

function resolveVisualSource(analysis) {
  if (!analysis?.imagePath) {
    return 'Aucun visuel';
  }
  return 'Visuel Python';
}

function isHybridVisualAnalysis(analysis) {
  return Boolean(analysis?.imagePath) && (analysis?.analysisSource || '').trim().toUpperCase() === 'JAVA_FALLBACK';
}

function buildFallbackHistory(baseAnalysis) {
  const seed = baseAnalysis || fallbackSingle;
  return [
    {
      ...seed,
      patternDetected: seed.patternDetected,
      confidence: seed.confidence,
      aiSummary: seed.aiSummary,
      aiNarrative: seed.aiNarrative,
      convictionScore: seed.convictionScore,
      signalFusionState: seed.signalFusionState,
      divergenceType: seed.divergenceType,
      divergenceStrength: seed.divergenceStrength,
      momentumState: seed.momentumState,
      momentumStrength: seed.momentumStrength,
      candlestickPattern: seed.candlestickPattern,
      candlestickStrength: seed.candlestickStrength,
      convergenceSignals: seed.convergenceSignals,
      analysisTimestamp: null,
    },
    {
      ...seed,
      patternDetected: 'DOUBLE_BOTTOM',
      confidence: 0.77,
      aiSummary: 'Le support a deja servi de tremplin et le flux reste pro-cassure.',
      aiNarrative: 'La convergence commence a se construire: structure de retournement validee, divergence haussiere plus visible et momentum qui repart.',
      convictionScore: 69,
      signalFusionState: 'BUILDING_CONVERGENCE',
      divergenceType: 'REGULAR_BULLISH',
      divergenceStrength: 0.63,
      momentumState: 'WEAK_BULLISH_MOMENTUM',
      momentumStrength: 0.59,
      candlestickPattern: 'PIERCING_LINE',
      candlestickStrength: 0.56,
      convergenceSignals: [
        'Structure Double Bottom',
        'Divergence regular bullish',
        'Momentum weak bullish momentum',
      ],
      analysisTimestamp: null,
      direction: 'BULLISH',
    },
    {
      ...seed,
      patternDetected: 'COMPRESSION',
      confidence: 0.62,
      aiSummary: 'La structure etait encore en compression avant acceleration.',
      aiNarrative: 'Le marche restait encore trop compresse pour produire une convergence multi-signal franche.',
      convictionScore: 46,
      signalFusionState: 'FRAGILE_SETUP',
      divergenceType: null,
      divergenceStrength: null,
      momentumState: 'NEUTRAL_COMPRESSION',
      momentumStrength: 0.44,
      candlestickPattern: null,
      candlestickStrength: null,
      convergenceSignals: ['Structure Compression'],
      analysisTimestamp: null,
      direction: 'NEUTRAL',
    },
  ];
}

function formatTimelineDate(value) {
  if (!value) {
    return 'Date indisponible';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
  });
}

function firstRowKey(row) {
  if (!row) {
    return '';
  }
  return `${row.symbol}-${row.detectedPattern}-${row.analysisTimestamp || row.priorityRank || 'latest'}`;
}

function computeRiskLevel(fakeoutRisk, riskRewardRatio, targetConfidence, fakeoutRiskAdjusted) {
  const effectiveFakeoutRisk = isFiniteNumber(fakeoutRiskAdjusted)
    ? Math.round(fakeoutRiskAdjusted * 100)
    : (fakeoutRisk ?? 100);
  let score = 0;
  if (effectiveFakeoutRisk <= 25) {
    score += 2;
  } else if (effectiveFakeoutRisk <= 40) {
    score += 1;
  }

  if ((riskRewardRatio ?? 0) >= 2.0) {
    score += 2;
  } else if ((riskRewardRatio ?? 0) >= 1.3) {
    score += 1;
  }

  if ((targetConfidence ?? 0) >= 0.75) {
    score += 2;
  } else if ((targetConfidence ?? 0) >= 0.6) {
    score += 1;
  }

  if (score >= 5) {
    return {
      label: 'Maîtrisé',
      tone: '#1f7a4d',
      background: 'rgba(31,122,77,0.12)',
      description: 'Le stop, la cible et le risque de fakeout restent cohérents pour une exécution disciplinée.',
    };
  }
  if (score >= 3) {
    return {
      label: 'Équilibré',
      tone: '#8d6c16',
      background: 'rgba(141,108,22,0.12)',
      description: 'Le setup reste exploitable, mais il demande plus de sélectivité sur le point d’entrée.',
    };
  }
  return {
    label: 'Fragile',
    tone: '#aa3d33',
    background: 'rgba(170,61,51,0.12)',
    description: 'Le ratio de gain et le bruit de structure imposent un filtre strict avant exécution.',
  };
}

function buildConfidenceBreakdown(analysis, direction) {
  const pump = analysis?.pumpProbability ?? 0;
  const dump = analysis?.dumpProbability ?? 0;
  const edgeRaw = direction === 'BEARISH' ? dump - pump : pump - dump;
  const volumeScore = analysis?.volumeConfirmationScore;
  const volumeHelperParts = [];

  if (isFiniteNumber(analysis?.breakoutVolumeRatio)) {
    volumeHelperParts.push(`Ratio ${formatMultiplier(analysis.breakoutVolumeRatio)}`);
  }
  if (analysis?.volumeTrend) {
    volumeHelperParts.push(`Tendance ${formatVolumeTrendLabel(analysis.volumeTrend).toLowerCase()}`);
  }
  if (analysis?.volumeComment) {
    volumeHelperParts.push(analysis.volumeComment);
  }

  const items = [
    {
      label: 'Confiance pattern',
      helper: 'Probabilité brute de reconnaissance de la figure.',
      value: clampPercent(formatConfidence(analysis?.confidence ?? 0)),
      display: `${formatConfidence(analysis?.confidence ?? 0)}%`,
      accent: '#173f5f',
    },
    {
      label: 'Conviction cible',
      helper: 'Solidité estimée de la projection théorique.',
      value: clampPercent(formatConfidence(analysis?.targetConfidence ?? 0)),
      display: typeof analysis?.targetConfidence === 'number' ? `${formatConfidence(analysis.targetConfidence)}%` : 'n/a',
      accent: '#2f6690',
    },
  ];

  if (isFiniteNumber(volumeScore)) {
    items.push({
      label: 'Volume strength',
      helper: volumeHelperParts.length
        ? volumeHelperParts.join(' · ')
        : 'Qualité de la participation sur la construction puis la cassure.',
      value: clampPercent(volumeScore * 100),
      display: formatNormalizedScore(volumeScore),
      accent: '#1f7a4d',
    });
  }

  items.push(
    {
      label: 'Edge directionnel',
      helper: direction === 'BEARISH' ? 'Avantage du scénario baissier sur le scénario haussier.' : 'Avantage du scénario haussier sur le scénario baissier.',
      value: clampPercent(50 + (edgeRaw / 2)),
      display: `${edgeRaw >= 0 ? '+' : ''}${edgeRaw} pts`,
      accent: direction === 'BEARISH' ? '#aa3d33' : '#1f7a4d',
    },
    {
      label: 'Contrôle du fakeout',
      helper: 'Plus la barre est haute, plus le bruit de cassure est contenu.',
      value: clampPercent(100 - (analysis?.fakeoutRisk ?? 100)),
      display: `${clampPercent(100 - (analysis?.fakeoutRisk ?? 100))}%`,
      accent: '#8d6c16',
    },
  );

  return items;
}

function computeSetupVerdict(analysis, direction) {
  const patternConfidence = formatConfidence(analysis?.confidence ?? 0);
  const targetConfidence = typeof analysis?.targetConfidence === 'number' ? formatConfidence(analysis.targetConfidence) : 0;
  const riskRewardComponent = clampPercent(((analysis?.riskRewardRatio ?? 0) / 3) * 100);
  const fakeoutControl = clampPercent(100 - (analysis?.fakeoutRisk ?? 100));
  const directionalBias = direction === 'BEARISH'
    ? clampPercent(50 + (((analysis?.dumpProbability ?? 0) - (analysis?.pumpProbability ?? 0)) / 2))
    : clampPercent(50 + (((analysis?.pumpProbability ?? 0) - (analysis?.dumpProbability ?? 0)) / 2));

  const score = Math.round(
    (patternConfidence * 0.32)
    + (targetConfidence * 0.23)
    + (riskRewardComponent * 0.18)
    + (fakeoutControl * 0.15)
    + (directionalBias * 0.12),
  );

  if (score >= 82) {
    return {
      signalStrength: 'Très forte',
      setupQuality: 'A+',
      tone: '#1f7a4d',
      background: 'rgba(31,122,77,0.12)',
      description: 'Setup prioritaire: le pattern, la cible et la structure de risque sont alignés pour une vraie décision.',
      score,
    };
  }

  if (score >= 72) {
    return {
      signalStrength: 'Forte',
      setupQuality: 'A',
      tone: '#214f77',
      background: 'rgba(33,79,119,0.12)',
      description: 'Setup exploitable avec conviction, sous réserve d’une entrée propre sur le niveau clé.',
      score,
    };
  }

  if (score >= 60) {
    return {
      signalStrength: 'Modérée',
      setupQuality: 'B',
      tone: '#8d6c16',
      background: 'rgba(141,108,22,0.12)',
      description: 'Lecture crédible, mais qui demande davantage de sélectivité et de discipline d’exécution.',
      score,
    };
  }

  return {
    signalStrength: 'Faible',
    setupQuality: 'C',
    tone: '#aa3d33',
    background: 'rgba(170,61,51,0.12)',
    description: 'Le signal reste lisible, mais il ne doit pas dominer la prise de décision sans confirmation supplémentaire.',
    score,
  };
}

function localizeTradeLabel(value) {
  if (!value) {
    return value;
  }

  let localized = String(value);
  const replacements = [
    ['Breakout State', 'État de cassure haussière'],
    ['Breakdown State', 'État de cassure baissière'],
    ['Expansion State', 'État d’expansion'],
    ['Breakout', 'Cassure haussière'],
    ['Breakdown', 'Cassure baissière'],
    ['Top Quality', 'Qualité sommet'],
    ['Bottom Quality', 'Qualité creux'],
    ['Channel Quality', 'Qualité canal'],
    ['Cup Authenticity', 'Authenticité cup'],
    ['Triangle Quality', 'Qualité triangle'],
    ['Flag Quality', 'Qualité flag'],
    ['Wedge Quality', 'Qualité biseau'],
    ['Range Quality', 'Qualité range'],
    ['Base Quality', 'Qualité base'],
    ['Structure Quality', 'Qualité structure'],
    ['Curvature', 'Courbure'],
    ['Distribution Breakdown', 'Distribution en cassure'],
    ['Reversal Breakout', 'Cassure de retournement'],
    ['Curved Distribution', 'Distribution arrondie'],
    ['Curved Base Breakout', 'Base arrondie en cassure'],
    ['Channel Expansion Breakout', 'Expansion de canal'],
    ['Rounded Continuation Breakout', 'Continuation arrondie en cassure'],
    ['Compression Breakdown', 'Compression en cassure baissière'],
    ['Compression Breakout', 'Compression en cassure haussière'],
    ['Continuation Pullback', 'Pullback de continuation'],
    ['Converging Exhaustion', 'Essoufflement convergent'],
    ['Range Expansion', 'Expansion de range'],
    ['Constructive Base', 'Base constructive'],
    ['Distribution Ceiling', 'Plafond de distribution'],
    ['Balanced Range', 'Range équilibré'],
    ['Balanced Compression', 'Compression équilibrée'],
    ['Parallel Trend Structure', 'Structure de canal parallèle'],
    ['Volatility Compression', 'Compression de volatilité'],
    ['High', 'Forte'],
    ['Medium', 'Moyenne'],
    ['Low', 'Faible'],
  ];

  replacements.forEach(([from, to]) => {
    localized = localized.replaceAll(from, to);
  });

  return localized;
}

function getUserLoginFromAuth() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return typeof user?.login === 'string' ? user.login : '';
  } catch (error) {
    return '';
  }
}

function normalizeUiText(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function sameTimeframe(left, right) {
  return normalizeUiText(left).toUpperCase() === normalizeUiText(right).toUpperCase();
}

function buildPatternAnalysisLookupKey(symbol, exchange, timeframe) {
  const normalizedSymbol = normalizeUiText(symbol).toUpperCase();
  const normalizedTimeframe = normalizeUiText(timeframe).toUpperCase();
  if (!normalizedSymbol || !normalizedTimeframe) {
    return '';
  }

  const normalizedExchange = normalizeUiText(exchange).toUpperCase() || 'BINANCE';
  return `${normalizedSymbol}::${normalizedExchange}::${normalizedTimeframe}`;
}

function extractPatternImageMetadata(source) {
  const imagePath = normalizeUiText(source?.imagePath);
  if (!imagePath) {
    return null;
  }

  return {
    imagePath,
    imageGeneratedAt: source?.imageGeneratedAt || null,
    imageVersion: normalizeUiText(source?.imageVersion) || '',
  };
}

function normalizeApiImageUrl(value) {
  const normalized = normalizeUiText(value);
  if (!normalized) {
    return '';
  }

  if (/^(https?:|blob:|data:)/i.test(normalized)) {
    return normalized;
  }

  let sanitized = normalized.replace(/\\/g, '/');
  sanitized = sanitized.replace(/^\/+pattern-scanner\/+?/i, '/');
  sanitized = sanitized.replace(/^pattern-scanner\/+?/i, '/');
  sanitized = sanitized.replace(/^\/{2,}api\//i, '/api/');
  sanitized = sanitized.replace(/^api\//i, '/api/');

  const apiIndex = sanitized.indexOf('/api/');
  if (apiIndex > 0) {
    sanitized = sanitized.slice(apiIndex);
  }

  return sanitized;
}

function normalizeTelegramPreviewPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const normalizedCharts = Array.isArray(payload.multiTimeframeCharts)
    ? payload.multiTimeframeCharts.map((chart) => ({
      ...chart,
      imageUrl: normalizeApiImageUrl(chart?.imageUrl),
    }))
    : [];

  return {
    ...payload,
    imageUrl: normalizeApiImageUrl(payload.imageUrl),
    multiTimeframeCharts: normalizedCharts,
  };
}

function isValidTelegramChatId(value) {
  const normalized = normalizeUiText(value);
  if (!normalized) {
    return false;
  }
  return TELEGRAM_NUMERIC_CHAT_ID_PATTERN.test(normalized) || TELEGRAM_USERNAME_CHAT_ID_PATTERN.test(normalized);
}

function getTelegramChatIdValidationMessage(value) {
  const normalized = normalizeUiText(value);
  if (!normalized || isValidTelegramChatId(normalized)) {
    return '';
  }
  return 'Format de chatId invalide. Utilisez 123456789, -1001234567890 ou @mon_canal.';
}

function decorateTelegramErrorMessage(message, fallbackMessage, attemptedChatId = '') {
  const normalized = normalizeUiText(message);
  if (!normalized) {
    return fallbackMessage;
  }

  const lowered = normalized.toLowerCase();
  if (lowered.includes('chat not found')) {
    if (normalizeUiText(attemptedChatId).startsWith('@')) {
      return `${normalized} Un format @nom ne fonctionne que pour un canal ou groupe public où le bot est déjà présent. Pour une discussion privée ou un bot, utilisez l'identifiant numérique du chat.`;
    }
    return `${normalized} Vérifiez le chatId, démarrez le bot en message privé, ou ajoutez-le au canal/groupe cible.`;
  }
  if (lowered.includes('bot was blocked')) {
    return `${normalized} Ouvrez une conversation avec le bot puis relancez l’envoi.`;
  }
  if (lowered.includes('user is deactivated')) {
    return `${normalized} Le destinataire Telegram lié à ce chatId n’est plus actif.`;
  }

  return normalized;
}

function buildTelegramChatStorageKey(login) {
  return `${TELEGRAM_CHAT_ID_STORAGE_PREFIX}${normalizeUiText(login) || 'anonymous'}`;
}

async function parseApiError(response) {
  const responseText = await response.text();
  if (!responseText) {
    return 'Erreur inconnue.';
  }

  try {
    const parsed = JSON.parse(responseText);
    return parsed?.message || parsed?.error || responseText;
  } catch (error) {
    return responseText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function parsePatternRawJson(rawJson) {
  if (!rawJson || typeof rawJson !== 'string') {
    return null;
  }
  try {
    return JSON.parse(rawJson);
  } catch (error) {
    return null;
  }
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function parseFiniteNumber(value) {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const parsed = parseFiniteNumber(value);
    if (parsed != null) {
      return parsed;
    }
  }
  return null;
}

function firstNonEmptyText(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function firstBoolean(...values) {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return null;
}

function readAdvancedBlock(source, key, fallback = null) {
  if (source?.[key] && typeof source[key] === 'object') {
    return source[key];
  }
  return fallback;
}

function formatDivergenceIndicatorLabel(value) {
  const normalized = normalizeUiText(value).toUpperCase();
  if (!normalized) {
    return 'n/a';
  }
  if (normalized === 'RSI') return 'RSI';
  if (normalized === 'MACD') return 'MACD';
  if (normalized === 'VOLUME') return 'Volume';
  return formatSignalToken(normalized);
}

async function fetchAdvancedPatternAnalysis(request) {
  const response = await fetch('/api/pattern/analyze-advanced', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return response.json();
}

function normalizeVolumeTrend(value) {
  const normalized = normalizeUiText(value);
  if (normalized === 'CONTRACTING' || normalized === 'EXPANDING' || normalized === 'FLAT' || normalized === 'DIVERGING') {
    return normalized;
  }
  return null;
}

function formatMultiplier(value) {
  if (!isFiniteNumber(value)) {
    return 'n/a';
  }
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}x`;
}

function formatVolumeTrendLabel(value) {
  const normalized = normalizeVolumeTrend(value);
  if (normalized === 'CONTRACTING') return 'Contraction';
  if (normalized === 'EXPANDING') return 'Expansion';
  if (normalized === 'DIVERGING') return 'Divergence';
  if (normalized === 'FLAT') return 'Plat';
  return 'n/a';
}

function extractVolumeFields(source, rawPayload = parsePatternRawJson(source?.rawJson)) {
  const diagnostics = rawPayload?.diagnostics && typeof rawPayload.diagnostics === 'object'
    ? rawPayload.diagnostics
    : null;
  const validation = rawPayload?.validation && typeof rawPayload.validation === 'object'
    ? rawPayload.validation
    : diagnostics?.validation && typeof diagnostics.validation === 'object'
      ? diagnostics.validation
      : null;

  const volumeConfirmationScore = firstFiniteNumber(
    source?.volumeConfirmationScore,
    rawPayload?.volumeConfirmationScore,
    validation?.volumeConfirmationScore,
  );
  const breakoutVolumeRatio = firstFiniteNumber(
    source?.breakoutVolumeRatio,
    rawPayload?.breakoutVolumeRatio,
  );
  const fakeoutRiskAdjusted = firstFiniteNumber(
    source?.fakeoutRiskAdjusted,
    rawPayload?.fakeoutRiskAdjusted,
  );
  const volumeTrend = normalizeVolumeTrend(source?.volumeTrend) || normalizeVolumeTrend(rawPayload?.volumeTrend);
  const volumeComment = firstNonEmptyText(source?.volumeComment, rawPayload?.volumeComment);

  const volumeConfirmation = typeof source?.volumeConfirmation === 'boolean'
    ? source.volumeConfirmation
    : typeof rawPayload?.volumeConfirmation === 'boolean'
      ? rawPayload.volumeConfirmation
      : isFiniteNumber(volumeConfirmationScore)
        ? volumeConfirmationScore >= 0.6
        : false;

  return {
    volumeConfirmation,
    volumeConfirmationScore,
    breakoutVolumeRatio,
    volumeTrend,
    volumeComment,
    fakeoutRiskAdjusted,
  };
}

function normalizeSignalFusionState(value) {
  const normalized = normalizeUiText(value);
  if (['HIGH_CONVICTION_SETUP', 'CONFIRMED_CONVERGENCE', 'BUILDING_CONVERGENCE', 'FRAGILE_SETUP'].includes(normalized)) {
    return normalized;
  }
  return null;
}

function normalizeMomentumState(value) {
  const normalized = normalizeUiText(value);
  if (['BULLISH_ACCELERATION', 'BEARISH_ACCELERATION', 'WEAK_BULLISH_MOMENTUM', 'WEAK_BEARISH_MOMENTUM', 'MOMENTUM_EXHAUSTION', 'NEUTRAL_COMPRESSION'].includes(normalized)) {
    return normalized;
  }
  return null;
}

function normalizeDivergenceType(value) {
  const normalized = normalizeUiText(value);
  if (['REGULAR_BULLISH', 'HIDDEN_BULLISH', 'REGULAR_BEARISH', 'HIDDEN_BEARISH', 'VOLUME_BULLISH', 'VOLUME_BEARISH'].includes(normalized)) {
    return normalized;
  }
  return null;
}

function formatSignalToken(value) {
  const normalized = normalizeUiText(value);
  if (!normalized) {
    return 'n/a';
  }
  return normalized
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
}

function formatSignalFusionState(value) {
  const normalized = normalizeSignalFusionState(value);
  if (normalized === 'HIGH_CONVICTION_SETUP') return 'Haute conviction';
  if (normalized === 'CONFIRMED_CONVERGENCE') return 'Convergence confirmee';
  if (normalized === 'BUILDING_CONVERGENCE') return 'Convergence en construction';
  if (normalized === 'FRAGILE_SETUP') return 'Setup fragile';
  return 'n/a';
}

function formatMomentumStateLabel(value) {
  const normalized = normalizeMomentumState(value);
  if (!normalized) {
    return 'n/a';
  }
  if (normalized === 'NEUTRAL_COMPRESSION') {
    return 'Compression neutre';
  }
  if (normalized === 'MOMENTUM_EXHAUSTION') {
    return 'Essoufflement momentum';
  }
  return formatSignalToken(normalized);
}

function formatDivergenceTypeLabel(value) {
  const normalized = normalizeDivergenceType(value);
  if (!normalized) {
    return 'n/a';
  }
  if (normalized === 'VOLUME_BULLISH') return 'Volume bullish';
  if (normalized === 'VOLUME_BEARISH') return 'Volume bearish';
  return formatSignalToken(normalized);
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => firstNonEmptyText(entry))
    .filter(Boolean);
}

function normalizeDirectionToken(value) {
  const normalized = normalizeUiText(value).toUpperCase();
  if (normalized === 'BULLISH' || normalized === 'BEARISH' || normalized === 'NEUTRAL') {
    return normalized;
  }
  return null;
}

function normalizeMultiTimeframeSlice(entry, fallbackTimeframe = '') {
  const timeframe = normalizeUiText(entry?.timeframe || fallbackTimeframe).toUpperCase();
  const patternScore = firstFiniteNumber(entry?.patternScore);
  const convictionScore = firstFiniteNumber(entry?.convictionScore);
  const confidence = firstFiniteNumber(entry?.confidence);
  const direction = normalizeDirectionToken(entry?.direction);
  const volumeTrend = normalizeVolumeTrend(entry?.volumeTrend);
  const volumeConfirmation = typeof entry?.volumeConfirmation === 'boolean' ? entry.volumeConfirmation : null;
  const available = Boolean(direction)
    || isFiniteNumber(patternScore)
    || isFiniteNumber(convictionScore)
    || isFiniteNumber(confidence)
    || Boolean(volumeTrend)
    || typeof volumeConfirmation === 'boolean';

  return {
    timeframe,
    patternScore: isFiniteNumber(patternScore) ? Math.round(patternScore) : null,
    convictionScore: isFiniteNumber(convictionScore) ? Math.round(convictionScore) : null,
    confidence,
    direction: direction || 'NEUTRAL',
    volumeTrend,
    volumeConfirmation,
    available,
  };
}

function normalizeMultiTimeframeDetail(detail) {
  if (!detail || typeof detail !== 'object') {
    return null;
  }

  const timeframes = Array.isArray(detail.timeframes) ? detail.timeframes : [];
  const byTimeframe = new Map(
    timeframes
      .map((entry) => {
        const timeframe = normalizeUiText(entry?.timeframe).toUpperCase();
        return timeframe ? [timeframe, normalizeMultiTimeframeSlice(entry, timeframe)] : null;
      })
      .filter(Boolean),
  );
  const dominantDirection = normalizeDirectionToken(detail?.dominantDirection) || 'NEUTRAL';
  const dominantTimeframe = normalizeUiText(detail?.dominantTimeframe).toUpperCase();

  return {
    alignmentScore: firstFiniteNumber(detail?.alignmentScore),
    convictionScore: firstFiniteNumber(detail?.convictionScore),
    volumeAlignmentScore: firstFiniteNumber(detail?.volumeAlignmentScore),
    dominantDirection,
    dominantTimeframe: dominantTimeframe || null,
    timeframes: CALIBRATION_TIMEFRAMES.map((timeframe) => (
      byTimeframe.get(timeframe) || normalizeMultiTimeframeSlice({}, timeframe)
    )),
  };
}

function extractSignalFusionFields(source, rawPayload = parsePatternRawJson(source?.rawJson)) {
  const diagnostics = rawPayload?.diagnostics && typeof rawPayload.diagnostics === 'object'
    ? rawPayload.diagnostics
    : null;
  const advancedDivergence = readAdvancedBlock(source, 'divergence', diagnostics?.divergence && typeof diagnostics.divergence === 'object' ? diagnostics.divergence : null);
  const advancedMomentum = readAdvancedBlock(source, 'momentum', diagnostics?.momentum && typeof diagnostics.momentum === 'object' ? diagnostics.momentum : null);
  const advancedCandlestick = readAdvancedBlock(source, 'candlestick', diagnostics?.candlestick && typeof diagnostics.candlestick === 'object' ? diagnostics.candlestick : null);
  const advancedSignalFusion = readAdvancedBlock(source, 'signalFusion', diagnostics?.signalFusion && typeof diagnostics.signalFusion === 'object' ? diagnostics.signalFusion : null);
  const signalFusion = diagnostics?.signalFusion && typeof diagnostics.signalFusion === 'object'
    ? diagnostics.signalFusion
    : null;
  const divergence = diagnostics?.divergence && typeof diagnostics.divergence === 'object'
    ? diagnostics.divergence
    : null;
  const momentum = diagnostics?.momentum && typeof diagnostics.momentum === 'object'
    ? diagnostics.momentum
    : null;
  const candlestick = diagnostics?.candlestick && typeof diagnostics.candlestick === 'object'
    ? diagnostics.candlestick
    : null;

  const divergenceType = normalizeDivergenceType(
    firstNonEmptyText(source?.divergenceType, advancedDivergence?.type, rawPayload?.divergenceType, divergence?.divergenceType, divergence?.type),
  );
  const divergenceStrength = firstFiniteNumber(
    source?.divergenceStrength,
    advancedDivergence?.strength,
    rawPayload?.divergenceStrength,
    divergence?.strength,
  );
  const divergenceIndicator = firstNonEmptyText(advancedDivergence?.indicator, divergence?.indicator);
  const divergenceComment = firstNonEmptyText(advancedDivergence?.comment, divergence?.comment);
  const momentumState = normalizeMomentumState(
    firstNonEmptyText(source?.momentumState, advancedMomentum?.state, rawPayload?.momentumState, momentum?.momentumState),
  );
  const momentumStrength = firstFiniteNumber(
    source?.momentumStrength,
    advancedMomentum?.strength,
    rawPayload?.momentumStrength,
    momentum?.momentumStrength,
  );
  const candlestickPattern = firstNonEmptyText(
    source?.candlestickPattern,
    advancedCandlestick?.pattern,
    rawPayload?.candlestickPattern,
    candlestick?.pattern,
  );
  const candlestickStrength = firstFiniteNumber(
    source?.candlestickStrength,
    advancedCandlestick?.strength,
    rawPayload?.candlestickStrength,
    candlestick?.strength,
  );
  const convictionScore = firstFiniteNumber(
    source?.convictionScore,
    advancedSignalFusion?.convictionScore,
    rawPayload?.convictionScore,
    signalFusion?.convictionScore,
  );
  const signalFusionState = normalizeSignalFusionState(
    firstNonEmptyText(source?.signalFusionState, advancedSignalFusion?.state, rawPayload?.signalFusionState, signalFusion?.signalFusionState),
  );
  const aiNarrative = firstNonEmptyText(
    source?.aiNarrative,
    advancedSignalFusion?.aiNarrative,
    rawPayload?.aiNarrative,
    signalFusion?.aiNarrative,
  );
  const convergenceSignals = normalizeStringArray(
    source?.convergenceSignals
      || advancedSignalFusion?.convergenceSignals
      || rawPayload?.convergenceSignals
      || signalFusion?.convergenceSignals
      || diagnostics?.convergenceSignals,
  );

  return {
    divergenceType,
    divergenceStrength,
    momentumState,
    momentumStrength,
    candlestickPattern,
    candlestickStrength,
    convictionScore: isFiniteNumber(convictionScore) ? Math.round(convictionScore) : null,
    signalFusionState,
    aiNarrative,
    convergenceSignals,
    advancedDivergence: divergenceType || divergenceIndicator || divergenceComment || firstBoolean(advancedDivergence?.detected, divergence?.detected) != null
      ? {
        detected: firstBoolean(advancedDivergence?.detected, divergence?.detected, Boolean(divergenceType)),
        type: divergenceType,
        indicator: divergenceIndicator,
        strength: isFiniteNumber(divergenceStrength) ? divergenceStrength : null,
        comment: divergenceComment,
      }
      : null,
    advancedMomentum: momentumState || isFiniteNumber(momentumStrength) || firstBoolean(advancedMomentum?.macdBullishCross, momentum?.macdBullishCross) != null
      ? {
        state: momentumState,
        strength: isFiniteNumber(momentumStrength) ? momentumStrength : null,
        macdBullishCross: firstBoolean(advancedMomentum?.macdBullishCross, momentum?.macdBullishCross),
        macdBearishCross: firstBoolean(advancedMomentum?.macdBearishCross, momentum?.macdBearishCross),
        stochRsiBullish: firstBoolean(advancedMomentum?.stochRsiBullish, momentum?.stochRsiBullish),
        stochRsiBearish: firstBoolean(advancedMomentum?.stochRsiBearish, momentum?.stochRsiBearish),
        comment: firstNonEmptyText(advancedMomentum?.comment, momentum?.comment),
      }
      : null,
    advancedCandlestick: candlestickPattern || isFiniteNumber(candlestickStrength) || firstBoolean(advancedCandlestick?.confirmationRequired, candlestick?.confirmationRequired) != null
      ? {
        pattern: candlestickPattern,
        direction: firstNonEmptyText(advancedCandlestick?.direction, candlestick?.direction),
        strength: isFiniteNumber(candlestickStrength) ? candlestickStrength : null,
        confirmationRequired: firstBoolean(advancedCandlestick?.confirmationRequired, candlestick?.confirmationRequired),
        comment: firstNonEmptyText(advancedCandlestick?.comment, candlestick?.comment),
      }
      : null,
    advancedSignalFusion: signalFusionState || isFiniteNumber(convictionScore) || aiNarrative || convergenceSignals.length
      ? {
        convictionScore: isFiniteNumber(convictionScore) ? Math.round(convictionScore) : null,
        state: signalFusionState,
        convergenceSignals,
        aiNarrative,
      }
      : null,
  };
}

function normalizePatternAnalysis(analysis) {
  if (!analysis || typeof analysis !== 'object') {
    return analysis;
  }

  const rawPayload = parsePatternRawJson(analysis.rawJson);
  return {
    ...analysis,
    ...extractVolumeFields(analysis, rawPayload),
    ...extractSignalFusionFields(analysis, rawPayload),
  };
}

function normalizeScannerRow(row) {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const rawPayload = parsePatternRawJson(row.rawJson);
  const volumeFields = extractVolumeFields(row, rawPayload);
  const signalFusionFields = extractSignalFusionFields(row, rawPayload);
  const multiTimeframeScore = firstFiniteNumber(row.multiTimeframeScore);
  const multiTimeframeDetail = normalizeMultiTimeframeDetail(row.multiTimeframeDetail);
  const convictionScore = resolveEffectiveConvictionScore({
    ...row,
    ...signalFusionFields,
    multiTimeframeDetail,
  });

  return {
    ...row,
    ...volumeFields,
    ...signalFusionFields,
    hasAnnotatedImage: Boolean(row.hasAnnotatedImage),
    imagePath: normalizeUiText(row.imagePath) || '',
    imageGeneratedAt: row.imageGeneratedAt || null,
    imageVersion: normalizeUiText(row.imageVersion) || '',
    convictionScore: isFiniteNumber(convictionScore) ? Math.round(convictionScore) : null,
    multiTimeframeScore: isFiniteNumber(multiTimeframeScore) ? Math.round(multiTimeframeScore) : null,
    multiTimeframeDetail,
  };
}

function buildSignalFusionPalette(state, score) {
  const normalizedState = normalizeSignalFusionState(state);
  const numericScore = isFiniteNumber(score) ? Math.round(score) : 0;
  if (normalizedState === 'HIGH_CONVICTION_SETUP' || numericScore >= 82) {
    return {
      tone: '#1f7a4d',
      background: 'rgba(31,122,77,0.12)',
      surface: 'linear-gradient(180deg, rgba(238,250,243,0.98) 0%, rgba(255,255,255,0.98) 100%)',
      border: 'rgba(31,122,77,0.22)',
    };
  }
  if (normalizedState === 'CONFIRMED_CONVERGENCE' || numericScore >= 68) {
    return {
      tone: '#8d6c16',
      background: 'rgba(141,108,22,0.12)',
      surface: 'linear-gradient(180deg, rgba(255,248,234,0.98) 0%, rgba(255,255,255,0.98) 100%)',
      border: 'rgba(141,108,22,0.22)',
    };
  }
  return {
    tone: '#aa3d33',
    background: 'rgba(170,61,51,0.12)',
    surface: 'linear-gradient(180deg, rgba(252,241,239,0.98) 0%, rgba(255,255,255,0.98) 100%)',
    border: 'rgba(170,61,51,0.22)',
  };
}

function buildSignalConvergenceItems(analysis) {
  const explicitItems = normalizeStringArray(analysis?.convergenceSignals);
  if (explicitItems.length) {
    return explicitItems;
  }

  const derivedItems = [];
  if (analysis?.patternDetected) {
    derivedItems.push(`Structure ${formatSignalToken(analysis.patternDetected)}`);
  }
  if (analysis?.divergenceType) {
    derivedItems.push(`Divergence ${formatDivergenceTypeLabel(analysis.divergenceType).toLowerCase()}`);
  }
  if (analysis?.momentumState && analysis.momentumState !== 'NEUTRAL_COMPRESSION') {
    derivedItems.push(`Momentum ${formatMomentumStateLabel(analysis.momentumState).toLowerCase()}`);
  }
  if (analysis?.candlestickPattern) {
    derivedItems.push(`Chandelier ${formatSignalToken(analysis.candlestickPattern)}`);
  }
  if (analysis?.volumeConfirmation) {
    derivedItems.push('Volume confirme la structure');
  }
  return derivedItems;
}

function hasActionableDivergenceSignal(analysis) {
  const divergenceType = normalizeDivergenceType(firstNonEmptyText(analysis?.divergenceType, analysis?.advancedDivergence?.type));
  return Boolean(
    divergenceType
    || analysis?.advancedDivergence?.detected === true
    || normalizeUiText(analysis?.advancedDivergence?.indicator)
    || normalizeUiText(analysis?.advancedDivergence?.comment),
  );
}

function hasActionableMomentumSignal(analysis) {
  const momentumState = normalizeMomentumState(firstNonEmptyText(analysis?.momentumState, analysis?.advancedMomentum?.state));
  return Boolean(
    (momentumState && momentumState !== 'NEUTRAL_COMPRESSION')
    || analysis?.advancedMomentum?.macdBullishCross
    || analysis?.advancedMomentum?.macdBearishCross
    || analysis?.advancedMomentum?.stochRsiBullish
    || analysis?.advancedMomentum?.stochRsiBearish
    || normalizeUiText(analysis?.advancedMomentum?.comment),
  );
}

function hasActionableCandlestickSignal(analysis) {
  const candlestickPattern = normalizeUiText(firstNonEmptyText(analysis?.candlestickPattern, analysis?.advancedCandlestick?.pattern));
  const candlestickStrength = firstFiniteNumber(analysis?.candlestickStrength, analysis?.advancedCandlestick?.strength);
  return Boolean(
    candlestickPattern
    || normalizeUiText(analysis?.advancedCandlestick?.direction)
    || isFiniteNumber(candlestickStrength)
    || analysis?.advancedCandlestick?.confirmationRequired === true
    || normalizeUiText(analysis?.advancedCandlestick?.comment),
  );
}

function hasActionableFusionSignal(analysis) {
  const signalFusionState = normalizeSignalFusionState(firstNonEmptyText(analysis?.signalFusionState, analysis?.advancedSignalFusion?.state));
  const convictionScore = firstFiniteNumber(analysis?.convictionScore, analysis?.advancedSignalFusion?.convictionScore);
  const convergenceSignals = normalizeStringArray(analysis?.convergenceSignals || analysis?.advancedSignalFusion?.convergenceSignals);
  return Boolean(
    signalFusionState
    || isFiniteNumber(convictionScore)
    || convergenceSignals.length
    || firstNonEmptyText(analysis?.aiNarrative, analysis?.advancedSignalFusion?.aiNarrative),
  );
}

function hasAdvancedSignalCoverage(analysis) {
  return hasActionableDivergenceSignal(analysis)
    || hasActionableMomentumSignal(analysis)
    || hasActionableCandlestickSignal(analysis)
    || hasActionableFusionSignal(analysis);
}

function findMultiTimeframeSlice(detail, timeframe) {
  const normalizedTimeframe = normalizeUiText(timeframe).toUpperCase();
  if (!detail || !normalizedTimeframe || !Array.isArray(detail.timeframes)) {
    return null;
  }
  return detail.timeframes.find((slice) => normalizeUiText(slice?.timeframe).toUpperCase() === normalizedTimeframe) || null;
}

function resolveEffectiveConvictionScore(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const multiTimeframeDetail = source?.multiTimeframeDetail && typeof source.multiTimeframeDetail === 'object'
    ? source.multiTimeframeDetail
    : null;
  const currentSlice = findMultiTimeframeSlice(multiTimeframeDetail, source?.timeframe);

  return firstFiniteNumber(
    source?.convictionScore,
    source?.advancedSignalFusion?.convictionScore,
    multiTimeframeDetail?.convictionScore,
    currentSlice?.convictionScore,
  );
}

function formatConvictionValue(source) {
  const convictionScore = resolveEffectiveConvictionScore(source);
  return isFiniteNumber(convictionScore) ? `${Math.round(convictionScore)}/100` : 'n/a';
}

function formatDivergenceSummary(analysis) {
  const divergenceType = normalizeDivergenceType(firstNonEmptyText(analysis?.divergenceType, analysis?.advancedDivergence?.type));
  const divergenceStrength = firstFiniteNumber(analysis?.divergenceStrength, analysis?.advancedDivergence?.strength);
  if (divergenceType) {
    return divergenceStrength
      ? `${formatDivergenceTypeLabel(divergenceType)} · ${formatNormalizedScore(divergenceStrength)}`
      : formatDivergenceTypeLabel(divergenceType);
  }
  if (analysis?.advancedDivergence?.indicator) {
    return formatDivergenceIndicatorLabel(analysis.advancedDivergence.indicator);
  }
  return 'Signal détecté';
}

function formatMomentumSummary(analysis) {
  const momentumState = normalizeMomentumState(firstNonEmptyText(analysis?.momentumState, analysis?.advancedMomentum?.state));
  const momentumStrength = firstFiniteNumber(analysis?.momentumStrength, analysis?.advancedMomentum?.strength);
  if (momentumState) {
    return momentumStrength
      ? `${formatMomentumStateLabel(momentumState)} · ${formatNormalizedScore(momentumStrength)}`
      : formatMomentumStateLabel(momentumState);
  }
  if (analysis?.advancedMomentum?.macdBullishCross) return 'Croisement MACD haussier';
  if (analysis?.advancedMomentum?.macdBearishCross) return 'Croisement MACD baissier';
  if (analysis?.advancedMomentum?.stochRsiBullish) return 'Stoch RSI haussier';
  if (analysis?.advancedMomentum?.stochRsiBearish) return 'Stoch RSI baissier';
  return 'Signal détecté';
}

function formatCandlestickSummary(analysis) {
  const candlestickPattern = normalizeUiText(firstNonEmptyText(analysis?.candlestickPattern, analysis?.advancedCandlestick?.pattern));
  const candlestickStrength = firstFiniteNumber(analysis?.candlestickStrength, analysis?.advancedCandlestick?.strength);
  if (candlestickPattern) {
    return candlestickStrength
      ? `${formatSignalToken(candlestickPattern)} · ${formatNormalizedScore(candlestickStrength)}`
      : formatSignalToken(candlestickPattern);
  }
  const candlestickDirection = normalizeDirectionToken(analysis?.advancedCandlestick?.direction);
  if (candlestickDirection) {
    return `Biais ${directionLabel(candlestickDirection).toLowerCase()}`;
  }
  if (analysis?.advancedCandlestick?.confirmationRequired === true) {
    return 'Confirmation requise';
  }
  return 'Signal détecté';
}

function normalizeRadarSortMode(value) {
  const normalized = normalizeUiText(value).toUpperCase();
  if (normalized === '1H' || normalized === '4H' || normalized === '1D' || normalized === 'GLOBAL') {
    return normalized;
  }
  return 'GLOBAL';
}

function getTimeframeScore(source, timeframe) {
  const detail = source?.multiTimeframeDetail;
  if (!detail || !Array.isArray(detail.timeframes)) {
    return null;
  }
  const targetTimeframe = normalizeUiText(timeframe).toUpperCase();
  const slice = detail.timeframes.find((entry) => normalizeUiText(entry?.timeframe).toUpperCase() === targetTimeframe);
  if (!slice) {
    return null;
  }
  return firstFiniteNumber(slice.patternScore, slice.convictionScore, slice.confidence);
}

function compareScannerRowsBySelectedScore(left, right, sortMode = 'GLOBAL') {
  const normalizedSortMode = normalizeRadarSortMode(sortMode);

  const leftPrimary = normalizedSortMode === 'GLOBAL'
    ? firstFiniteNumber(left?.scoreGlobal, left?.patternScore)
    : getTimeframeScore(left, normalizedSortMode);
  const rightPrimary = normalizedSortMode === 'GLOBAL'
    ? firstFiniteNumber(right?.scoreGlobal, right?.patternScore)
    : getTimeframeScore(right, normalizedSortMode);

  const primaryDelta = (rightPrimary ?? -Infinity) - (leftPrimary ?? -Infinity);
  if (primaryDelta !== 0) {
    return primaryDelta;
  }

  const scoreGlobalDelta = (firstFiniteNumber(right?.scoreGlobal, right?.patternScore) ?? 0) - (firstFiniteNumber(left?.scoreGlobal, left?.patternScore) ?? 0);
  if (scoreGlobalDelta !== 0) {
    return scoreGlobalDelta;
  }

  const convictionDelta = (firstFiniteNumber(right?.convictionScore) ?? 0) - (firstFiniteNumber(left?.convictionScore) ?? 0);
  if (convictionDelta !== 0) {
    return convictionDelta;
  }

  const confidenceDelta = (right?.confidence ?? 0) - (left?.confidence ?? 0);
  if (confidenceDelta !== 0) {
    return confidenceDelta;
  }

  const rightTimestamp = right?.analysisTimestamp ? new Date(right.analysisTimestamp).getTime() : 0;
  const leftTimestamp = left?.analysisTimestamp ? new Date(left.analysisTimestamp).getTime() : 0;
  return rightTimestamp - leftTimestamp;
}

function reprioritizeScannerRows(rows, sortMode = 'GLOBAL') {
  const normalizedSortMode = normalizeRadarSortMode(sortMode);
  return (Array.isArray(rows) ? rows : [])
    .slice()
    .sort((left, right) => compareScannerRowsBySelectedScore(left, right, normalizedSortMode))
    .map((row, index) => ({
      ...row,
      priorityRank: index + 1,
    }));
}

function buildVolumeStrength(source) {
  const score = source?.volumeConfirmationScore;
  const ratio = source?.breakoutVolumeRatio;

  if (isFiniteNumber(score)) {
    if (score >= 0.72 || (isFiniteNumber(ratio) && ratio >= 1.5)) {
      return {
        label: 'Strong',
        tone: '#1f7a4d',
        background: 'rgba(31,122,77,0.12)',
        detail: formatNormalizedScore(score),
      };
    }
    if (score >= 0.48 || (isFiniteNumber(ratio) && ratio >= 1.05)) {
      return {
        label: 'Medium',
        tone: '#8d6c16',
        background: 'rgba(141,108,22,0.12)',
        detail: formatNormalizedScore(score),
      };
    }
    return {
      label: 'Weak',
      tone: '#aa3d33',
      background: 'rgba(170,61,51,0.12)',
      detail: formatNormalizedScore(score),
    };
  }

  if (source?.volumeConfirmation) {
    return {
      label: 'Medium',
      tone: '#8d6c16',
      background: 'rgba(141,108,22,0.12)',
      detail: 'Oui',
    };
  }

  return {
    label: 'n/a',
    tone: '#6f7f85',
    background: 'rgba(111,127,133,0.12)',
    detail: 'n/a',
  };
}

function buildConvictionGrade(source, direction) {
  const convictionScore = resolveEffectiveConvictionScore(source);
  const setupScore = isFiniteNumber(source?.patternScore) ? Math.round(source.patternScore) : null;
  const score = isFiniteNumber(convictionScore) ? Math.round(convictionScore) : setupScore;
  const verdict = score >= 82
    ? { label: 'Grade institutionnel', tier: 'A+', tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' }
    : score >= 72
      ? { label: 'Fort', tier: 'A', tone: '#214f77', background: 'rgba(33,79,119,0.12)' }
      : score >= 60
        ? { label: 'Modéré', tier: 'B', tone: '#8d6c16', background: 'rgba(141,108,22,0.12)' }
        : { label: 'Setup faible', tier: 'C', tone: '#aa3d33', background: 'rgba(170,61,51,0.12)' };

  return {
    ...verdict,
    score,
    bias: directionHeadline(direction),
  };
}

const CONVICTION_GRADE_GUIDE = [
  {
    tier: 'A+',
    label: 'Grade institutionnel',
    range: '82-100',
    description: 'Convergence très propre: structure, conviction et signaux avancés sont déjà alignés.',
  },
  {
    tier: 'A',
    label: 'Fort',
    range: '72-81',
    description: 'Setup fort et exploitable maintenant, avec peu de réserves majeures.',
  },
  {
    tier: 'B',
    label: 'Modéré',
    range: '60-71',
    description: 'Lecture correcte mais encore incomplète: il manque souvent une confirmation ou un meilleur timing.',
  },
  {
    tier: 'C',
    label: 'Setup faible',
    range: '<60',
    description: 'Setup faible ou trop fragile pour être prioritaire dans le radar.',
  },
];

function getConvictionGradeGuide(tier) {
  return CONVICTION_GRADE_GUIDE.find((entry) => entry.tier === tier) || CONVICTION_GRADE_GUIDE[CONVICTION_GRADE_GUIDE.length - 1];
}

function formatConvictionGradeTooltip(grade) {
  const guide = getConvictionGradeGuide(grade?.tier);
  const scoreLabel = isFiniteNumber(grade?.score) ? ` Score courant: ${grade.score}/100.` : '';
  return `${guide.tier} · ${guide.label} (${guide.range}) — ${guide.description}${scoreLabel}`;
}

function renderConvictionGradeTooltip(grade, onOpenLexicon) {
  const tooltipText = formatConvictionGradeTooltip(grade);
  if (typeof onOpenLexicon !== 'function') {
    return tooltipText;
  }

  return (
    <Stack spacing={0.7} sx={{ maxWidth: 340 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
        {tooltipText}
      </Typography>
      <Button
        size="small"
        variant="text"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenLexicon();
        }}
        sx={{
          alignSelf: 'flex-start',
          p: 0,
          minWidth: 0,
          textTransform: 'none',
          color: '#f3c65c',
          fontWeight: 700,
          ':hover': { background: 'transparent', textDecoration: 'underline' },
        }}
      >
        Voir le lexique
      </Button>
    </Stack>
  );
}

const GradeLegend = ({ onOpenLexicon }) => (
  <Stack spacing={0.8} sx={{ mt: 1.2 }}>
    <Typography variant="caption" sx={{ color: '#5f7279', fontWeight: 700, letterSpacing: '0.04em' }}>
      Lecture rapide des grades
    </Typography>
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
      {CONVICTION_GRADE_GUIDE.map((entry) => {
        const tone = entry.tier === 'A+'
          ? '#1f7a4d'
          : entry.tier === 'A'
            ? '#214f77'
            : entry.tier === 'B'
              ? '#8d6c16'
              : '#aa3d33';
        const background = entry.tier === 'A+'
          ? 'rgba(31,122,77,0.12)'
          : entry.tier === 'A'
            ? 'rgba(33,79,119,0.12)'
            : entry.tier === 'B'
              ? 'rgba(141,108,22,0.12)'
              : 'rgba(170,61,51,0.12)';

        return (
          <Tooltip key={entry.tier} title={renderConvictionGradeTooltip(entry, onOpenLexicon)} arrow>
            <Chip
              label={`${entry.tier} · ${entry.label}`}
              size="small"
              sx={{
                bgcolor: background,
                color: tone,
                fontWeight: 800,
                border: `1px solid ${tone}22`,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  </Stack>
);

function buildConvictionMatrix(analysis, direction) {
  const volumeStrength = buildVolumeStrength(analysis);
  const multiTimeframeDetail = analysis?.multiTimeframeDetail;
  const structureSignal = analysis?.signalFusionState ? formatSignalFusionState(analysis.signalFusionState) : 'Structure pure';
  const structureScore = isFiniteNumber(analysis?.confidence) ? formatConfidence(analysis.confidence) : null;
  const effectiveConviction = resolveEffectiveConvictionScore(analysis);

  return [
    {
      label: 'Structure',
      value: structureScore != null ? `${structureScore}%` : structureSignal,
      helper: analysis?.patternDetected ? getPatternTitle(analysis.patternDetected) : 'Pattern détecté',
      tone: '#17323c',
      background: '#eef4f6',
    },
    {
      label: 'Volume',
      value: volumeStrength.detail !== 'n/a' ? `${volumeStrength.label} · ${volumeStrength.detail}` : volumeStrength.label,
      helper: analysis?.volumeComment || 'Participation sur la cassure et la construction.',
      tone: volumeStrength.tone,
      background: volumeStrength.background,
    },
    {
      label: 'Divergence',
      value: hasActionableDivergenceSignal(analysis) ? formatDivergenceSummary(analysis) : 'Aucune',
      helper: analysis?.advancedDivergence?.indicator ? formatDivergenceIndicatorLabel(analysis.advancedDivergence.indicator) : 'Signal de provenance',
      tone: hasActionableDivergenceSignal(analysis) ? '#214f77' : '#6f7f85',
      background: hasActionableDivergenceSignal(analysis) ? 'rgba(33,79,119,0.12)' : 'rgba(111,127,133,0.12)',
    },
    {
      label: 'Momentum',
      value: hasActionableMomentumSignal(analysis) ? formatMomentumSummary(analysis) : 'Aucun',
      helper: analysis?.advancedMomentum?.comment || 'Impulse directionnelle.',
      tone: hasActionableMomentumSignal(analysis) ? '#1f7a4d' : '#6f7f85',
      background: hasActionableMomentumSignal(analysis) ? 'rgba(31,122,77,0.12)' : 'rgba(111,127,133,0.12)',
    },
    {
      label: 'Chandelier',
      value: hasActionableCandlestickSignal(analysis) ? formatCandlestickSummary(analysis) : 'Aucun',
      helper: analysis?.advancedCandlestick?.comment || 'Validation tactique court terme.',
      tone: hasActionableCandlestickSignal(analysis) ? '#8d6c16' : '#6f7f85',
      background: hasActionableCandlestickSignal(analysis) ? 'rgba(141,108,22,0.12)' : 'rgba(111,127,133,0.12)',
    },
    {
      label: 'MTF',
      value: isFiniteNumber(multiTimeframeDetail?.convictionScore) ? formatInsightScore(multiTimeframeDetail.convictionScore) : isFiniteNumber(effectiveConviction) ? formatInsightScore(effectiveConviction) : 'n/a',
      helper: multiTimeframeDetail?.dominantTimeframe
        ? `${directionHeadline(multiTimeframeDetail.dominantDirection || direction)} · ${multiTimeframeDetail.dominantTimeframe}`
        : 'Alignement inter-timeframe.',
      tone: '#17323c',
      background: '#fcf8ef',
    },
  ];
}

function getTradeValidationProfile(source) {
  const rawPayload = parsePatternRawJson(source?.rawJson);
  const pattern = (source?.patternDetected || rawPayload?.patternDetected || '').toUpperCase();

  if (pattern === 'DOUBLE_TOP') {
    return {
      pattern,
      structureLabel: 'Top Quality',
      structureHelper: 'Qualite des deux sommets, correction intermediaire et incapacite du second top a prolonger la hausse.',
      breakdownLabel: 'Breakdown',
      breakdownHelper: 'Cassure sous neckline recente, nette et encore exploitable sans faux breakdown immediat.',
      thresholdLabel: 'Tolerance sommets',
      breakdownMaxLabel: 'Breakdown max',
      preBreakdownLabel: 'Pre-breakdown max',
      descriptionSuffix: 'Le Double Top n’est retenu que si les cinq filtres obligatoires restent coherents sur cette fenetre.',
      defaultStructureType: 'Distribution Breakdown',
      defaultPatternLabel: 'Double Top Candidate',
    };
  }

  if (pattern === 'DOUBLE_BOTTOM') {
    return {
      pattern,
      structureLabel: 'Bottom Quality',
      structureHelper: 'Qualite des deux creux, profondeur du rebound intermediaire et capacite du second test a tenir le support.',
      breakdownLabel: 'Breakout',
      breakdownHelper: 'Breakout au-dessus de la neckline, recent et encore defendable sans extension deja consommee.',
      thresholdLabel: 'Tolerance creux',
      breakdownMaxLabel: 'Breakout max',
      preBreakdownLabel: 'Pre-breakout max',
      descriptionSuffix: 'Le Double Bottom n’est retenu que si le support est reellement defendu puis confirme par un breakout exploitable.',
      defaultStructureType: 'Reversal Breakout',
      defaultPatternLabel: 'Double Bottom Candidate',
    };
  }

  if (pattern === 'HEAD_AND_SHOULDERS') {
    return {
      pattern,
      structureLabel: 'Structure',
      structureHelper: 'Symetrie epaules / tete, prominence de la tete et incapacite de l’epaule droite a relancer la hausse.',
      breakdownLabel: 'Breakdown',
      breakdownHelper: 'Cassure de neckline recente, defendable et encore exploitable sans reintegration rapide.',
      thresholdLabel: 'Tolerances epaules / duree',
      breakdownMaxLabel: 'Breakdown max',
      preBreakdownLabel: 'Pre-breakdown max',
      descriptionSuffix: 'Le Head & Shoulders n’est retenu que si les cinq filtres obligatoires restent coherents sur cette fenetre.',
      defaultStructureType: 'Distribution Breakdown',
      defaultPatternLabel: 'Head & Shoulders Candidate',
    };
  }

  if (pattern === 'TRIPLE_TOP') {
    return {
      pattern,
      structureLabel: 'Top Quality',
      structureHelper: 'Cohérence des trois sommets, profondeur des replis intermédiaires et incapacité du troisième top à prolonger la hausse.',
      breakdownLabel: 'Breakdown',
      breakdownHelper: 'Cassure de neckline récente, nette et encore exploitable sans structure déjà consommée.',
      thresholdLabel: 'Tolérance sommets / durée',
      breakdownMaxLabel: 'Breakdown max',
      preBreakdownLabel: 'Pre-breakdown max',
      descriptionSuffix: 'Le Triple Top n’est retenu que si la troisième tentative échoue réellement et que la cassure reste tradable.',
      defaultStructureType: 'Distribution Breakdown',
      defaultPatternLabel: 'Triple Top Candidate',
    };
  }

  if (pattern === 'TRIPLE_BOTTOM') {
    return {
      pattern,
      structureLabel: 'Bottom Quality',
      structureHelper: 'Cohérence des trois creux, profondeur des rebounds et incapacité du troisième test à casser le support.',
      breakdownLabel: 'Breakout',
      breakdownHelper: 'Breakout de neckline récent, propre et encore tradable.',
      thresholdLabel: 'Tolérance creux / durée',
      breakdownMaxLabel: 'Breakout max',
      preBreakdownLabel: 'Pre-breakout max',
      descriptionSuffix: 'Le Triple Bottom reste plus strict que le Double Bottom: trois tests ne valent rien sans vraie sortie exploitable.',
      defaultStructureType: 'Reversal Breakout',
      defaultPatternLabel: 'Triple Bottom Candidate',
    };
  }

  if (pattern === 'ROUNDED_TOP') {
    return {
      pattern,
      structureLabel: 'Curvature',
      structureHelper: 'Authenticité de la courbure, discipline du retournement et support suffisamment défendable pour éviter les faux sommets arrondis.',
      breakdownLabel: 'Breakdown',
      breakdownHelper: 'Cassure sous la base arrondie récente, confirmée et pas déjà trop étendue.',
      thresholdLabel: 'Tolérance rims',
      breakdownMaxLabel: 'Breakdown max',
      preBreakdownLabel: 'Pre-breakdown max',
      descriptionSuffix: 'Le Rounded Top est volontairement plus strict que les figures A/B: la forme ne suffit jamais sans vraie cassure tradable.',
      defaultStructureType: 'Curved Distribution',
      defaultPatternLabel: 'Rounded Top Candidate',
    };
  }

  if (pattern === 'ROUNDED_BOTTOM') {
    return {
      pattern,
      structureLabel: 'Curvature',
      structureHelper: 'Authenticité de la base arrondie, compression du creux et transition progressive vers un vrai breakout.',
      breakdownLabel: 'Breakout',
      breakdownHelper: 'Breakout au-dessus des rims, récent et encore exploitable sans extension excessive.',
      thresholdLabel: 'Tolérance rims',
      breakdownMaxLabel: 'Breakout max',
      preBreakdownLabel: 'Pre-breakout max',
      descriptionSuffix: 'Le Rounded Bottom n’est retenu que si la base arrondie débouche sur un breakout concret avec qualité de trade suffisante.',
      defaultStructureType: 'Curved Base Breakout',
      defaultPatternLabel: 'Rounded Bottom Candidate',
    };
  }

  if (pattern === 'CHANNEL_BREAKOUT') {
    return {
      pattern,
      structureLabel: 'Channel Quality',
      structureHelper: 'Validite du canal, coherences des deux bornes et alternance reelle des contacts avant la sortie.',
      breakdownLabel: 'Breakout',
      breakdownHelper: 'Cassure confirmee au-dessus de la borne haute, avec cloture nette et extension encore tradable.',
      thresholdLabel: 'Largeur / alignement',
      breakdownMaxLabel: 'Breakout max',
      preBreakdownLabel: 'Pre-breakout max',
      descriptionSuffix: 'Le Channel Breakout n’est plus retenu sur simple contact de resistance ou wick isolee. Il faut un breakout confirme.',
      defaultStructureType: 'Channel Expansion Breakout',
      defaultPatternLabel: 'Channel Breakout Candidate',
    };
  }

  if (pattern === 'CUP_AND_HANDLE') {
    return {
      pattern,
      structureLabel: 'Cup Authenticity',
      structureHelper: 'Authenticite de la cup, symetrie des rims, qualite du handle et absence de V-shape artificiel.',
      breakdownLabel: 'Breakout',
      breakdownHelper: 'Breakout confirme au-dessus du rim, recent et encore exploitable apres le handle.',
      thresholdLabel: 'Tolérance rims / durée',
      breakdownMaxLabel: 'Breakout max',
      preBreakdownLabel: 'Pre-breakout max',
      descriptionSuffix: 'Le Cup & Handle n’est valide que si la structure est organique et que le breakout du rim est reelement confirme.',
      defaultStructureType: 'Rounded Continuation Breakout',
      defaultPatternLabel: 'Cup & Handle Candidate',
    };
  }

  if (pattern === 'DESCENDING_TRIANGLE') {
    return {
      pattern,
      structureLabel: 'Triangle Quality',
      structureHelper: 'Support horizontal defendable, resistance descendante et compression credible avant la cassure.',
      breakdownLabel: 'Breakdown',
      breakdownHelper: 'Cassure confirmee sous support, recente et encore tradable sans reintegration immediate.',
      thresholdLabel: 'Tolérance support',
      breakdownMaxLabel: 'Breakdown max',
      preBreakdownLabel: 'Pre-breakdown max',
      descriptionSuffix: 'Le Descending Triangle n’est valide que si le support casse vraiment. La simple proximite n’est jamais vendue comme signal.',
      defaultStructureType: 'Compression Breakdown',
      defaultPatternLabel: 'Descending Triangle Candidate',
    };
  }

  if (pattern === 'ASCENDING_TRIANGLE') {
    return {
      pattern,
      structureLabel: 'Triangle Quality',
      structureHelper: 'Resistance defendue, support ascendant credible et compression réelle avant la sortie.',
      breakdownLabel: 'Breakout',
      breakdownHelper: 'Breakout au-dessus de la borne haute, recent et encore tradable.',
      thresholdLabel: 'Compression / support',
      breakdownMaxLabel: 'Breakout max',
      preBreakdownLabel: 'Pre-breakout max',
      descriptionSuffix: 'L’Ascending Triangle n’est pas un dessin: il faut une vraie compression haussière et une sortie exploitable.',
      defaultStructureType: 'Compression Breakout',
      defaultPatternLabel: 'Ascending Triangle Candidate',
    };
  }

  if (pattern === 'BULL_FLAG' || pattern === 'BEAR_FLAG') {
    return {
      pattern,
      structureLabel: 'Flag Quality',
      structureHelper: pattern === 'BULL_FLAG'
        ? 'Impulsion initiale, retracement contenu et canal correctif réellement contre-tendance.'
        : 'Impulsion baissière initiale, rebond contenu et canal correctif réellement contre-tendance.',
      breakdownLabel: pattern === 'BULL_FLAG' ? 'Breakout' : 'Breakdown',
      breakdownHelper: pattern === 'BULL_FLAG'
        ? 'Sortie haussière du flag, récente et encore exploitable.'
        : 'Sortie baissière du flag, récente et encore exploitable.',
      thresholdLabel: 'Impulse / retracement',
      breakdownMaxLabel: pattern === 'BULL_FLAG' ? 'Breakout max' : 'Breakdown max',
      preBreakdownLabel: pattern === 'BULL_FLAG' ? 'Pre-breakout max' : 'Pre-breakdown max',
      descriptionSuffix: 'Le flag n’est retenu que si l’impulsion existe vraiment et que la consolidation reste disciplinée.',
      defaultStructureType: 'Continuation Pullback',
      defaultPatternLabel: pattern === 'BULL_FLAG' ? 'Bull Flag Candidate' : 'Bear Flag Candidate',
    };
  }

  if (pattern === 'FALLING_WEDGE' || pattern === 'RISING_WEDGE') {
    return {
      pattern,
      structureLabel: 'Wedge Quality',
      structureHelper: pattern === 'FALLING_WEDGE'
        ? 'Convergence baissière, contacts crédibles et essoufflement vendeur avant sortie haussière.'
        : 'Convergence haussière, contacts crédibles et essoufflement acheteur avant sortie baissière.',
      breakdownLabel: pattern === 'FALLING_WEDGE' ? 'Breakout' : 'Breakdown',
      breakdownHelper: pattern === 'FALLING_WEDGE'
        ? 'Cassure au-dessus de la borne haute, récente et exploitable.'
        : 'Cassure sous la borne basse, récente et exploitable.',
      thresholdLabel: 'Convergence / compression',
      breakdownMaxLabel: pattern === 'FALLING_WEDGE' ? 'Breakout max' : 'Breakdown max',
      preBreakdownLabel: pattern === 'FALLING_WEDGE' ? 'Pre-breakout max' : 'Pre-breakdown max',
      descriptionSuffix: 'Le wedge n’est retenu que si la compression traduit un vrai essoufflement, pas une simple diagonale visuelle.',
      defaultStructureType: 'Converging Exhaustion',
      defaultPatternLabel: pattern === 'FALLING_WEDGE' ? 'Falling Wedge Candidate' : 'Rising Wedge Candidate',
    };
  }

  if (pattern === 'BREAKOUT_RANGE' || pattern === 'BREAKDOWN_RANGE') {
    return {
      pattern,
      structureLabel: 'Range Quality',
      structureHelper: 'Multiples défenses sur les deux bornes, box propre et pas de faux milieu de range vendu comme signal.',
      breakdownLabel: pattern === 'BREAKOUT_RANGE' ? 'Breakout' : 'Breakdown',
      breakdownHelper: pattern === 'BREAKOUT_RANGE'
        ? 'Cassure au-dessus du range, récente et encore exploitable.'
        : 'Cassure sous le range, récente et encore exploitable.',
      thresholdLabel: 'Contacts / box',
      breakdownMaxLabel: pattern === 'BREAKOUT_RANGE' ? 'Breakout max' : 'Breakdown max',
      preBreakdownLabel: pattern === 'BREAKOUT_RANGE' ? 'Pre-breakout max' : 'Pre-breakdown max',
      descriptionSuffix: 'Le range n’est retenu que si la structure est réellement défendable avant expansion.',
      defaultStructureType: 'Range Expansion',
      defaultPatternLabel: pattern === 'BREAKOUT_RANGE' ? 'Breakout Range Candidate' : 'Breakdown Range Candidate',
    };
  }

  if (pattern === 'ACCUMULATION_BASE' || pattern === 'DISTRIBUTION_TOP') {
    return {
      pattern,
      structureLabel: 'Base Quality',
      structureHelper: pattern === 'ACCUMULATION_BASE'
        ? 'Série de défenses sur support, plafond absorbé progressivement et préparation d’expansion haussière.'
        : 'Série d’échecs sous résistance, support fragilisé et préparation d’expansion baissière.',
      breakdownLabel: pattern === 'ACCUMULATION_BASE' ? 'Breakout State' : 'Breakdown State',
      breakdownHelper: 'Etat de maturité de la structure: pré-breakout, candidate, confirmé ou retest.',
      thresholdLabel: 'Clean base',
      breakdownMaxLabel: pattern === 'ACCUMULATION_BASE' ? 'Breakout max' : 'Breakdown max',
      preBreakdownLabel: pattern === 'ACCUMULATION_BASE' ? 'Pre-breakout max' : 'Pre-breakdown max',
      descriptionSuffix: 'Ces patterns de base ne sont retenus que si la construction est lisible et offre encore un trade propre.',
      defaultStructureType: pattern === 'ACCUMULATION_BASE' ? 'Constructive Base' : 'Distribution Ceiling',
      defaultPatternLabel: pattern === 'ACCUMULATION_BASE' ? 'Accumulation Base Candidate' : 'Distribution Top Candidate',
    };
  }

  if (pattern === 'RECTANGLE' || pattern === 'SYMMETRICAL_TRIANGLE' || pattern === 'CHANNEL' || pattern === 'COMPRESSION') {
    const labelMap = {
      RECTANGLE: 'Rectangle Candidate',
      SYMMETRICAL_TRIANGLE: 'Symmetrical Triangle Candidate',
      CHANNEL: 'Channel Candidate',
      COMPRESSION: 'Compression Candidate',
    };
    return {
      pattern,
      structureLabel: 'Structure Quality',
      structureHelper: 'Lisibilité des bornes, discipline des contacts et qualité de la compression ou du couloir.',
      breakdownLabel: 'Expansion State',
      breakdownHelper: 'Etat de maturité de la sortie potentielle: pré-breakout, candidate, confirmé ou retest.',
      thresholdLabel: 'Clean structure',
      breakdownMaxLabel: 'Expansion max',
      preBreakdownLabel: 'Pre-expansion max',
      descriptionSuffix: 'Les structures neutres ne sont plus vendues comme directionnelles par défaut: seule la qualité d’expansion compte.',
      defaultStructureType: pattern === 'RECTANGLE'
        ? 'Balanced Range'
        : pattern === 'SYMMETRICAL_TRIANGLE'
          ? 'Balanced Compression'
          : pattern === 'CHANNEL'
            ? 'Parallel Trend Structure'
            : 'Volatility Compression',
      defaultPatternLabel: labelMap[pattern],
    };
  }

  return {
    pattern,
    structureLabel: 'Structure',
    structureHelper: 'Symetrie epaules / tete et qualite de la construction globale.',
    breakdownLabel: 'Breakout',
    breakdownHelper: 'Cassure recente ou proche, encore exploitable et pas deja consommee.',
    thresholdLabel: 'Tolerances epaules / duree',
    breakdownMaxLabel: 'Breakout max',
    preBreakdownLabel: 'Pre-breakout max',
    descriptionSuffix: 'L’IHS n’est retenu que si les cinq filtres obligatoires restent coherents sur cette fenetre.',
    defaultStructureType: 'Reversal Breakout',
    defaultPatternLabel: 'Inverse Head & Shoulders Candidate',
  };
}

function supportsTradeValidation(pattern) {
  const normalizedPattern = (pattern || '').toUpperCase();
  return normalizedPattern === 'DOUBLE_TOP'
    || normalizedPattern === 'DOUBLE_BOTTOM'
    || normalizedPattern === 'TRIPLE_TOP'
    || normalizedPattern === 'TRIPLE_BOTTOM'
    || normalizedPattern === 'HEAD_AND_SHOULDERS'
    || normalizedPattern === 'INVERSE_HEAD_AND_SHOULDERS'
    || normalizedPattern === 'ROUNDED_TOP'
    || normalizedPattern === 'ROUNDED_BOTTOM'
    || normalizedPattern === 'CHANNEL_BREAKOUT'
    || normalizedPattern === 'CUP_AND_HANDLE'
    || normalizedPattern === 'DESCENDING_TRIANGLE'
    || normalizedPattern === 'ASCENDING_TRIANGLE'
    || normalizedPattern === 'BULL_FLAG'
    || normalizedPattern === 'BEAR_FLAG'
    || normalizedPattern === 'FALLING_WEDGE'
    || normalizedPattern === 'RISING_WEDGE'
    || normalizedPattern === 'BREAKOUT_RANGE'
    || normalizedPattern === 'BREAKDOWN_RANGE'
    || normalizedPattern === 'ACCUMULATION_BASE'
    || normalizedPattern === 'DISTRIBUTION_TOP'
    || normalizedPattern === 'RECTANGLE'
    || normalizedPattern === 'SYMMETRICAL_TRIANGLE'
    || normalizedPattern === 'CHANNEL'
    || normalizedPattern === 'COMPRESSION';
}

function tradeValidationPatternLabel(pattern) {
  const normalizedPattern = (pattern || '').toUpperCase();
  return getPatternTitle(normalizedPattern) || normalizedPattern || 'ce pattern';
}

function extractPatternDiagnostics(source) {
  const sourceDiagnostics = source?.advancedDiagnostics && typeof source.advancedDiagnostics === 'object'
    ? source.advancedDiagnostics
    : source?.diagnostics && typeof source.diagnostics === 'object'
      ? source.diagnostics
      : null;
  const rawPayload = parsePatternRawJson(source?.rawJson);
  const diagnostics = rawPayload?.diagnostics && typeof rawPayload.diagnostics === 'object'
    ? rawPayload.diagnostics
    : sourceDiagnostics;
  const validation = rawPayload?.validation && typeof rawPayload.validation === 'object'
    ? rawPayload.validation
    : sourceDiagnostics?.validation && typeof sourceDiagnostics.validation === 'object'
      ? sourceDiagnostics.validation
    : diagnostics?.validation && typeof diagnostics.validation === 'object'
      ? diagnostics.validation
      : null;

  if (!validation) {
    return null;
  }

  const thresholds = rawPayload?.validationThresholds && typeof rawPayload.validationThresholds === 'object'
    ? rawPayload.validationThresholds
    : sourceDiagnostics?.thresholds && typeof sourceDiagnostics.thresholds === 'object'
      ? sourceDiagnostics.thresholds
    : diagnostics?.thresholds && typeof diagnostics.thresholds === 'object'
      ? diagnostics.thresholds
      : null;

  return {
    rawPayload,
    validation,
    thresholds,
    strategyPrinciple: diagnostics?.strategyPrinciple || rawPayload?.strategyPrinciple || 'Nous détectons des trades, pas des dessins.',
    structureType: diagnostics?.structureType || rawPayload?.structureType || null,
    patternLabel: diagnostics?.patternLabel || rawPayload?.patternLabel || null,
    tradeQualityLabel: diagnostics?.tradeQualityLabel || rawPayload?.tradeQualityLabel || null,
    confidenceLabel: diagnostics?.confidenceLabel || rawPayload?.confidenceLabel || null,
    reliabilityTier: diagnostics?.reliabilityTier || rawPayload?.reliabilityTier || null,
    reliabilityLabel: diagnostics?.reliabilityLabel || rawPayload?.reliabilityLabel || null,
    patternDetected: (rawPayload?.patternDetected || source?.patternDetected || '').toUpperCase(),
  };
}

function deriveValidationTradeQualityLabel(validation) {
  const tradeQuality = validation?.tradeQuality ?? 0;
  const riskRewardRatio = validation?.riskRewardRatio ?? 0;
  const fakeoutRisk = validation?.estimatedFakeoutRisk ?? 100;

  if (tradeQuality >= 0.78 && riskRewardRatio >= 2.0 && fakeoutRisk <= 34) {
    return 'Qualité A';
  }
  if (tradeQuality >= 0.58 && riskRewardRatio >= 1.6 && fakeoutRisk <= 52) {
    return 'Qualité B';
  }
  return 'Qualité C';
}

function deriveValidationConfidenceLabel(validation) {
  const score = validation?.score ?? 0;
  const threshold = validation?.threshold ?? 0;

  if (score >= threshold + 0.12) {
    return 'Forte';
  }
  if (score >= threshold + 0.04) {
    return 'Moyenne';
  }
  return 'Faible';
}

function buildTradeValidationMeta(source) {
  const diagnostics = extractPatternDiagnostics(source);
  if (!diagnostics) {
    return [];
  }

  const profile = getTradeValidationProfile(source);

  const meta = [
    {
      label: 'Type de structure',
      value: localizeTradeLabel(diagnostics.structureType || profile.defaultStructureType),
    },
    {
      label: 'Lecture pattern',
      value: localizeTradeLabel(diagnostics.patternLabel) || getPatternTitle(diagnostics.patternDetected || source?.patternDetected || profile.pattern),
    },
  ];

  if (diagnostics.validation?.breakoutStateLabel) {
    meta.push({
      label: 'État de cassure',
      value: localizeTradeLabel(diagnostics.validation.breakoutStateLabel),
    });
  }

  if (typeof diagnostics.validation?.assetEligibilityScore === 'number') {
    const reasons = Array.isArray(diagnostics.validation.assetEligibilityReasons)
      ? diagnostics.validation.assetEligibilityReasons.filter(Boolean).join(', ')
      : '';
    meta.push({
      label: 'Éligibilité actif',
      value: reasons
        ? `${formatNormalizedScore(diagnostics.validation.assetEligibilityScore)} · ${reasons}`
        : formatNormalizedScore(diagnostics.validation.assetEligibilityScore),
    });
  }

  meta.push(
    {
      label: 'Qualité trade',
      value: localizeTradeLabel(diagnostics.tradeQualityLabel || deriveValidationTradeQualityLabel(diagnostics.validation)),
    },
    {
      label: 'Confiance',
      value: localizeTradeLabel(diagnostics.confidenceLabel || deriveValidationConfidenceLabel(diagnostics.validation)),
    },
    {
      label: 'Fiabilité pattern',
      value: diagnostics.reliabilityTier && diagnostics.reliabilityLabel
        ? `${diagnostics.reliabilityTier} · ${diagnostics.reliabilityLabel}`
        : localizeTradeLabel(diagnostics.reliabilityTier || diagnostics.reliabilityLabel || 'n/a'),
    },
  );

  return meta;
}

function buildTradeValidationBreakdown(source) {
  const diagnostics = extractPatternDiagnostics(source);
  if (!diagnostics) {
    return [];
  }

  const { validation } = diagnostics;
  const profile = getTradeValidationProfile(source);
  const breakdownScore = validation.breakdownQuality ?? validation.breakoutQuality ?? 0;
  const boundaryScore = validation.necklineScore ?? validation.boundaryScore ?? validation.executionScore;

  const items = [
    {
      label: 'Contexte',
      helper: 'Contexte baissier préalable avant tentative de retournement.',
      value: clampPercent((validation.contextScore ?? 0) * 100),
      display: formatNormalizedScore(validation.contextScore),
      accent: '#264653',
    },
    {
      label: localizeTradeLabel(profile.structureLabel),
      helper: profile.structureHelper,
      value: clampPercent((validation.structureScore ?? 0) * 100),
      display: formatNormalizedScore(validation.structureScore),
      accent: '#2f6690',
    },
    {
      label: localizeTradeLabel(profile.breakdownLabel),
      helper: profile.breakdownHelper,
      value: clampPercent(breakdownScore * 100),
      display: formatNormalizedScore(breakdownScore),
      accent: '#1f7a4d',
    },
  ];

  if (typeof boundaryScore === 'number') {
    items.splice(2, 0, {
      label: 'Frontière',
      helper: 'Zone defendable de validation structurelle: neckline, rim ou borne chartiste selon le pattern.',
      value: clampPercent(boundaryScore * 100),
      display: formatNormalizedScore(boundaryScore),
      accent: '#7b5b10',
    });
  }

  if (typeof validation.volumeConfirmationScore === 'number') {
    items.push({
      label: 'Volume',
      helper: 'Transition de volume entre la construction de la figure et la cassure validée.',
      value: clampPercent(validation.volumeConfirmationScore * 100),
      display: formatNormalizedScore(validation.volumeConfirmationScore),
      accent: '#8f6b10',
    });
  }

  if (typeof validation.assetEligibilityScore === 'number') {
    items.push({
      label: 'Éligibilité',
      helper: 'Crédibilité chartiste de l’actif selon volatilité, stabilité et participation. Une pénalité réduit nativement la confiance.',
      value: clampPercent(validation.assetEligibilityScore * 100),
      display: formatNormalizedScore(validation.assetEligibilityScore),
      accent: '#5f4b8b',
    });
  }

  items.push({
    label: 'Exécution',
    helper: 'Exploitabilité réelle du setup via R/R, fakeout et confirmation de volume.',
    value: clampPercent((validation.tradeQuality ?? 0) * 100),
    display: formatNormalizedScore(validation.tradeQuality),
    accent: '#aa3d33',
  });

  return items;
}

function buildTradeValidationThresholds(source) {
  const diagnostics = extractPatternDiagnostics(source);
  if (!diagnostics?.thresholds) {
    return [];
  }

  const { thresholds } = diagnostics;
  const profile = getTradeValidationProfile(source);
  const weights = thresholds.weights || {};
  const isDirectionalWeightProfile = weights.boundary != null || weights.breakout != null;
  const isStateWeightProfile = weights.confirmation != null;
  const toleranceValue = thresholds.toleranceDisplay || (
    thresholds.maxTopTolerance != null
      ? `${Math.round((thresholds.maxTopTolerance ?? 0) * 100)}%`
      : thresholds.maxRimTolerance != null
        ? `${Math.round((thresholds.maxRimTolerance ?? 0) * 100)}%`
        : thresholds.maxSupportTolerance != null
          ? `${Math.round((thresholds.maxSupportTolerance ?? 0) * 100)}%`
          : `${Math.round((thresholds.maxShoulderDepthTolerance ?? 0) * 100)}% / ${Math.round((thresholds.maxDurationTolerance ?? 0) * 100)}%`
  );
  const breakdownAtr = thresholds.maxBreakdownAtr ?? thresholds.maxBreakoutAtr;
  const breakdownPercent = thresholds.maxBreakdownPercent ?? thresholds.maxBreakoutPercent;
  const preBreakdownAtr = thresholds.maxPreBreakdownAtr ?? thresholds.maxPreBreakoutAtr;
  const preBreakdownPercent = thresholds.maxPreBreakdownPercent ?? thresholds.maxPreBreakoutPercent;
  const weightBoundary = weights.boundary ?? weights.neckline ?? weights.execution ?? 0;
  const weightBreakout = weights.breakout ?? 0;
  const weightLabel = isStateWeightProfile
    ? 'Poids S/Cf/V/T'
    : isDirectionalWeightProfile
      ? 'Poids C/S/B/Br/T'
      : 'Poids score';
  const weightValue = isStateWeightProfile
    ? `${Math.round((weights.structure ?? 0) * 100)} / ${Math.round((weights.confirmation ?? 0) * 100)} / ${Math.round((weights.volume ?? 0) * 100)} / ${Math.round((weights.trade ?? 0) * 100)}`
    : isDirectionalWeightProfile
      ? `${Math.round((weights.context ?? 0) * 100)} / ${Math.round((weights.structure ?? 0) * 100)} / ${Math.round(weightBoundary * 100)} / ${Math.round(weightBreakout * 100)} / ${Math.round((weights.trade ?? 0) * 100)}`
      : `${Math.round((weights.context ?? 0) * 100)} / ${Math.round((weights.structure ?? 0) * 100)} / ${Math.round(weightBoundary * 100)} / ${Math.round(weightBreakout * 100)} / ${Math.round((weights.trade ?? 0) * 100)}`;
  const breakoutLimitValue = breakdownAtr != null && breakdownPercent != null
    ? `${formatRatio(breakdownAtr)} ATR ou ${Math.round((breakdownPercent ?? 0) * 100)}%`
    : thresholds.maxStructureWidthRatio != null
      ? `${Math.round((thresholds.maxStructureWidthRatio ?? 0) * 100)}% largeur max`
      : 'State-driven';
  const thresholdItems = [
    {
      label: 'Seuil global',
      value: formatNormalizedScore(thresholds.scoreThreshold),
    },
    {
      label: profile.thresholdLabel,
      value: toleranceValue,
    },
    {
      label: 'R/R mini / fakeout max',
      value: `${formatRatio(thresholds.minRiskRewardRatio)} / ${Math.round(thresholds.maxFakeoutRisk ?? 0)}%`,
    },
    {
      label: profile.breakdownMaxLabel,
      value: breakoutLimitValue,
    },
    {
      label: weightLabel,
      value: weightValue,
    },
  ];

  if (preBreakdownAtr != null && preBreakdownPercent != null) {
    thresholdItems.splice(4, 0, {
      label: profile.preBreakdownLabel,
      value: `${formatRatio(preBreakdownAtr)} ATR ou ${Math.round((preBreakdownPercent ?? 0) * 100)}%`,
    });
  }

  if (thresholds.minBreakoutRecency != null) {
    thresholdItems.splice(4, 0, {
      label: 'Recency mini',
      value: formatNormalizedScore(thresholds.minBreakoutRecency),
    });
  }

  return thresholdItems;
}

function scannerRowToAnalysis(row) {
  const normalizedRow = normalizeScannerRow(row);
  const rawPayload = parsePatternRawJson(normalizedRow?.rawJson);
  return {
    ...normalizedRow,
    patternDetected: normalizedRow.detectedPattern,
    direction: deriveRowDirection(normalizedRow),
    patternScore: normalizedRow.scoreGlobal,
    timeToResolution: normalizedRow.timeframe,
    analysisSource: rawPayload?.analysisSource || normalizedRow.analysisSource || 'SCANNER_ROW',
    rawJson: normalizedRow.rawJson,
  };
}

function mergeCurrentAnalysisIntoScannerRows(rows, analysis) {
  if (!Array.isArray(rows) || !rows.length || !analysis?.symbol || !analysis?.exchange || !analysis?.timeframe) {
    return Array.isArray(rows) ? rows : [];
  }

  const targetSymbol = String(analysis.symbol).toUpperCase();
  const targetExchange = String(analysis.exchange).toUpperCase();
  const targetTimeframe = String(analysis.timeframe).toUpperCase();
  let merged = false;

  const nextRows = rows.map((row) => {
    if (
      String(row?.symbol || '').toUpperCase() !== targetSymbol
      || String(row?.exchange || '').toUpperCase() !== targetExchange
      || String(row?.timeframe || '').toUpperCase() !== targetTimeframe
    ) {
      return row;
    }

    merged = true;
    return normalizeScannerRow({
      ...row,
      exchange: analysis.exchange,
      timeframe: analysis.timeframe,
      detectedPattern: analysis.patternDetected,
      confidence: analysis.confidence,
      pumpProbability: analysis.pumpProbability,
      dumpProbability: analysis.dumpProbability,
      fakeoutRisk: analysis.fakeoutRisk,
      breakoutLevel: analysis.breakoutLevel,
      targetPrice: analysis.targetPrice,
      stopInvalidation: analysis.stopInvalidation,
      riskRewardRatio: analysis.riskRewardRatio,
      targetConfidence: analysis.targetConfidence,
      volumeConfirmation: analysis.volumeConfirmation,
      volumeConfirmationScore: analysis.volumeConfirmationScore,
      breakoutVolumeRatio: analysis.breakoutVolumeRatio,
      volumeTrend: analysis.volumeTrend,
      volumeComment: analysis.volumeComment,
      fakeoutRiskAdjusted: analysis.fakeoutRiskAdjusted,
      scoreGlobal: typeof analysis.patternScore === 'number' ? analysis.patternScore : row.scoreGlobal,
      bullish: analysis.direction === 'BULLISH',
      bearish: analysis.direction === 'BEARISH',
      aiSummary: analysis.aiSummary,
      hasAnnotatedImage: Boolean(analysis.imagePath || row.imagePath),
      imagePath: analysis.imagePath || row.imagePath || '',
      imageGeneratedAt: analysis.imageGeneratedAt || row.imageGeneratedAt || null,
      imageVersion: analysis.imageVersion || row.imageVersion || '',
      analysisTimestamp: analysis.analysisTimestamp,
      rawJson: analysis.rawJson,
    });
  });

  if (!merged) {
    return rows;
  }

  return reprioritizeScannerRows(nextRows);
}

const PatternScannerPage = () => {
  const [singleState, setSingleState] = useState(defaultSingleState);
  const [filters, setFilters] = useState(defaultFilters);
  const [learningOpen, setLearningOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [singleAnalysis, setSingleAnalysis] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [scannerRows, setScannerRows] = useState([]);
  const [scannerHydrated, setScannerHydrated] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(true);
  const [singleError, setSingleError] = useState('');
  const [scannerError, setScannerError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [patternHistory, setPatternHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [scannerProgress, setScannerProgress] = useState(emptyScannerProgress);
  const [expandedRowKey, setExpandedRowKey] = useState('');
  const [calibrationRequested, setCalibrationRequested] = useState(false);
  const [calibrationLoading, setCalibrationLoading] = useState(false);
  const [calibrationError, setCalibrationError] = useState('');
  const [calibrationResults, setCalibrationResults] = useState([]);
  const [calibrationInsights, setCalibrationInsights] = useState(null);
  const [visualMode, setVisualMode] = useState('4H');
  const [activeInsightTab, setActiveInsightTab] = useState('summary');
  const [quickActionFeedback, setQuickActionFeedback] = useState('');
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramPreview, setTelegramPreview] = useState(null);
  const [telegramPreviewLoading, setTelegramPreviewLoading] = useState(false);
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramSending, setTelegramSending] = useState(false);
  const [telegramError, setTelegramError] = useState('');
  const [telegramNotice, setTelegramNotice] = useState('');
  const [telegramSendAvailable, setTelegramSendAvailable] = useState(true);
  const [telegramSendStatusMessage, setTelegramSendStatusMessage] = useState('');
  const [telegramPreviewImageFailures, setTelegramPreviewImageFailures] = useState({});
  const [compactProMode, setCompactProMode] = useState(() => loadCompactProPreference());
  const [patternImageBlobUrl, setPatternImageBlobUrl] = useState('');
  const bootstrapGuardRef = useRef(false);
  const scannerProgressIntervalRef = useRef(null);
  const focusSectionRef = useRef(null);
  const timeframeImageMemoryRef = useRef(new Map());

  const rememberPatternImageSource = useCallback((source) => {
    const lookupKey = buildPatternAnalysisLookupKey(source?.symbol, source?.exchange, source?.timeframe);
    const metadata = extractPatternImageMetadata(source);
    if (!lookupKey || !metadata) {
      return;
    }

    timeframeImageMemoryRef.current.set(lookupKey, metadata);
  }, []);

  const resolveTimeframeImageSeed = useCallback((symbol, exchange, timeframe, preferredSeed = null) => {
    const lookupKey = buildPatternAnalysisLookupKey(symbol, exchange, timeframe);
    const normalizedPreferredSeed = normalizePatternAnalysis(preferredSeed || null);
    if (!lookupKey) {
      return normalizedPreferredSeed;
    }

    const preferredSeedMatches = buildPatternAnalysisLookupKey(
      normalizedPreferredSeed?.symbol,
      normalizedPreferredSeed?.exchange,
      normalizedPreferredSeed?.timeframe,
    ) === lookupKey;

    if (preferredSeedMatches && extractPatternImageMetadata(normalizedPreferredSeed)) {
      return normalizedPreferredSeed;
    }

    const scannerRowCandidate = scannerRows.find((row) => (
      buildPatternAnalysisLookupKey(row?.symbol, row?.exchange, row?.timeframe) === lookupKey
      && normalizeUiText(row?.imagePath)
    ));
    if (scannerRowCandidate) {
      return scannerRowToAnalysis(scannerRowCandidate);
    }

    const historyCandidate = patternHistory.find((entry) => (
      buildPatternAnalysisLookupKey(entry?.symbol, entry?.exchange, entry?.timeframe) === lookupKey
      && normalizeUiText(entry?.imagePath)
    ));
    if (historyCandidate) {
      return normalizePatternAnalysis(historyCandidate);
    }

    const rememberedMetadata = timeframeImageMemoryRef.current.get(lookupKey);
    if (!rememberedMetadata) {
      return preferredSeedMatches ? normalizedPreferredSeed : null;
    }

    return {
      ...(preferredSeedMatches
        ? normalizedPreferredSeed
        : {
          symbol: normalizeUiText(symbol).toUpperCase(),
          exchange: normalizeUiText(exchange).toUpperCase() || 'BINANCE',
          timeframe: normalizeUiText(timeframe).toUpperCase(),
        }),
      ...rememberedMetadata,
    };
  }, [patternHistory, scannerRows]);

  const currentSingleAnalysis = useMemo(() => normalizePatternAnalysis(singleAnalysis || fallbackSingle), [singleAnalysis]);
  const sourceScannerRows = useMemo(() => {
    const rows = !scannerHydrated && scannerRows.length === 0
      ? fallbackScanner
      : scannerRows;
    return reprioritizeScannerRows(rows.map(normalizeScannerRow), filters.radarSortMode);
  }, [filters.radarSortMode, scannerHydrated, scannerRows]);
  const filteredScannerRows = useMemo(
    () => reprioritizeScannerRows(sourceScannerRows.filter(
      (row) => (!filters.patternType || row.detectedPattern === filters.patternType)
        && (row.scoreGlobal ?? 0) >= filters.minScore
        && ((resolveEffectiveConvictionScore(row) ?? 0) >= filters.minConviction)
        && (!filters.advancedSignalsOnly || hasAdvancedSignalCoverage(row)),
    ), filters.radarSortMode),
    [filters.advancedSignalsOnly, filters.minConviction, filters.minScore, filters.patternType, filters.radarSortMode, sourceScannerRows],
  );
  const topSetup = filteredScannerRows[0] || sourceScannerRows[0] || null;
  const topSetupHasAdvancedSignals = useMemo(() => hasAdvancedSignalCoverage(topSetup), [topSetup]);
  const topSetupVolumeStrength = useMemo(() => buildVolumeStrength(topSetup), [topSetup]);
  const currentDirection = currentSingleAnalysis.direction || 'NEUTRAL';
  const currentDiagnostics = useMemo(() => extractPatternDiagnostics(currentSingleAnalysis), [currentSingleAnalysis]);
  const currentVolumeStrength = useMemo(() => buildVolumeStrength(currentSingleAnalysis), [currentSingleAnalysis]);
  const currentEffectiveConvictionScore = useMemo(() => resolveEffectiveConvictionScore(currentSingleAnalysis), [currentSingleAnalysis]);
  const currentConvictionGrade = useMemo(
    () => buildConvictionGrade(currentSingleAnalysis, currentDirection),
    [currentDirection, currentSingleAnalysis],
  );
  const currentSignalFusionPalette = useMemo(
    () => buildSignalFusionPalette(currentSingleAnalysis.signalFusionState, currentEffectiveConvictionScore),
    [currentEffectiveConvictionScore, currentSingleAnalysis.signalFusionState],
  );
  const riskLevel = useMemo(
    () => computeRiskLevel(
      currentSingleAnalysis.fakeoutRisk,
      currentSingleAnalysis.riskRewardRatio,
      currentSingleAnalysis.targetConfidence,
      currentSingleAnalysis.fakeoutRiskAdjusted,
    ),
    [
      currentSingleAnalysis.fakeoutRisk,
      currentSingleAnalysis.riskRewardRatio,
      currentSingleAnalysis.targetConfidence,
      currentSingleAnalysis.fakeoutRiskAdjusted,
    ],
  );
  const confidenceBreakdown = useMemo(
    () => buildConfidenceBreakdown(currentSingleAnalysis, currentDirection),
    [currentSingleAnalysis, currentDirection],
  );
  const historyEntries = patternHistory.length ? patternHistory.slice(0, 6) : buildFallbackHistory(currentSingleAnalysis);
  const scannerProgressPercent = scannerProgress.total > 0 ? scannerProgress.percent : 0;
  const requestedUniverseSize = useMemo(() => resolveUniverseSizeValue(filters.universe), [filters.universe]);
  const activeRadarTimeframe = useMemo(
    () => normalizeUiText(filters.radarTimeframe).toUpperCase() || normalizeUiText(singleState.timeframe).toUpperCase() || '4H',
    [filters.radarTimeframe, singleState.timeframe],
  );
  const activeRadarSortMode = useMemo(
    () => normalizeRadarSortMode(filters.radarSortMode),
    [filters.radarSortMode],
  );
  const displayedScannerRowsCount = sourceScannerRows.length;
  const radarCoverageLabel = useMemo(() => {
    if (requestedUniverseSize <= 0) {
      return `${displayedScannerRowsCount} actif${displayedScannerRowsCount > 1 ? 's' : ''} charges`;
    }
    return `Couverture ${displayedScannerRowsCount}/${requestedUniverseSize}`;
  }, [displayedScannerRowsCount, requestedUniverseSize]);
  const localPatternFilterLabel = filters.patternType ? getPatternTitle(filters.patternType) : 'Tous les patterns';
  const localRadarFilterSummary = useMemo(() => {
    const parts = [localPatternFilterLabel];
    if (filters.minScore > 0) {
      parts.push(`score min ${filters.minScore}`);
    }
    if (filters.minConviction > 0) {
      parts.push(`conviction min ${filters.minConviction}`);
    }
    if (filters.advancedSignalsOnly) {
      parts.push('signaux avancés uniquement');
    }
    return parts.join(', ');
  }, [filters.advancedSignalsOnly, filters.minConviction, filters.minScore, localPatternFilterLabel]);
  const hasLocalScannerFilters = Boolean(filters.patternType) || filters.minScore > 0 || filters.minConviction > 0 || filters.advancedSignalsOnly;
  const analysisSourceLabel = formatAnalysisSource(currentSingleAnalysis.analysisSource);
  const visualSourceLabel = resolveVisualSource(currentSingleAnalysis);
  const hybridVisualAnalysis = isHybridVisualAnalysis(currentSingleAnalysis);
  const hasAnnotatedVisual = Boolean(patternImageBlobUrl) || (Boolean(currentSingleAnalysis.imagePath) && !imageLoadFailed);
  const calibrationResultsByTimeframe = useMemo(() => {
    const entries = new Map();
    calibrationResults.forEach((result) => {
      const timeframe = result?.analysis?.timeframe;
      if (timeframe) {
        entries.set(String(timeframe).toUpperCase(), result);
      }
    });
    return entries;
  }, [calibrationResults]);
  const setupVerdict = useMemo(
    () => computeSetupVerdict(currentSingleAnalysis, currentDirection),
    [currentDirection, currentSingleAnalysis],
  );
  const topScannerRows = useMemo(() => filteredScannerRows.slice(0, 3), [filteredScannerRows]);
  const remainingScannerRows = useMemo(() => filteredScannerRows.slice(3), [filteredScannerRows]);
  const historyTimelineEntries = useMemo(() => historyEntries.slice(0, 4), [historyEntries]);
  const telegramChatIdValidationMessage = getTelegramChatIdValidationMessage(telegramChatId);
  const telegramMultiTimeframeCharts = useMemo(
    () => (Array.isArray(telegramPreview?.multiTimeframeCharts) ? telegramPreview.multiTimeframeCharts : []),
    [telegramPreview],
  );
  const quickActions = useMemo(
    () => ([
      { label: 'Envoyer sur Telegram', icon: <SendRoundedIcon />, variant: 'outlined' },
    ]),
    [],
  );
  const connectedUserLogin = getUserLoginFromAuth();

  const buildTelegramPayload = useCallback((chatIdOverride = telegramChatId) => ({
    login: getUserLoginFromAuth() || null,
    chatId: normalizeUiText(chatIdOverride) || null,
    symbol: currentSingleAnalysis.symbol,
    exchange: currentSingleAnalysis.exchange,
    timeframe: currentSingleAnalysis.timeframe,
    candles: Number(singleState.candles) || 450,
  }), [currentSingleAnalysis.exchange, currentSingleAnalysis.symbol, currentSingleAnalysis.timeframe, singleState.candles, telegramChatId]);

  const applyTelegramAvailability = useCallback((payload) => {
    const nextAvailability = typeof payload?.sendAvailable === 'boolean' ? payload.sendAvailable : true;
    const nextStatusMessage = normalizeUiText(payload?.sendStatusMessage);
    setTelegramSendAvailable(nextAvailability);
    setTelegramSendStatusMessage(nextStatusMessage);
  }, []);

  const persistTelegramChatId = useCallback(async (chatIdValue, { showMessage = true } = {}) => {
    const login = getUserLoginFromAuth();
    const storageKey = buildTelegramChatStorageKey(login);
    const normalizedChatId = normalizeUiText(chatIdValue);

    if (normalizedChatId) {
      localStorage.setItem(storageKey, normalizedChatId);
    } else {
      localStorage.removeItem(storageKey);
    }

    if (login) {
      const response = await fetch('/api/pattern/telegram/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, chatId: normalizedChatId || null }),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const payload = await response.json();
  applyTelegramAvailability(payload);
      const persistedChatId = normalizeUiText(payload?.chatId);
      setTelegramChatId(persistedChatId);

      if (persistedChatId) {
        localStorage.setItem(storageKey, persistedChatId);
      } else {
        localStorage.removeItem(storageKey);
      }

      if (showMessage) {
        setTelegramNotice(persistedChatId
          ? 'ChatId Telegram enregistré pour les prochains envois.'
          : 'ChatId Telegram supprimé des préférences enregistrées.');
      }

      return persistedChatId;
    }

    setTelegramChatId(normalizedChatId);

    if (showMessage) {
      setTelegramNotice(normalizedChatId
        ? 'ChatId Telegram enregistré localement sur cet appareil.'
        : 'ChatId Telegram local supprimé sur cet appareil.');
    }

    return normalizedChatId;
  }, [applyTelegramAvailability]);

  const loadTelegramChatId = useCallback(async () => {
    const login = getUserLoginFromAuth();
    const storageKey = buildTelegramChatStorageKey(login);
    const localChatId = normalizeUiText(localStorage.getItem(storageKey));
    let resolvedChatId = localChatId;

    setTelegramChatId(localChatId);

    if (login) {
      try {
        const response = await fetch(`/api/pattern/telegram/settings?login=${encodeURIComponent(login)}`);
        if (response.ok) {
          const payload = await response.json();
          applyTelegramAvailability(payload);
          const persistedChatId = normalizeUiText(payload?.chatId);
          resolvedChatId = persistedChatId || localChatId;
          setTelegramChatId(resolvedChatId);

          if (resolvedChatId) {
            localStorage.setItem(storageKey, resolvedChatId);
          } else {
            localStorage.removeItem(storageKey);
          }
        }
      } catch (error) {
        // Fall back to local storage when settings cannot be loaded.
      }
    }

    return resolvedChatId;
  }, [applyTelegramAvailability]);

  const loadTelegramPreview = useCallback(async (chatIdOverride = telegramChatId) => {
    setTelegramPreviewLoading(true);
    setTelegramError('');
    setTelegramPreviewImageFailures({});

    try {
      const response = await fetch('/api/pattern/telegram/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildTelegramPayload(chatIdOverride)),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const payload = await response.json();
      const normalizedPayload = normalizeTelegramPreviewPayload(payload);
      applyTelegramAvailability(normalizedPayload);
      setTelegramPreview(normalizedPayload);

      if (!normalizeUiText(chatIdOverride) && normalizeUiText(normalizedPayload?.resolvedChatId)) {
        setTelegramChatId(normalizeUiText(normalizedPayload.resolvedChatId));
      }
    } catch (error) {
      setTelegramPreview(null);
      setTelegramError(error instanceof Error ? error.message : 'Aperçu Telegram indisponible.');
    } finally {
      setTelegramPreviewLoading(false);
    }
  }, [applyTelegramAvailability, buildTelegramPayload, telegramChatId]);

  const openTelegramDialog = useCallback(async () => {
    setTelegramDialogOpen(true);
    setTelegramError('');
    setTelegramNotice('');
    setTelegramPreview(null);
    const resolvedChatId = await loadTelegramChatId();
    await loadTelegramPreview(resolvedChatId || '');
  }, [loadTelegramChatId, loadTelegramPreview]);

  const stopScannerProgressPolling = useCallback(() => {
    if (scannerProgressIntervalRef.current) {
      window.clearInterval(scannerProgressIntervalRef.current);
      scannerProgressIntervalRef.current = null;
    }
  }, []);

  const fetchScannerProgress = useCallback(async () => {
    try {
      const response = await fetch('/api/pattern/scanner-progress');
      if (!response.ok) {
        return;
      }
      const payload = await response.json();
      setScannerProgress({
        ...emptyScannerProgress,
        ...payload,
      });
    } catch (error) {
      // Ignore progress polling errors and keep the main scanner request authoritative.
    }
  }, []);

  const startScannerProgressPolling = useCallback(() => {
    stopScannerProgressPolling();
    setScannerProgress((current) => ({
      ...current,
      running: true,
      errorMessage: '',
    }));
    void fetchScannerProgress();
    scannerProgressIntervalRef.current = window.setInterval(() => {
      void fetchScannerProgress();
    }, 800);
  }, [fetchScannerProgress, stopScannerProgressPolling]);

  const loadPatternHistory = async (symbol, exchange, timeframe, fallbackAnalysis) => {
    setHistoryLoading(true);
    setHistoryError('');

    try {
      const query = new URLSearchParams();
      if (exchange) {
        query.set('exchange', exchange);
      }
      if (timeframe) {
        query.set('timeframe', timeframe);
      }
      query.set('limit', String(PATTERN_HISTORY_LIMIT));
      const response = await fetch(`/api/pattern/${encodeURIComponent(symbol)}?${query.toString()}`);
      if (!response.ok) {
        throw new Error('Historique indisponible');
      }
      const payload = await response.json();
      if (Array.isArray(payload) && payload.length > 0) {
        const normalizedHistory = payload.map(normalizePatternAnalysis);
        setPatternHistory(normalizedHistory);
        return normalizedHistory;
      } else {
        const fallbackHistory = buildFallbackHistory(fallbackAnalysis);
        setPatternHistory(fallbackHistory);
        return fallbackHistory;
      }
    } catch (error) {
      setHistoryError('Historique pattern indisponible. Une chronologie representative est affichee.');
      const fallbackHistory = buildFallbackHistory(fallbackAnalysis);
      setPatternHistory(fallbackHistory);
      return fallbackHistory;
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadScanner = async (options = {}) => {
    const { stateOverride, analysisOverride, filtersOverride, ...queryOptions } = options;
    const scannerState = stateOverride || singleState;
    const scannerFilters = filtersOverride || filters;
    setScannerLoading(true);
    setScannerError('');
    startScannerProgressPolling();
    try {
      const response = await fetch(`/api/pattern/scanner?${buildScannerQuery(scannerFilters, scannerState, queryOptions)}`);
      if (!response.ok) {
        throw new Error('Scanner indisponible');
      }
      const payload = await response.json();
      const nextRows = Array.isArray(payload) ? payload.map(normalizeScannerRow) : [];
      const reconciledRows = analysisOverride
        ? mergeCurrentAnalysisIntoScannerRows(nextRows, analysisOverride)
        : nextRows;
      setScannerHydrated(true);
      setScannerRows(reconciledRows);
      setExpandedRowKey(firstRowKey(reconciledRows[3] || reconciledRows[0]));
    } catch (error) {
      setScannerError('Le scanner pattern n’est pas disponible pour le moment.');
      setScannerRows(fallbackScanner);
      setExpandedRowKey(firstRowKey(fallbackScanner[3] || fallbackScanner[0]));
    } finally {
      await fetchScannerProgress();
      stopScannerProgressPolling();
      setScannerLoading(false);
    }
  };

  const analyzeSingleAsset = async (overrides = {}) => {
    const requestedSeedAnalysis = normalizePatternAnalysis(overrides.seedAnalysis || null);
    const normalizedState = {
      ...singleState,
      ...overrides,
      symbol: (overrides.symbol || singleState.symbol).toUpperCase(),
      exchange: (overrides.exchange || singleState.exchange).toUpperCase(),
      timeframe: (overrides.timeframe || singleState.timeframe).toUpperCase(),
      candles: Number(overrides.candles || singleState.candles) || 450,
      imageCandles: normalizeAnnotatedImageCandles(overrides.imageCandles ?? singleState.imageCandles),
    };
    const seedAnalysis = resolveTimeframeImageSeed(
      normalizedState.symbol,
      normalizedState.exchange,
      normalizedState.timeframe,
      requestedSeedAnalysis,
    );

    setSingleState(normalizedState);
    setSingleLoading(true);
    setSingleError('');
    focusSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    try {
      const payload = await fetchAdvancedPatternAnalysis({
        symbol: normalizedState.symbol,
        exchange: normalizedState.exchange,
        timeframe: normalizedState.timeframe,
        candles: normalizedState.candles,
        imageCandles: normalizedState.imageCandles,
      });
      const normalizedPayload = normalizePatternAnalysis(payload);
      rememberPatternImageSource(normalizedPayload);
      const payloadWithImageFallback = !normalizedPayload?.imagePath && seedAnalysis?.imagePath
        ? {
          ...normalizedPayload,
          imagePath: seedAnalysis.imagePath,
          imageGeneratedAt: normalizedPayload.imageGeneratedAt || seedAnalysis.imageGeneratedAt || null,
          imageVersion: normalizedPayload.imageVersion || seedAnalysis.imageVersion || '',
        }
        : normalizedPayload;

      setSingleAnalysis(payloadWithImageFallback);
      setScannerRows((currentRows) => mergeCurrentAnalysisIntoScannerRows(currentRows, payloadWithImageFallback));
      void loadPatternHistory(normalizedState.symbol, normalizedState.exchange, normalizedState.timeframe, payloadWithImageFallback)
        .then((history) => {
          if (payloadWithImageFallback?.imagePath || !Array.isArray(history)) {
            return;
          }

          const candidateWithImage = history.find((entry) => (
            sameTimeframe(entry?.timeframe, normalizedState.timeframe)
            && normalizeUiText(entry?.imagePath)
          ));
          if (candidateWithImage) {
            setSingleAnalysis((current) => ({
              ...normalizePatternAnalysis(current),
              imagePath: candidateWithImage.imagePath,
              imageGeneratedAt: candidateWithImage.imageGeneratedAt || null,
              imageVersion: candidateWithImage.imageVersion || '',
            }));
          }
        });
      void loadScanner({ stateOverride: normalizedState, analysisOverride: payloadWithImageFallback });
    } catch (error) {
      const fallbackAnalysis = {
        ...fallbackSingle,
        symbol: normalizedState.symbol,
        exchange: normalizedState.exchange,
        timeframe: normalizedState.timeframe,
        imagePath: seedAnalysis?.imagePath || '',
        imageGeneratedAt: seedAnalysis?.imageGeneratedAt || null,
        imageVersion: seedAnalysis?.imageVersion || '',
      };
      setSingleError('Analyse live indisponible. Un exemple representatif est affiche.');
      setSingleAnalysis(fallbackAnalysis);
      void loadPatternHistory(normalizedState.symbol, normalizedState.exchange, normalizedState.timeframe, fallbackAnalysis);
    } finally {
      setSingleLoading(false);
    }
  };

  const focusOnRow = (row) => {
    const selectedTimeframe = String(row?.timeframe || '4H').toUpperCase();
    setVisualMode(['1H', '4H', '1D'].includes(selectedTimeframe) ? selectedTimeframe : '4H');
    const seededAnalysis = resolveTimeframeImageSeed(
      row?.symbol,
      row?.exchange,
      selectedTimeframe,
      scannerRowToAnalysis(row),
    ) || scannerRowToAnalysis(row);
    setSingleAnalysis(seededAnalysis);
    void analyzeSingleAsset({
      symbol: row.symbol,
      exchange: row.exchange,
      timeframe: selectedTimeframe,
      candles: singleState.candles,
      seedAnalysis: seededAnalysis,
    });
  };

  const handleQuickAction = useCallback((actionLabel) => {
    if (actionLabel === 'Envoyer sur Telegram') {
      void openTelegramDialog();
      return;
    }

    setQuickActionFeedback(`${actionLabel} prêt pour ${currentSingleAnalysis.symbol}. Branche maintenant le workflow backend correspondant pour le rendre persistant.`);
  }, [currentSingleAnalysis.symbol, openTelegramDialog]);

  const handleTelegramSave = useCallback(async () => {
    const validationMessage = getTelegramChatIdValidationMessage(telegramChatId);
    if (validationMessage) {
      setTelegramError(validationMessage);
      setTelegramNotice('');
      return;
    }

    setTelegramSaving(true);
    setTelegramError('');

    try {
      const persistedChatId = await persistTelegramChatId(telegramChatId, { showMessage: true });
      await loadTelegramPreview(persistedChatId || '');
    } catch (error) {
      setTelegramError(decorateTelegramErrorMessage(error instanceof Error ? error.message : '', 'Impossible d’enregistrer le chatId Telegram.', telegramChatId));
    } finally {
      setTelegramSaving(false);
    }
  }, [loadTelegramPreview, persistTelegramChatId, telegramChatId]);

  const handleTelegramSend = useCallback(async () => {
    if (!telegramSendAvailable) {
      setTelegramError(telegramSendStatusMessage || 'Telegram n’est pas configuré côté serveur. Ajoutez le bot token et activez la fonctionnalité.');
      return;
    }

    const normalizedChatId = normalizeUiText(telegramChatId);
    if (!normalizedChatId) {
      setTelegramError('Saisissez un chatId Telegram avant l’envoi.');
      return;
    }

    const validationMessage = getTelegramChatIdValidationMessage(normalizedChatId);
    if (validationMessage) {
      setTelegramError(validationMessage);
      return;
    }

    setTelegramSending(true);
    setTelegramError('');
    setTelegramNotice('');

    try {
      const persistedChatId = await persistTelegramChatId(normalizedChatId, { showMessage: false });

      const response = await fetch('/api/pattern/telegram/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildTelegramPayload(persistedChatId || normalizedChatId)),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const payload = await response.json();
      setTelegramNotice(payload?.message || 'Setup envoyé sur Telegram.');
      await loadTelegramPreview(persistedChatId || normalizedChatId);
    } catch (error) {
      setTelegramError(decorateTelegramErrorMessage(error instanceof Error ? error.message : '', 'Impossible d’envoyer le setup sur Telegram.', normalizedChatId));
    } finally {
      setTelegramSending(false);
    }
  }, [buildTelegramPayload, loadTelegramPreview, persistTelegramChatId, telegramChatId, telegramSendAvailable, telegramSendStatusMessage]);

  const runReplayCalibration = useCallback(async () => {
    const symbol = currentSingleAnalysis.symbol?.trim()?.toUpperCase();
    const exchange = currentSingleAnalysis.exchange?.trim()?.toUpperCase() || 'BINANCE';
    const currentTimeframe = currentSingleAnalysis.timeframe?.trim()?.toUpperCase() || '4H';
    if (!symbol) {
      setCalibrationRequested(true);
      setCalibrationResults([]);
      setCalibrationInsights(null);
      setCalibrationError('Aucun actif sélectionné pour lancer la calibration.');
      return;
    }

    const calibrationCandles = Math.max(80, Math.min(160, Number(singleState.candles) || 80));

    setCalibrationRequested(true);
    setCalibrationLoading(true);
    setCalibrationError('');

    try {
      const query = new URLSearchParams({
        symbol,
        exchange,
        currentTimeframe,
        candles: String(calibrationCandles),
        offset: '0',
      });
      const response = await fetch(`/api/admin/pattern/replay/context?${query.toString()}`);
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || 'Contexte multi-timeframe indisponible.');
      }

      const nextResults = Array.isArray(payload?.timeframes) ? payload.timeframes : [];
      const nextWarnings = Array.isArray(payload?.warnings) ? payload.warnings.filter(Boolean) : [];
      const advancedReplayAnalyses = await Promise.allSettled(
        nextResults.map((result) => {
          const replayCandles = Array.isArray(result?.candles) ? result.candles.filter(Boolean) : [];
          const replayAnalysis = result?.analysis && typeof result.analysis === 'object' ? result.analysis : null;
          const replayTimeframe = firstNonEmptyText(replayAnalysis?.timeframe);

          if (!replayCandles.length || !replayTimeframe) {
            return Promise.resolve(replayAnalysis);
          }

          return fetchAdvancedPatternAnalysis({
            symbol,
            exchange,
            timeframe: replayTimeframe,
            candles: replayCandles.length,
            imageCandles: Math.min(120, replayCandles.length),
            candlePayload: replayCandles,
          });
        }),
      );
      const enrichedResults = nextResults.map((result, index) => {
        const advancedReplayAnalysis = advancedReplayAnalyses[index];
        if (advancedReplayAnalysis?.status === 'fulfilled' && advancedReplayAnalysis.value) {
          return {
            ...result,
            analysis: advancedReplayAnalysis.value,
          };
        }
        return result;
      });
      const replayAdvancedWarnings = advancedReplayAnalyses.flatMap((entry, index) => {
        if (entry?.status !== 'rejected') {
          return [];
        }
        const replayTimeframe = firstNonEmptyText(nextResults[index]?.analysis?.timeframe, CALIBRATION_TIMEFRAMES[index]) || 'n/a';
        const reason = entry.reason instanceof Error ? entry.reason.message : 'Analyse avancée replay indisponible.';
        return [`${replayTimeframe}: ${reason}`];
      });

      setCalibrationResults(enrichedResults);
      setCalibrationInsights(payload?.insights || null);
      setCalibrationError([...nextWarnings, ...replayAdvancedWarnings].join(' | '));
    } catch (error) {
      setCalibrationResults([]);
      setCalibrationInsights(null);
      setCalibrationError(error instanceof Error ? error.message : 'Contexte multi-timeframe indisponible.');
    } finally {
      setCalibrationLoading(false);
    }
  }, [currentSingleAnalysis.exchange, currentSingleAnalysis.symbol, currentSingleAnalysis.timeframe, singleState.candles]);

  useEffect(() => {
    if (visualMode !== 'multi') {
      return;
    }

    if (calibrationLoading || calibrationRequested || calibrationResults.length === CALIBRATION_TIMEFRAMES.length) {
      return;
    }

    void runReplayCalibration();
  }, [calibrationLoading, calibrationRequested, calibrationResults.length, runReplayCalibration, visualMode]);

  useEffect(() => {
    scannerRows.forEach((row) => {
      rememberPatternImageSource(row);
    });
  }, [rememberPatternImageSource, scannerRows]);

  useEffect(() => {
    patternHistory.forEach((entry) => {
      rememberPatternImageSource(entry);
    });
  }, [patternHistory, rememberPatternImageSource]);

  useEffect(() => {
    const bootstrapPage = async () => {
      if (bootstrapGuardRef.current) {
        return;
      }
      bootstrapGuardRef.current = true;

      setSingleLoading(true);
      setScannerLoading(true);
      setHistoryLoading(true);
      setSingleError('');
      setScannerError('');
      setHistoryError('');
      let seededAnalysis = fallbackSingle;

      try {
        const scannerQuery = buildScannerQuery(defaultFilters, defaultSingleState);
        startScannerProgressPolling();
        try {
          const scannerResponse = await fetch(`/api/pattern/scanner?${scannerQuery}`);
          if (!scannerResponse.ok) {
            throw new Error('Scanner indisponible');
          }

          const payload = await scannerResponse.json();
          const nextRows = Array.isArray(payload) ? payload.map(normalizeScannerRow) : [];
          setScannerHydrated(true);
          setScannerRows(nextRows);
          setExpandedRowKey(firstRowKey(nextRows[0]));

          const scannerSeed = nextRows.find((row) => Boolean(row?.imagePath)) || nextRows[0] || null;
          if (scannerSeed) {
            seededAnalysis = scannerRowToAnalysis(scannerSeed);
            setSingleAnalysis(seededAnalysis);
          } else {
            setSingleAnalysis(fallbackSingle);
          }
        } catch (error) {
          setScannerError('Le scanner pattern n’est pas disponible pour le moment.');
          setScannerRows(fallbackScanner);
          setExpandedRowKey(firstRowKey(fallbackScanner[0]));
          setSingleAnalysis(fallbackSingle);
        } finally {
          await fetchScannerProgress();
          stopScannerProgressPolling();
          setScannerLoading(false);
        }

        const historyQuery = new URLSearchParams({
          exchange: defaultSingleState.exchange,
          timeframe: defaultSingleState.timeframe,
          limit: String(PATTERN_HISTORY_LIMIT),
        });
        let bootstrapHistory = buildFallbackHistory(seededAnalysis);
        try {
          const historyResponse = await fetch(`/api/pattern/${encodeURIComponent(defaultSingleState.symbol)}?${historyQuery.toString()}`);
          if (!historyResponse.ok) {
            throw new Error('Historique indisponible');
          }

          const payload = await historyResponse.json();
          bootstrapHistory = Array.isArray(payload) && payload.length > 0
            ? payload.map(normalizePatternAnalysis)
            : buildFallbackHistory(seededAnalysis);
          setPatternHistory(bootstrapHistory);
        } catch (error) {
          setHistoryError('Historique pattern indisponible. Une chronologie representative est affichee.');
          bootstrapHistory = buildFallbackHistory(seededAnalysis);
          setPatternHistory(bootstrapHistory);
        } finally {
          setHistoryLoading(false);
        }

        try {
          const singlePayload = await fetchAdvancedPatternAnalysis({
            symbol: defaultSingleState.symbol,
            exchange: defaultSingleState.exchange,
            timeframe: defaultSingleState.timeframe,
            candles: defaultSingleState.candles,
            imageCandles: defaultSingleState.imageCandles,
          });

          const normalizedSingle = normalizePatternAnalysis(singlePayload);
          rememberPatternImageSource(normalizedSingle);
          const historyImageCandidate = Array.isArray(bootstrapHistory)
            ? bootstrapHistory.find((entry) => (
              sameTimeframe(entry?.timeframe, defaultSingleState.timeframe)
              && normalizeUiText(entry?.imagePath)
            ))
            : null;
          const liveAnalysis = !normalizedSingle?.imagePath && historyImageCandidate?.imagePath
            ? {
              ...normalizedSingle,
              imagePath: historyImageCandidate.imagePath,
              imageGeneratedAt: normalizedSingle.imageGeneratedAt || historyImageCandidate.imageGeneratedAt || null,
              imageVersion: normalizedSingle.imageVersion || historyImageCandidate.imageVersion || '',
            }
            : normalizedSingle;

          setSingleAnalysis(liveAnalysis);
          setScannerRows((currentRows) => mergeCurrentAnalysisIntoScannerRows(currentRows, liveAnalysis));
        } catch (error) {
          setSingleError('Analyse live indisponible. Un exemple representatif est affiche.');
          setSingleAnalysis(seededAnalysis);
        } finally {
          setSingleLoading(false);
        }
      } catch (error) {
        setSingleError('Analyse live indisponible. Un exemple representatif est affiche.');
        setScannerError('Le scanner pattern n’est pas disponible pour le moment.');
        setHistoryError('Historique pattern indisponible. Une chronologie representative est affichee.');
        setSingleAnalysis(fallbackSingle);
        setScannerRows(fallbackScanner);
        setPatternHistory(buildFallbackHistory(fallbackSingle));
        setExpandedRowKey(firstRowKey(fallbackScanner[0]));
        setSingleLoading(false);
        setScannerLoading(false);
        setHistoryLoading(false);
      }
    };

    void bootstrapPage();

    return () => {
      stopScannerProgressPolling();
    };
  }, [fetchScannerProgress, rememberPatternImageSource, startScannerProgressPolling, stopScannerProgressPolling]);

  useEffect(() => {
    let cancelled = false;
    let imageBlobUrl = '';
    let retryTimer = null;

    setImageLoadFailed(false);
    setPatternImageBlobUrl('');

    const loadImageBlob = async () => {
      const blob = await fetchPatternImageAsBlob(currentSingleAnalysis);
      if (blob && !cancelled) {
        imageBlobUrl = URL.createObjectURL(blob);
        setPatternImageBlobUrl(imageBlobUrl);
      } else if (!cancelled && currentSingleAnalysis.symbol) {
        retryTimer = window.setTimeout(() => void pollGeneratedImage(), 2000);
      }
    };

        const pollGeneratedImage = async (attempt = 0) => {
      if (cancelled || attempt >= 15) {
        return;
      }

      const controller = new AbortController();
      const abortTimer = window.setTimeout(() => controller.abort(), 5000);

      try {
        const blob = await fetchPatternImageAsBlob(currentSingleAnalysis, {
          forceSymbolLookup: true,
          signal: controller.signal,
        });
        if (blob && !cancelled) {
          imageBlobUrl = URL.createObjectURL(blob);
          setPatternImageBlobUrl(imageBlobUrl);
          return;
        }
      } catch (error) {
        // Timeout or network error — the visual remains optional while asynchronous generation is pending.
      } finally {
        window.clearTimeout(abortTimer);
      }

      retryTimer = window.setTimeout(() => void pollGeneratedImage(attempt + 1), 2000);
    };

    if (currentSingleAnalysis.imagePath) {
      void loadImageBlob();
    } else if (currentSingleAnalysis.symbol) {
      retryTimer = window.setTimeout(() => void pollGeneratedImage(), 2000);
    }

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
    };
  }, [currentSingleAnalysis]);

  useEffect(() => {
    setVisualMode((currentMode) => (['multi', '1H', '4H', '1D'].includes(currentMode) ? currentMode : '4H'));
  }, [currentSingleAnalysis.symbol, currentSingleAnalysis.timeframe, currentSingleAnalysis.patternDetected]);

  useEffect(() => {
    setCalibrationRequested(false);
    setCalibrationLoading(false);
    setCalibrationError('');
    setCalibrationResults([]);
    setCalibrationInsights(null);
  }, [currentSingleAnalysis.symbol, currentSingleAnalysis.exchange, currentSingleAnalysis.timeframe]);

  useEffect(() => {
    setActiveInsightTab('summary');
    setQuickActionFeedback('');
    setTelegramPreview(null);
    setTelegramError('');
    setTelegramNotice('');
    setTelegramPreviewImageFailures({});
  }, [currentSingleAnalysis.symbol, currentSingleAnalysis.timeframe, currentSingleAnalysis.patternDetected]);

  useEffect(() => {
    try {
      localStorage.setItem(PATTERN_SCANNER_COMPACT_PRO_STORAGE_KEY, compactProMode ? '1' : '0');
    } catch {
      // Ignore storage errors to avoid blocking the UI.
    }
  }, [compactProMode]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: 'radial-gradient(circle at top left, rgba(228,197,114,0.18) 0%, rgba(228,197,114,0) 28%), linear-gradient(180deg, #f8f4ec 0%, #f2ede3 52%, #ebe5db 100%)',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 6,
            p: { xs: 3, md: 5 },
            mb: 4,
            background: 'linear-gradient(135deg, #10252b 0%, #214550 38%, #43736f 100%)',
            color: '#f8f4ec',
          }}
        >
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} justifyContent="space-between" alignItems="stretch">
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                label="Scanner de figures"
                sx={{ mb: 2, bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', letterSpacing: '0.08em', fontWeight: 700 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 720 }}>
                Intelligence chartiste pour détecter les meilleurs setups crypto
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(248,244,236,0.82)', maxWidth: 660, lineHeight: 1.75 }}>
                Cette vue priorise le graphe, la direction, les niveaux de breakout, la cible, le stop et une lecture IA exploitable. Le scanner global reste pilotable, mais la page met d’abord le focus sur la meilleure decision possible pour un actif donne.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setLearningOpen(true)}
                  sx={{
                    minHeight: 56,
                    px: 3,
                    borderRadius: 99,
                    borderColor: 'rgba(248,244,236,0.45)',
                    color: '#f8f4ec',
                    ':hover': { borderColor: '#f8f4ec', background: 'rgba(248,244,236,0.08)' },
                  }}
                >
                  Apprendre les figures chartistes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setGlossaryOpen(true)}
                  sx={{
                    minHeight: 56,
                    px: 3,
                    borderRadius: 99,
                    borderColor: 'rgba(248,244,236,0.45)',
                    color: '#f8f4ec',
                    ':hover': { borderColor: '#f8f4ec', background: 'rgba(248,244,236,0.08)' },
                  }}
                >
                  Lexique du scanner
                </Button>
                {topSetup && (
                  <Button
                    variant="text"
                    onClick={() => focusOnRow(topSetup)}
                    sx={{ color: '#f8f4ec', fontWeight: 700, alignSelf: 'stretch' }}
                  >
                    Ouvrir le cockpit prioritaire
                  </Button>
                )}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {topSetup ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 5,
                    background: 'rgba(248,244,236,0.08)',
                    border: '1px solid rgba(248,244,236,0.14)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" sx={{ color: 'rgba(248,244,236,0.72)', letterSpacing: '0.12em' }}>
                        Priorité radar du moment
                      </Typography>
                      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 0.8, mb: 1.2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                          {topSetup.symbol}
                        </Typography>
                        <Chip
                          label={directionHeadline(deriveRowDirection(topSetup))}
                          sx={{
                            bgcolor: directionSoftBackground(deriveRowDirection(topSetup)),
                            color: '#f8f4ec',
                            border: `1px solid ${directionColor(deriveRowDirection(topSetup))}`,
                            fontWeight: 700,
                          }}
                        />
                      </Stack>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
                        {getPatternTitle(topSetup.detectedPattern)}
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.35 }}>
                        <Chip label={`Objectif ${formatPercentMove(topSetup.targetPrice, topSetup.breakoutLevel)}`} sx={{ bgcolor: 'rgba(248,244,236,0.14)', color: '#f8f4ec', fontWeight: 800 }} />
                        <Chip label={`Breakout ${formatPriceLevel(topSetup.breakoutLevel)}`} sx={{ bgcolor: 'rgba(248,244,236,0.1)', color: '#f8f4ec', fontWeight: 700 }} />
                        {topSetupHasAdvancedSignals ? (
                          <Chip label="Signaux avancés actifs" sx={{ bgcolor: 'rgba(143,214,177,0.18)', color: '#e8fbf1', fontWeight: 800, border: '1px solid rgba(143,214,177,0.28)' }} />
                        ) : null}
                        {topSetup.signalFusionState ? (
                          <Chip
                            label={formatSignalFusionState(topSetup.signalFusionState)}
                            sx={{ bgcolor: 'rgba(248,244,236,0.14)', color: '#f8f4ec', fontWeight: 800 }}
                          />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.8)', lineHeight: 1.7, maxWidth: 460 }}>
                        {topSetup.aiSummary}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap alignSelf="flex-start">
                      <MetricTile label="Confiance" value={`${formatConfidence(topSetup.confidence)}%`} tone="#f3c65c" />
                      <MetricTile label="Score global" value={topSetup.scoreGlobal} tone="#8fd6b1" />
                      <MetricTile label="Conviction" value={isFiniteNumber(topSetup.convictionScore) ? `${Math.round(topSetup.convictionScore)}/100` : 'n/a'} tone="#f8f4ec" />
                      <MetricTile label="Volume" value={topSetupVolumeStrength.label} tone={topSetupVolumeStrength.tone} />
                      <MetricTile label="R/R" value={formatRatio(topSetup.riskRewardRatio)} tone="#f8f4ec" />
                    </Stack>
                  </Stack>
                </Paper>
              ) : null}
            </Box>
          </Stack>
        </Paper>

        <Paper ref={focusSectionRef} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 5, background: '#fff8ef', mb: 4 }}>
          <Stack spacing={3.2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#17323c', mb: 0.75 }}>
                Cockpit de décision par actif
              </Typography>
              <Typography variant="body1" sx={{ color: '#4d6068', maxWidth: 900, lineHeight: 1.8 }}>
                Le visuel prend la place principale. La colonne latérale concentre uniquement ce qui aide à décider vite: verdict, lecture IA, historique court et analyse avancée sur demande.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: '1px solid rgba(16,37,43,0.08)',
                background: '#fffdf8',
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
                <TextField
                  label="Crypto"
                  value={singleState.symbol}
                  onChange={(event) => setSingleState((current) => ({ ...current, symbol: event.target.value.toUpperCase() }))}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="pattern-exchange-label">Plateforme</InputLabel>
                  <Select
                    labelId="pattern-exchange-label"
                    value={singleState.exchange}
                    label="Plateforme"
                    onChange={(event) => setSingleState((current) => ({ ...current, exchange: event.target.value }))}
                  >
                    <MenuItem value="BINANCE">Binance</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="pattern-timeframe-label">Unité de temps</InputLabel>
                  <Select
                    labelId="pattern-timeframe-label"
                    value={singleState.timeframe}
                    label="Unité de temps"
                    onChange={(event) => setSingleState((current) => ({ ...current, timeframe: event.target.value }))}
                  >
                    <MenuItem value="1H">1H</MenuItem>
                    <MenuItem value="4H">4H</MenuItem>
                    <MenuItem value="1D">1D</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  type="number"
                  label="Bougies"
                  value={singleState.candles}
                  onChange={(event) => setSingleState((current) => ({ ...current, candles: Number(event.target.value) || 450 }))}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="pattern-image-candles-label">Fenêtre image</InputLabel>
                  <Select
                    labelId="pattern-image-candles-label"
                    value={singleState.imageCandles}
                    label="Fenêtre image"
                    onChange={(event) => setSingleState((current) => ({
                      ...current,
                      imageCandles: normalizeAnnotatedImageCandles(event.target.value),
                    }))}
                  >
                    {ANNOTATED_IMAGE_CANDLE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>{`${option} bougies`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={() => analyzeSingleAsset()}
                  disabled={singleLoading}
                  startIcon={<ShowChartRoundedIcon />}
                  sx={{
                    minHeight: 56,
                    minWidth: { lg: 220 },
                    borderRadius: 99,
                    px: 3.2,
                    bgcolor: '#17323c',
                    ':hover': { bgcolor: '#0f2329' },
                  }}
                >
                  Ouvrir le cockpit
                </Button>
              </Stack>
            </Paper>

            {singleError && <Alert severity="warning">{singleError}</Alert>}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 3fr) minmax(340px, 1fr)' },
                gap: 3,
                alignItems: 'stretch',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 5,
                  background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
                  border: '1px solid rgba(16,37,43,0.08)',
                  minHeight: '100%',
                  overflow: 'hidden',
                }}
              >
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.2} justifyContent="space-between" alignItems={{ xs: 'stretch', xl: 'flex-start' }}>
                    <Box sx={{ maxWidth: 820, minWidth: 0, flex: 1 }}>
                      <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.14em', fontWeight: 800 }}>
                        Figure détectée
                      </Typography>
                      <Typography
                        variant="h2"
                        sx={{
                          mt: 0.6,
                          mb: 0.9,
                          fontWeight: 900,
                          color: '#17323c',
                          textTransform: 'uppercase',
                          letterSpacing: '-0.04em',
                          lineHeight: 1.02,
                          fontSize: { xs: '2.1rem', md: '3rem', xl: '3.55rem' },
                          overflowWrap: 'break-word',
                          wordBreak: 'normal',
                        }}
                      >
                        {getPatternTitle(currentSingleAnalysis.patternDetected)}
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Chip label={`${currentSingleAnalysis.symbol} · ${currentSingleAnalysis.exchange}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                        <Chip label={`Fenêtre ${currentSingleAnalysis.timeframe}`} sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 800 }} />
                        <Chip label={`Analyse ${formatDateTime(currentSingleAnalysis.analysisTimestamp)}`} sx={{ bgcolor: '#f8ede6', color: '#17323c', fontWeight: 700 }} />
                      </Stack>
                    </Box>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.2,
                        borderRadius: 4,
                        minWidth: { xl: 250 },
                        background: directionSoftBackground(currentDirection),
                        border: `1px solid ${directionColor(currentDirection)}22`,
                      }}
                    >
                      <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
                        Direction dominante
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 0.6, mb: 0.7, fontWeight: 900, color: directionColor(currentDirection) }}>
                        {directionHeadline(currentDirection)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#415760', lineHeight: 1.7 }}>
                        Fenêtre de résolution: {currentSingleAnalysis.timeToResolution || 'n/a'}.
                      </Typography>
                    </Paper>
                  </Stack>

                  {hybridVisualAnalysis ? (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 3,
                        alignItems: 'center',
                        background: 'rgba(27, 79, 114, 0.08)',
                        border: '1px solid rgba(27, 79, 114, 0.18)',
                      }}
                    >
                      Les niveaux, le score et le pattern affichés proviennent du fallback Java. Le visuel annoté reste fourni par le moteur Python.
                    </Alert>
                  ) : null}

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.6,
                      borderRadius: 4,
                      border: '1px solid rgba(16,37,43,0.08)',
                      background: hasAnnotatedVisual
                        ? 'linear-gradient(180deg, rgba(245,239,228,0.72) 0%, rgba(255,255,255,0.96) 100%)'
                        : 'linear-gradient(180deg, rgba(236,233,227,0.92) 0%, rgba(245,243,239,0.98) 100%)',
                      opacity: 1,
                      transition: 'background 180ms ease',
                    }}
                  >
                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
                      <Box>
                        <Typography variant="overline" sx={{ color: '#6b7a80', letterSpacing: '0.12em', fontWeight: 800 }}>
                          Mode de visualisation
                        </Typography>
                        {!hasAnnotatedVisual ? (
                          <Typography variant="caption" sx={{ display: 'block', color: '#6b7a80', fontWeight: 700 }}>
                            Indisponible tant qu’aucune image annotée exploitable n’est remontée.
                          </Typography>
                        ) : null}
                      </Box>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1}>
                        {['1H', '4H', '1D'].map((timeframe) => (
                          <Button
                            key={`annotated-${timeframe}`}
                            variant={visualMode === timeframe ? 'contained' : 'outlined'}
                            onClick={() => {
                              setVisualMode(timeframe);
                              void analyzeSingleAsset({
                                symbol: currentSingleAnalysis.symbol,
                                exchange: currentSingleAnalysis.exchange,
                                timeframe,
                                candles: singleState.candles,
                                seedAnalysis: currentSingleAnalysis,
                              });
                            }}
                            sx={{
                              minWidth: { sm: 92 },
                              fontWeight: 800,
                              borderRadius: 999,
                              bgcolor: visualMode === timeframe ? '#17323c' : 'transparent',
                              color: visualMode === timeframe ? '#f8f4ec' : '#17323c',
                              borderColor: 'rgba(23,50,60,0.2)',
                              ':hover': {
                                bgcolor: visualMode === timeframe ? '#0f2329' : 'rgba(23,50,60,0.04)',
                                borderColor: '#17323c',
                              },
                            }}
                          >
                            {timeframe}
                          </Button>
                        ))}
                        <Button
                          variant={visualMode === 'multi' ? 'contained' : 'outlined'}
                          startIcon={<ShowChartRoundedIcon />}
                          onClick={() => setVisualMode('multi')}
                          sx={{
                            minWidth: { sm: 220 },
                            fontWeight: 800,
                            borderRadius: 999,
                            bgcolor: visualMode === 'multi' ? '#17323c' : 'transparent',
                            color: visualMode === 'multi' ? '#f8f4ec' : '#17323c',
                            borderColor: 'rgba(23,50,60,0.2)',
                            ':hover': {
                              bgcolor: visualMode === 'multi' ? '#0f2329' : 'rgba(23,50,60,0.04)',
                              borderColor: '#17323c',
                            },
                            ':disabled': {
                              bgcolor: 'rgba(255,255,255,0.48)',
                              color: 'rgba(23,50,60,0.34)',
                              borderColor: 'rgba(23,50,60,0.08)',
                            },
                          }}
                        >
                          Vue 1H / 4H / 1D
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>

                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      background: 'linear-gradient(180deg, #f5efe4 0%, #efe5d4 100%)',
                      border: '1px solid rgba(16,37,43,0.08)',
                      minHeight: { xs: 400, md: 620, xl: 720 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 1.5, md: 2 },
                    }}
                  >
                    {visualMode === 'multi' ? (
                      <Stack spacing={1.6} sx={{ width: '100%' }}>
                        {calibrationError && calibrationResults.length === 0 ? (
                          <Alert severity="warning" sx={{ borderRadius: 3 }}>
                            {calibrationError}
                          </Alert>
                        ) : null}

                        {calibrationLoading && calibrationResults.length === 0 ? (
                          <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 760, mx: 'auto' }}>
                            <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800, textAlign: 'center' }}>
                              Replays 1H / 4H / 1D en cours
                            </Typography>
                            <LinearProgress sx={{ height: 10, borderRadius: 99 }} />
                          </Stack>
                        ) : (
                          <Stack spacing={1.5} sx={{ width: '100%' }}>
                            {calibrationInsights ? (
                              <MultiTimeframeInsightsSummary
                                insights={calibrationInsights}
                                currentTimeframe={currentSingleAnalysis.timeframe}
                              />
                            ) : null}

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                                gap: 1.5,
                                width: '100%',
                              }}
                            >
                              {CALIBRATION_TIMEFRAMES.map((timeframe) => (
                                <MultiTimeframeVisualCard
                                  key={`${currentSingleAnalysis.symbol || 'asset'}-${timeframe}`}
                                  timeframe={timeframe}
                                  result={calibrationResultsByTimeframe.get(timeframe)}
                                  highlight={timeframe === currentSingleAnalysis.timeframe}
                                  loading={calibrationLoading && !calibrationResultsByTimeframe.get(timeframe)}
                                />
                              ))}
                            </Box>
                          </Stack>
                        )}
                      </Stack>
                    ) : patternImageBlobUrl ? (
                      <Box
                        component="img"
                        src={patternImageBlobUrl}
                        alt={`Pattern ${currentSingleAnalysis.patternDetected} ${currentSingleAnalysis.symbol}`}
                        onError={() => setImageLoadFailed(true)}
                        sx={{
                          width: '100%',
                          height: '100%',
                          maxHeight: { xs: 430, md: 720, xl: 820 },
                          borderRadius: 3,
                          objectFit: 'contain',
                          background: '#f5efe4',
                        }}
                      />
                    ) : (
                      <Stack spacing={1.2} alignItems="center" sx={{ maxWidth: 520, textAlign: 'center', px: 2 }}>
                        <ShowChartRoundedIcon sx={{ fontSize: 48, color: '#6a7a80' }} />
                        <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 700 }}>
                          Aucune image annotée disponible pour cette analyse.
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#56686f', lineHeight: 1.8 }}>
                          Le moteur local continue d’exposer les niveaux et le score. Le graphe annoté apparaît automatiquement dès qu’une image Python valide a été produite.
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    <SignalPillar
                      label="Niveau de cassure"
                      value={formatPriceLevel(currentSingleAnalysis.breakoutLevel)}
                      helper="Zone à franchir ou défendre pour activer réellement le setup."
                      tone="#214f77"
                      icon={currentDirection === 'BEARISH' ? <SouthEastRoundedIcon fontSize="small" /> : <NorthEastRoundedIcon fontSize="small" />}
                    />
                    <SignalPillar
                      label="Prix cible"
                      value={formatPriceLevel(currentSingleAnalysis.targetPrice)}
                      helper="Projection théorique si la structure se déroule sans invalidation."
                      tone="#1f7a4d"
                      icon={<NorthEastRoundedIcon fontSize="small" />}
                    />
                    <SignalPillar
                      label="Stop d’invalidation"
                      value={formatPriceLevel(currentSingleAnalysis.stopInvalidation)}
                      helper="Niveau qui casse le scénario et impose de sortir de la lecture."
                      tone="#aa3d33"
                      icon={<SouthEastRoundedIcon fontSize="small" />}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                      gap: 1.2,
                    }}
                  >
                    <StatBadge label="Confiance pattern" value={`${formatConfidence(currentSingleAnalysis.confidence)}%`} accent="#173f5f" />
                    <StatBadge
                      label="Conviction"
                      value={formatConvictionValue(currentSingleAnalysis)}
                      accent={currentSignalFusionPalette.tone}
                    />
                    <StatBadge
                      label="Grade"
                      value={currentConvictionGrade.label}
                      accent={currentConvictionGrade.tone}
                      tooltip={renderConvictionGradeTooltip(currentConvictionGrade, () => setGlossaryOpen(true))}
                    />
                    <StatBadge label="Biais" value={currentConvictionGrade.bias} accent="#17323c" />
                    <StatBadge label="Source calcul" value={analysisSourceLabel} accent="#17323c" />
                    <StatBadge label="Source visuel" value={visualSourceLabel} accent="#17323c" />
                    <StatBadge
                      label="R/R"
                      value={formatRatio(currentSingleAnalysis.riskRewardRatio)}
                      accent="#264653"
                      tooltip={formatRiskRewardTooltip(currentSingleAnalysis.riskRewardRatio)}
                    />
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 5,
                  background: '#fff',
                  border: '1px solid rgba(16,37,43,0.08)',
                  overflow: 'hidden',
                  position: { xl: 'sticky' },
                  top: { xl: 32 },
                }}
              >
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.4}>
                    <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
                      Actions rapides
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }}>
                      Passer de la lecture à l’action
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.7 }}>
                      Prépare les prochaines actions sans quitter le cockpit. Les workflows persistants peuvent être branchés ensuite sur les mêmes CTA.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row', xl: 'column' }} spacing={1.1} flexWrap="wrap" useFlexGap>
                      {quickActions.map((action) => (
                        <Button
                          key={action.label}
                          variant={action.variant}
                          startIcon={action.icon}
                          onClick={() => handleQuickAction(action.label)}
                          sx={{
                            justifyContent: 'flex-start',
                            borderRadius: 99,
                            px: 2,
                            py: 1.15,
                            fontWeight: 700,
                            ...(action.variant === 'contained'
                              ? {
                                bgcolor: '#f3c65c',
                                color: '#10252b',
                                ':hover': { bgcolor: '#eab642' },
                              }
                              : {
                                borderColor: 'rgba(16,37,43,0.12)',
                                color: '#17323c',
                                background: '#fffdf8',
                                ':hover': { borderColor: '#17323c', background: '#fbf6eb' },
                              }),
                          }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                    {quickActionFeedback ? (
                      <Alert severity="info" sx={{ borderRadius: 3 }}>
                        {quickActionFeedback}
                      </Alert>
                    ) : null}
                  </Stack>
                </Box>

                <Divider />

                <Tabs
                  value={activeInsightTab}
                  onChange={(_, value) => setActiveInsightTab(value)}
                  variant="scrollable"
                  scrollButtons={false}
                  sx={{
                    px: 1.5,
                    '& .MuiTabs-indicator': {
                      bgcolor: '#17323c',
                      height: 3,
                      borderRadius: 99,
                    },
                  }}
                >
                  <Tab value="summary" label="Résumé IA" sx={{ textTransform: 'none', fontWeight: 700 }} />
                  <Tab value="confidence" label="Confiance" sx={{ textTransform: 'none', fontWeight: 700 }} />
                  <Tab value="history" label="Historique" sx={{ textTransform: 'none', fontWeight: 700 }} />
                  <Tab value="advanced" label="Analyse avancée" sx={{ textTransform: 'none', fontWeight: 700 }} />
                </Tabs>

                <Divider />

                <Box sx={{ p: 2.5 }}>
                  {activeInsightTab === 'summary' ? (
                    <Stack spacing={2}>
                      <ConvictionEnginePanel
                        analysis={currentSingleAnalysis}
                        direction={currentDirection}
                        verdict={setupVerdict}
                        grade={currentConvictionGrade}
                        palette={currentSignalFusionPalette}
                        multiTimeframeDetail={currentSingleAnalysis.multiTimeframeDetail}
                        onOpenLexicon={() => setGlossaryOpen(true)}
                      />

                      <SignalConvergencePanel analysis={currentSingleAnalysis} palette={currentSignalFusionPalette} compact />

                      {currentSingleAnalysis.multiTimeframeDetail ? (
                        <MultiTimeframeInsightsSummary
                          insights={currentSingleAnalysis.multiTimeframeDetail}
                          currentTimeframe={currentSingleAnalysis.timeframe}
                          compact
                        />
                      ) : null}

                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                          <AutoAwesomeRoundedIcon sx={{ color: '#17323c' }} />
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                            Lecture structurelle
                          </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ color: '#30444c', lineHeight: 1.85 }}>
                          {currentSingleAnalysis.aiSummary}
                        </Typography>
                      </Box>

                      <Paper elevation={0} sx={{ p: 1.7, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(16,37,43,0.08)' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.1 }}>
                          <DetailLine label="Invalidation" value={formatPriceLevel(currentSingleAnalysis.stopInvalidation)} />
                          <DetailLine label="Direction" value={directionHeadline(currentDirection)} />
                          <DetailLine label="Confiance cible" value={typeof currentSingleAnalysis.targetConfidence === 'number' ? `${formatConfidence(currentSingleAnalysis.targetConfidence)}%` : 'n/a'} />
                          <DetailLine
                            label="Fiabilité pattern"
                            value={currentDiagnostics?.reliabilityTier
                              ? (currentDiagnostics.reliabilityLabel
                                ? `${currentDiagnostics.reliabilityTier} · ${currentDiagnostics.reliabilityLabel}`
                                : currentDiagnostics.reliabilityTier)
                              : 'n/a'}
                          />
                          <DetailLine label="Confirmation volume" value={currentSingleAnalysis.volumeConfirmation ? 'Oui' : 'Non'} />
                          <DetailLine
                            label="Volume strength"
                            value={currentVolumeStrength.detail !== 'n/a'
                              ? `${currentVolumeStrength.label} · ${currentVolumeStrength.detail}`
                              : currentVolumeStrength.label}
                          />
                          <DetailLine label="Etat de fusion" value={currentSingleAnalysis.signalFusionState ? formatSignalFusionState(currentSingleAnalysis.signalFusionState) : 'n/a'} />
                          <DetailLine label="Lecture volume" value={currentSingleAnalysis.volumeComment || 'n/a'} />
                        </Box>
                      </Paper>
                    </Stack>
                  ) : null}

                  {activeInsightTab === 'confidence' ? (
                    <Stack spacing={1.8}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.8,
                          borderRadius: 4,
                          background: setupVerdict.background,
                          border: `1px solid ${setupVerdict.tone}22`,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center" sx={{ mb: 1.1 }}>
                          <Box>
                            <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
                              Verdict global
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }}>
                              Force du signal: {setupVerdict.signalStrength}
                            </Typography>
                          </Box>
                          <Chip
                            label={`Qualité ${setupVerdict.setupQuality}`}
                            sx={{
                              bgcolor: '#fff8ef',
                              color: setupVerdict.tone,
                              fontWeight: 900,
                              border: `1px solid ${setupVerdict.tone}22`,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" sx={{ color: '#30444c', lineHeight: 1.75, mb: 0.8 }}>
                          {setupVerdict.description}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#5f7279', fontWeight: 700 }}>
                          Score composite de lecture: {setupVerdict.score}/100
                        </Typography>
                      </Paper>

                      <Chip
                        label={`Risque opérationnel: ${riskLevel.label}`}
                        sx={{
                          alignSelf: 'flex-start',
                          bgcolor: riskLevel.background,
                          color: riskLevel.tone,
                          fontWeight: 800,
                          border: `1px solid ${riskLevel.tone}22`,
                        }}
                      />

                      <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.7 }}>
                        {riskLevel.description}
                      </Typography>

                      <Stack spacing={1.7}>
                        {confidenceBreakdown.map((item) => (
                          <Box key={item.label}>
                            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 700 }}>
                                {item.label}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: item.accent, fontWeight: 800 }}>
                                {item.display}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={item.value}
                              sx={{
                                height: 9,
                                borderRadius: 99,
                                bgcolor: 'rgba(16,37,43,0.08)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 99,
                                  bgcolor: item.accent,
                                },
                              }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', color: '#6f7f85', mt: 0.5 }}>
                              {item.helper}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Stack>
                  ) : null}

                  {activeInsightTab === 'history' ? (
                    <Stack spacing={1.5}>
                      {historyError ? <Alert severity="info">{historyError}</Alert> : null}
                      {historyLoading ? (
                        <LinearProgress sx={{ borderRadius: 99 }} />
                      ) : (
                        <Stack spacing={1.2}>
                          {historyTimelineEntries.map((entry, index) => {
                            const entryDirection = entry.direction || currentDirection;
                            const entryFusionPalette = buildSignalFusionPalette(entry.signalFusionState, entry.convictionScore);

                            return (
                              <Paper
                                key={`${entry.patternDetected}-${entry.analysisTimestamp || index}`}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 3,
                                  bgcolor: index === 0 ? '#f7f2e6' : '#fbfaf6',
                                  border: '1px solid rgba(16,37,43,0.06)',
                                }}
                              >
                                <Stack direction="row" spacing={1.2} alignItems="stretch">
                                  <Box sx={{ minWidth: 68 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>
                                      {formatTimelineDate(entry.analysisTimestamp)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6a7a80' }}>
                                      {entry.timeframe || currentSingleAnalysis.timeframe}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ position: 'relative', width: 12, display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{ width: 2, borderRadius: 99, background: 'rgba(16,37,43,0.12)' }} />
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 14,
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: directionColor(entryDirection),
                                      }}
                                    />
                                  </Box>

                                  <Stack spacing={1.05} sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" justifyContent="space-between" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>
                                          {getPatternTitle(entry.patternDetected)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#556970' }}>
                                          {directionHeadline(entryDirection)}
                                        </Typography>
                                      </Box>
                                      <Chip label={`${formatConfidence(entry.confidence)}%`} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }} />
                                    </Stack>

                                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                                      {entry.signalFusionState ? (
                                        <Chip
                                          label={formatSignalFusionState(entry.signalFusionState)}
                                          size="small"
                                          sx={{
                                            bgcolor: entryFusionPalette.background,
                                            color: entryFusionPalette.tone,
                                            fontWeight: 800,
                                            border: `1px solid ${entryFusionPalette.border}`,
                                          }}
                                        />
                                      ) : null}
                                      {isFiniteNumber(entry.convictionScore) ? (
                                        <Chip
                                          label={`Conviction ${Math.round(entry.convictionScore)}/100`}
                                          size="small"
                                          sx={{ bgcolor: '#f5f0e6', color: '#17323c', fontWeight: 700 }}
                                        />
                                      ) : null}
                                      {entry.divergenceType ? (
                                        <Chip label={`Div ${formatDivergenceTypeLabel(entry.divergenceType)}`} size="small" sx={{ bgcolor: '#fff8ef', color: '#17323c', fontWeight: 700 }} />
                                      ) : null}
                                      {entry.momentumState ? (
                                        <Chip label={`Mom ${formatMomentumStateLabel(entry.momentumState)}`} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }} />
                                      ) : null}
                                    </Stack>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#5a6c73',
                                        lineHeight: 1.7,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {entry.aiNarrative || entry.aiSummary}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  ) : null}

                  {activeInsightTab === 'advanced' ? (
                    <Stack spacing={2}>
                      {currentDiagnostics ? (
                        <TradeValidationPanel analysis={currentSingleAnalysis} />
                      ) : supportsTradeValidation(currentSingleAnalysis.patternDetected) ? (
                        <Alert severity="info">
                          {`Le moteur courant a retenu ${tradeValidationPatternLabel(currentSingleAnalysis.patternDetected)}, mais la validation trade-first détaillée n’a pas été remontée. Le fallback local a probablement arbitré sans diagnostics complets.`}
                        </Alert>
                      ) : null}

                      <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(16,37,43,0.08)' }}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.8} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'flex-start' }} sx={{ mb: 1.8 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 0.65 }}>
                              Calibration Binance 1H / 4H / 1D
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4d6068', lineHeight: 1.75 }}>
                              Ce sweep rejoue la fenêtre la plus récente de {currentSingleAnalysis.symbol || 'l’actif courant'} sur Binance, offset 0, sans persistance, pour comparer rapidement les signaux et sous-scores trade-first.
                            </Typography>
                          </Box>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} alignSelf="flex-start">
                            <Chip label="Replay live • offset 0 • non persisté" sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 800 }} />
                            <Button
                              variant="contained"
                              onClick={() => void runReplayCalibration()}
                              disabled={calibrationLoading}
                              startIcon={<InsightsRoundedIcon />}
                              sx={{ borderRadius: 99, bgcolor: '#17323c', ':hover': { bgcolor: '#0f2329' } }}
                            >
                              {calibrationLoading ? 'Calibration en cours...' : 'Lancer le sweep'}
                            </Button>
                          </Stack>
                        </Stack>

                        {calibrationError ? <Alert severity="warning" sx={{ mb: 1.5 }}>{calibrationError}</Alert> : null}

                        {calibrationLoading ? (
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c' }}>
                              Replays 1H / 4H / 1D en cours
                            </Typography>
                            <LinearProgress sx={{ height: 10, borderRadius: 99 }} />
                            <Typography variant="body2" sx={{ color: '#5a6d74' }}>
                              Le backend rejoue les fenêtres récentes directement sur Binance ou sur le stock historique si le live n’est pas disponible.
                            </Typography>
                          </Stack>
                        ) : calibrationResults.length > 0 ? (
                          <Stack spacing={1.5}>
                            {calibrationInsights ? (
                              <MultiTimeframeInsightsSummary
                                insights={calibrationInsights}
                                currentTimeframe={currentSingleAnalysis.timeframe}
                                compact
                              />
                            ) : null}

                            {calibrationResults.map((result) => (
                              <CalibrationReplayCard
                                key={`${result.analysis?.symbol || currentSingleAnalysis.symbol}-${result.analysis?.timeframe || 'unknown'}`}
                                result={result}
                                highlight={result.analysis?.timeframe === currentSingleAnalysis.timeframe}
                              />
                            ))}
                          </Stack>
                        ) : calibrationRequested ? (
                          <Alert severity="info">
                            Aucun replay exploitable n’a été remonté pour le sweep demandé.
                          </Alert>
                        ) : (
                          <Alert severity="info">
                            Lance le sweep pour comparer immédiatement la même lecture de marché sur 1H, 4H et 1D, avec les sous-scores trade-first si le moteur Python les remonte.
                          </Alert>
                        )}
                      </Paper>
                    </Stack>
                  ) : null}
                </Box>
              </Paper>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 5, background: '#fff' }}>
          <Stack spacing={2.1} sx={{ mb: 2.5 }}>
            <Box sx={{ maxWidth: 920 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#17323c', mb: 0.75 }}>
                Radar global des setups
              </Typography>
              <Typography variant="body1" sx={{ color: '#4d6068', maxWidth: 780, lineHeight: 1.8 }}>
                Le radar fait remonter d’abord les trois convictions à regarder maintenant, avec une priorisation locale qui combine score global et conviction multi-signal, puis laisse explorer le reste du classement sans noyer l’œil sous des cartes identiques.
              </Typography>
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, md: 1.8 },
                borderRadius: 4,
                background: 'linear-gradient(180deg, #fcf8ef 0%, #fffdfa 100%)',
                border: '1px solid rgba(16,37,43,0.08)',
              }}
            >
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.4}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', lg: 'center' }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                  <Chip
                    label={`${filteredScannerRows.length} setup${filteredScannerRows.length > 1 ? 's' : ''} visibles`}
                    sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 800 }}
                  />
                  <Chip
                    label={radarCoverageLabel}
                    sx={{ bgcolor: '#fff8ef', color: '#17323c', fontWeight: 800, border: '1px solid rgba(16,37,43,0.08)' }}
                  />
                  <Chip
                    label={`Fenêtre ${activeRadarTimeframe}`}
                    sx={{ bgcolor: '#f5f0e6', color: '#17323c', fontWeight: 800 }}
                  />
                  <Chip
                    label={`Tri ${activeRadarSortMode === 'GLOBAL' ? 'score global' : `score ${activeRadarSortMode}`}`}
                    sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800, border: '1px solid rgba(16,37,43,0.08)' }}
                  />
                </Stack>
                <FormControlLabel
                  control={<Switch checked={compactProMode} onChange={(event) => setCompactProMode(event.target.checked)} />}
                  label="Compact Pro"
                  sx={{ ml: { lg: 0.6 }, mr: 0, color: '#30444c' }}
                />
              </Stack>
              <GradeLegend onOpenLexicon={() => setGlossaryOpen(true)} />
            </Paper>
          </Stack>

          <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(16,37,43,0.08)', mb: 2.5 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel id="radar-timeframe-label">Fenêtre radar</InputLabel>
                <Select
                  labelId="radar-timeframe-label"
                  value={activeRadarTimeframe}
                  label="Fenêtre radar"
                  onChange={(event) => {
                    const nextTimeframe = String(event.target.value || '').toUpperCase();
                    const nextFilters = { ...filters, radarTimeframe: nextTimeframe };
                    setFilters(nextFilters);
                    void loadScanner({
                      filtersOverride: nextFilters,
                      stateOverride: { ...singleState, timeframe: nextTimeframe },
                    });
                  }}
                >
                  <MenuItem value="1H">1H</MenuItem>
                  <MenuItem value="4H">4H</MenuItem>
                  <MenuItem value="1D">1D</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel id="radar-sort-label">Tri score</InputLabel>
                <Select
                  labelId="radar-sort-label"
                  value={activeRadarSortMode}
                  label="Tri score"
                  onChange={(event) => setFilters((current) => ({ ...current, radarSortMode: normalizeRadarSortMode(event.target.value) }))}
                >
                  <MenuItem value="GLOBAL">Score global (défaut)</MenuItem>
                  <MenuItem value="1H">Score fenêtre 1H</MenuItem>
                  <MenuItem value="4H">Score fenêtre 4H</MenuItem>
                  <MenuItem value="1D">Score fenêtre 1D</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id="universe-label">Univers</InputLabel>
                <Select
                  labelId="universe-label"
                  value={filters.universe}
                  label="Univers"
                  onChange={(event) => setFilters((current) => ({ ...current, universe: event.target.value }))}
                >
                  <MenuItem value="top100">Top 100</MenuItem>
                  <MenuItem value="top200">Top 200</MenuItem>
                  <MenuItem value="top300">Top 300</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 240 }}>
                <InputLabel id="pattern-type-label">Pattern</InputLabel>
                <Select
                  labelId="pattern-type-label"
                  value={filters.patternType}
                  label="Pattern"
                  onChange={(event) => setFilters((current) => ({ ...current, patternType: event.target.value }))}
                >
                  <MenuItem value="">Tous les patterns</MenuItem>
                  {managedPatternOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Score min"
                type="number"
                value={filters.minScore}
                onChange={(event) => setFilters((current) => ({ ...current, minScore: Math.max(0, Number(event.target.value) || 0) }))}
                helperText="0 = afficher tout le classement chargé"
              />
              <TextField
                label="Conviction min"
                type="number"
                value={filters.minConviction}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  minConviction: Math.max(0, Math.min(100, Number(event.target.value) || 0)),
                }))}
                helperText="0 = ne pas filtrer la convergence"
              />
              <TextField
                label="Confiance min"
                type="number"
                value={filters.minConfidence}
                onChange={(event) => setFilters((current) => ({ ...current, minConfidence: Number(event.target.value) || 0 }))}
              />
              <TextField
                label="Pump min"
                type="number"
                value={filters.minPumpProbability}
                onChange={(event) => setFilters((current) => ({ ...current, minPumpProbability: Number(event.target.value) || 0 }))}
              />
              <TextField
                label="Secteur"
                value={filters.sector}
                onChange={(event) => setFilters((current) => ({ ...current, sector: event.target.value }))}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Switch checked={filters.bullishOnly} onChange={(event) => setFilters((current) => ({ ...current, bullishOnly: event.target.checked, bearishOnly: event.target.checked ? false : current.bearishOnly }))} />}
                label="Haussier uniquement"
              />
              <FormControlLabel
                control={<Switch checked={filters.bearishOnly} onChange={(event) => setFilters((current) => ({ ...current, bearishOnly: event.target.checked, bullishOnly: event.target.checked ? false : current.bullishOnly }))} />}
                label="Baissier uniquement"
              />
              <FormControlLabel
                control={<Switch checked={filters.topMoversOnly} onChange={(event) => setFilters((current) => ({ ...current, topMoversOnly: event.target.checked }))} />}
                label="Plus fortes variations uniquement"
              />
              <FormControlLabel
                control={<Switch checked={filters.advancedSignalsOnly} onChange={(event) => setFilters((current) => ({ ...current, advancedSignalsOnly: event.target.checked }))} />}
                label="Signaux avancés uniquement"
              />
            </Stack>
          </Paper>

          {scannerError && <Alert severity="info" sx={{ mb: 2 }}>{scannerError}</Alert>}

          {!scannerLoading && !scannerError && filteredScannerRows.length > 0 && filteredScannerRows.length < displayedScannerRowsCount ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={hasLocalScannerFilters ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setFilters((current) => ({ ...current, patternType: '', minScore: 0, minConviction: 0, advancedSignalsOnly: false }))}
                >
                  Tout afficher
                </Button>
              ) : null}
            >
              Le radar a bien classé {displayedScannerRowsCount} actifs sur {activeRadarTimeframe}. {filteredScannerRows.length} restent visibles avec les filtres locaux actuels ({localRadarFilterSummary}). Le rang visible privilégie le mix score radar + conviction multi-signal.
            </Alert>
          ) : null}

          {scannerLoading ? (
            <Paper
              elevation={0}
              sx={{
                py: 4,
                px: 3,
                borderRadius: 4,
                border: '1px solid rgba(16,37,43,0.08)',
                background: '#fcf8ef',
              }}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                      Analyse de l’univers en cours
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4d6068' }}>
                      Chargement du classement scanner et synchronisation de progression en temps réel.
                    </Typography>
                  </Box>
                  <CircularProgress size={26} sx={{ color: '#17323c' }} />
                </Stack>

                <LinearProgress
                  variant={scannerProgress.total > 0 ? 'determinate' : 'indeterminate'}
                  value={scannerProgress.total > 0 ? Math.max(scannerProgressPercent, 4) : undefined}
                  sx={{
                    height: 10,
                    borderRadius: 99,
                    bgcolor: 'rgba(16,37,43,0.08)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 99,
                      bgcolor: '#17323c',
                    },
                  }}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={scannerProgress.total > 0
                      ? `${scannerProgress.completed}/${scannerProgress.total} actifs traités`
                      : 'Initialisation du scanner'}
                    size="small"
                    sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 700 }}
                  />
                  {scannerProgress.currentSymbol && (
                    <Chip
                      label={`Actif en cours: ${scannerProgress.currentSymbol}`}
                      size="small"
                      sx={{ bgcolor: '#e8f0f2', color: '#17323c', fontWeight: 700 }}
                    />
                  )}
                  {scannerProgress.errorMessage && (
                    <Chip
                      label={`Info: ${scannerProgress.errorMessage}`}
                      size="small"
                      sx={{ bgcolor: '#fde1dc', color: '#7a2d23', fontWeight: 700 }}
                    />
                  )}
                </Stack>
              </Stack>
            </Paper>
          ) : filteredScannerRows.length === 0 ? (
            <Alert severity="info">
              {displayedScannerRowsCount > 0
                ? `Le radar a classé ${displayedScannerRowsCount} actifs sur ${activeRadarTimeframe}, mais aucun ne reste visible avec les filtres locaux actuels (${localRadarFilterSummary}).`
                : 'Aucun actif ne correspond au pattern selectionne avec les filtres actuels.'}
            </Alert>
          ) : (
            <Stack spacing={2.2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {topScannerRows.map((row, index) => (
                  <TopSetupSpotlightCard
                    key={firstRowKey(row)}
                    row={row}
                    rank={index + 1}
                    dominant={index === 0}
                    compactPro={compactProMode}
                    onFocus={() => focusOnRow(row)}
                  />
                ))}
              </Box>

              {remainingScannerRows.length > 0 ? (
                <Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 1.4 }}>
                    <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 800 }}>
                      Suite du classement
                    </Typography>
                    <Chip
                      label={`${remainingScannerRows.length} setup${remainingScannerRows.length > 1 ? 's' : ''} supplémentaire${remainingScannerRows.length > 1 ? 's' : ''}`}
                      sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }}
                    />
                  </Stack>

                  <Stack spacing={1.5}>
                    {remainingScannerRows.map((row) => {
                      const rowKey = firstRowKey(row);
                      const expanded = expandedRowKey === rowKey;

                      return (
                        <ScannerRowCard
                          key={rowKey}
                          row={row}
                          expanded={expanded}
                          highlight={false}
                          compactPro={compactProMode}
                          onToggle={() => setExpandedRowKey(expanded ? '' : rowKey)}
                          onFocus={() => focusOnRow(row)}
                          onOpenLexicon={() => setGlossaryOpen(true)}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          )}
        </Paper>
      </Container>

      <PatternLearningDrawer
        open={learningOpen}
        onClose={() => setLearningOpen(false)}
        highlightedPattern={currentSingleAnalysis?.patternDetected}
      />

      <PatternGlossaryDrawer
        open={glossaryOpen}
        onClose={() => setGlossaryOpen(false)}
      />

      <Dialog
        open={telegramDialogOpen}
        onClose={() => setTelegramDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Envoi Telegram du setup courant
          <Typography variant="body2" sx={{ mt: 0.8, color: '#5c6d73', lineHeight: 1.7 }}>
            Configure la destination, vérifie l’image annotée et contrôle exactement le message avant l’envoi.
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ background: '#fbf7ef' }}>
          <Stack spacing={2.2}>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              {connectedUserLogin
                ? `Le chatId sera enregistré pour ${connectedUserLogin} et réutilisé automatiquement lors des prochains envois.`
                : 'Aucun utilisateur connecté. Le chatId sera conservé localement sur cet appareil pour les prochains envois.'}
            </Alert>

            {!telegramSendAvailable ? (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                {telegramSendStatusMessage || 'Telegram n’est pas configuré côté serveur. L’aperçu reste disponible, mais l’envoi direct est désactivé.'}
              </Alert>
            ) : null}

            <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fff', border: '1px solid rgba(16,37,43,0.08)' }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.4} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
                <TextField
                  fullWidth
                  label="ChatId Telegram"
                  value={telegramChatId}
                  onChange={(event) => setTelegramChatId(event.target.value)}
                  error={Boolean(telegramChatIdValidationMessage)}
                  placeholder="Exemple: 123456789, -1001234567890 ou @mon_canal"
                  helperText={telegramChatIdValidationMessage || 'Utilise un ID numérique pour une discussion privée. Le format @mon_canal ne marche que pour un canal ou groupe public où le bot est déjà membre.'}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} sx={{ minWidth: { lg: 420 } }}>
                  <Button
                    variant="outlined"
                    onClick={() => void loadTelegramPreview(telegramChatId)}
                    disabled={telegramPreviewLoading || telegramSending}
                    sx={{ borderRadius: 99, fontWeight: 700 }}
                  >
                    {telegramPreviewLoading ? 'Chargement...' : 'Actualiser l’aperçu'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => void handleTelegramSave()}
                    disabled={telegramSaving || telegramSending}
                    sx={{ borderRadius: 99, fontWeight: 700 }}
                  >
                    {telegramSaving ? 'Enregistrement...' : 'Enregistrer ce chatId'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {telegramError ? <Alert severity="error" sx={{ borderRadius: 3 }}>{telegramError}</Alert> : null}
            {telegramNotice ? <Alert severity="success" sx={{ borderRadius: 3 }}>{telegramNotice}</Alert> : null}

            <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, background: '#fff', border: '1px solid rgba(16,37,43,0.08)' }}>
              <Stack spacing={2.2}>
                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.2} alignItems="stretch">
                <Box sx={{ flex: 1.15, minWidth: 0 }}>
                  <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
                    Image annotée du setup courant
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      minHeight: 320,
                      borderRadius: 4,
                      border: '1px solid rgba(16,37,43,0.08)',
                      background: 'linear-gradient(180deg, #f5efe4 0%, #efe5d4 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1.5,
                      overflow: 'hidden',
                    }}
                  >
                    {telegramPreviewLoading ? (
                      <Stack spacing={1.1} alignItems="center">
                        <CircularProgress size={28} sx={{ color: '#17323c' }} />
                        <Typography variant="body2" sx={{ color: '#5a6d74' }}>
                          Préparation de l’aperçu Telegram...
                        </Typography>
                      </Stack>
                    ) : telegramPreview?.imageAvailable
                      && telegramPreview?.imageUrl
                      && !telegramPreviewImageFailures[telegramPreview.imageUrl] ? (
                      <Box
                        component="img"
                        src={telegramPreview.imageUrl}
                        alt={`Aperçu Telegram ${telegramPreview.symbol}`}
                        onError={() => setTelegramPreviewImageFailures((current) => ({ ...current, [telegramPreview.imageUrl]: true }))}
                        sx={{ width: '100%', maxHeight: 520, objectFit: 'contain', borderRadius: 3 }}
                      />
                    ) : (
                      <Stack spacing={1.1} alignItems="center" sx={{ maxWidth: 360, textAlign: 'center' }}>
                        <ShowChartRoundedIcon sx={{ fontSize: 42, color: '#6a7a80' }} />
                        <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 800 }}>
                          Aucun visuel annoté disponible
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#56686f', lineHeight: 1.7 }}>
                          Le message Telegram partira en texte pur si aucune image annotée exploitable n’est disponible pour cette analyse.
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
                    Aperçu du message
                  </Typography>

                  {telegramPreviewLoading ? (
                    <Paper elevation={0} sx={{ mt: 1, p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(16,37,43,0.08)' }}>
                      <Stack spacing={1.2} alignItems="flex-start">
                        <CircularProgress size={24} sx={{ color: '#17323c' }} />
                        <Typography variant="body2" sx={{ color: '#5a6d74' }}>
                          Construction du message Telegram premium...
                        </Typography>
                      </Stack>
                    </Paper>
                  ) : telegramPreview ? (
                    <Stack spacing={1.2} sx={{ mt: 1 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`${telegramPreview.symbol} · ${telegramPreview.exchange}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                        <Chip label={`Fenêtre ${telegramPreview.timeframe}`} sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 800 }} />
                        <Chip label={telegramPreview.patternLabel} sx={{ bgcolor: '#f8ede6', color: '#17323c', fontWeight: 800 }} />
                        <Chip label={telegramPreview.directionLabel} sx={{ bgcolor: '#fff4db', color: '#17323c', fontWeight: 800 }} />
                        <Chip label={`Analyse ${formatDateTime(telegramPreview.analysisTimestamp)}`} sx={{ bgcolor: '#fffdf8', color: '#17323c', fontWeight: 700 }} />
                        {telegramPreview.resolvedChatId ? (
                          <Chip label={`Destination ${telegramPreview.resolvedChatId}`} sx={{ bgcolor: '#ecf7ef', color: '#1f7a4d', fontWeight: 800 }} />
                        ) : null}
                      </Stack>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 4,
                          background: '#fcf8ef',
                          border: '1px solid rgba(16,37,43,0.08)',
                          minHeight: 280,
                        }}
                      >
                        <Typography
                          component="pre"
                          sx={{
                            m: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: '#30444c',
                            lineHeight: 1.75,
                            fontFamily: 'inherit',
                            fontSize: '0.98rem',
                          }}
                        >
                          {telegramPreview.message}
                        </Typography>
                      </Paper>
                    </Stack>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1, borderRadius: 3 }}>
                      Ouvre l’aperçu pour générer le message Telegram à partir de l’analyse courante.
                    </Alert>
                  )}
                </Box>
                </Stack>

                {!telegramPreviewLoading && telegramPreview && telegramMultiTimeframeCharts.length > 0 ? (
                  <>
                    <Divider flexItem />
                    <Box>
                      <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
                        Pack multi-timeframe envoyé sous le setup courant
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.8, color: '#56686f', lineHeight: 1.75, maxWidth: 920 }}>
                        En plus du graphe courant, Telegram enverra les vues 1H, 4H et 1D dans cet ordre, chacune avec son propre texte d’analyse.
                      </Typography>

                      <Box
                        sx={{
                          mt: 1.5,
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                          gap: 1.5,
                        }}
                      >
                        {telegramMultiTimeframeCharts.map((chart) => {
                          const chartImageKey = chart.imageUrl || `${chart.symbol}-${chart.timeframe}`;
                          const chartImageFailed = Boolean(telegramPreviewImageFailures[chartImageKey]);

                          return (
                            <Paper
                              key={`${chart.symbol}-${chart.timeframe}-${chart.analysisTimestamp || chartImageKey}`}
                              elevation={0}
                              sx={{
                                p: 1.6,
                                borderRadius: 4,
                                background: '#fcf8ef',
                                border: '1px solid rgba(16,37,43,0.08)',
                              }}
                            >
                              <Stack spacing={1.2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip label={`${chart.symbol} · ${chart.exchange}`} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                                  <Chip label={`Fenêtre ${chart.timeframe}`} size="small" sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 800 }} />
                                  <Chip label={chart.patternLabel} size="small" sx={{ bgcolor: '#f8ede6', color: '#17323c', fontWeight: 800 }} />
                                  <Chip label={chart.directionLabel} size="small" sx={{ bgcolor: '#fff4db', color: '#17323c', fontWeight: 800 }} />
                                </Stack>

                                <Box
                                  sx={{
                                    minHeight: 220,
                                    borderRadius: 3,
                                    border: '1px solid rgba(16,37,43,0.08)',
                                    background: 'linear-gradient(180deg, #f5efe4 0%, #efe5d4 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 1.2,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {chart.imageAvailable && chart.imageUrl && !chartImageFailed ? (
                                    <Box
                                      component="img"
                                      src={chart.imageUrl}
                                      alt={`Aperçu Telegram ${chart.symbol} ${chart.timeframe}`}
                                      onError={() => setTelegramPreviewImageFailures((current) => ({ ...current, [chartImageKey]: true }))}
                                      sx={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 2.5 }}
                                    />
                                  ) : (
                                    <Stack spacing={0.9} alignItems="center" sx={{ maxWidth: 260, textAlign: 'center' }}>
                                      <ShowChartRoundedIcon sx={{ fontSize: 36, color: '#6a7a80' }} />
                                      <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>
                                        Visuel indisponible sur {chart.timeframe}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#56686f', lineHeight: 1.6 }}>
                                        Telegram enverra le texte d’analyse même si ce visuel annoté n’est pas exploitable.
                                      </Typography>
                                    </Stack>
                                  )}
                                </Box>

                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.4,
                                    borderRadius: 3,
                                    background: '#fff',
                                    border: '1px solid rgba(16,37,43,0.08)',
                                    minHeight: 180,
                                  }}
                                >
                                  <Typography
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      color: '#30444c',
                                      lineHeight: 1.7,
                                      fontFamily: 'inherit',
                                      fontSize: '0.94rem',
                                    }}
                                  >
                                    {chart.message}
                                  </Typography>
                                </Paper>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  </>
                ) : null}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTelegramDialogOpen(false)} sx={{ borderRadius: 99, fontWeight: 700 }}>
            Fermer
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleTelegramSend()}
            disabled={telegramSending || telegramPreviewLoading || !telegramSendAvailable || Boolean(telegramChatIdValidationMessage)}
            startIcon={<SendRoundedIcon />}
            sx={{ borderRadius: 99, bgcolor: '#17323c', ':hover': { bgcolor: '#0f2329' } }}
          >
            {telegramSending ? 'Envoi en cours...' : (telegramSendAvailable ? 'Envoyer maintenant' : 'Envoi indisponible')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MetricTile = ({ label, value, tone }) => (
  <Paper
    elevation={0}
    sx={{
      minWidth: 0,
      width: '100%',
      p: 2,
      borderRadius: 4,
      bgcolor: 'rgba(248,244,236,0.08)',
      color: '#f8f4ec',
      border: `1px solid ${tone}33`,
    }}
  >
    <Typography variant="caption" sx={{ color: 'rgba(248,244,236,0.72)', display: 'block', overflowWrap: 'break-word' }}>{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 800, color: tone, overflowWrap: 'break-word', wordBreak: 'normal' }}>{value}</Typography>
  </Paper>
);

const SignalNarrativeBlock = ({ label, value, palette }) => {
  if (!value) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 0.4,
        p: 1.2,
        borderRadius: 3,
        bgcolor: '#fffdf8',
        border: `1px solid ${palette.border}`,
      }}
    >
      <Typography variant="caption" sx={{ color: '#6d7e84', fontWeight: 800, letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.55, color: '#30444c', lineHeight: 1.7 }}>
        {value}
      </Typography>
    </Box>
  );
};

const SignalConvergencePanel = ({ analysis, palette, compact = false }) => {
  const hasAdvancedSignals = hasAdvancedSignalCoverage(analysis);
  const hasDivergenceSignal = hasActionableDivergenceSignal(analysis);
  const hasMomentumSignal = hasActionableMomentumSignal(analysis);
  const hasCandlestickSignal = hasActionableCandlestickSignal(analysis);
  const convergenceItems = hasAdvancedSignals ? buildSignalConvergenceItems(analysis) : [];
  const convictionScore = isFiniteNumber(analysis?.convictionScore) ? Math.round(analysis.convictionScore) : null;
  const normalizedFusionState = normalizeSignalFusionState(analysis?.signalFusionState);
  const fusionStateLabel = formatSignalFusionState(analysis?.signalFusionState);
  const advancedDivergence = analysis?.advancedDivergence;
  const advancedMomentum = analysis?.advancedMomentum;
  const advancedCandlestick = analysis?.advancedCandlestick;
  const divergenceIndicatorLabel = advancedDivergence?.indicator ? formatDivergenceIndicatorLabel(advancedDivergence.indicator) : null;
  const divergenceComment = firstNonEmptyText(advancedDivergence?.comment);
  const momentumComment = firstNonEmptyText(advancedMomentum?.comment);
  const candlestickComment = firstNonEmptyText(advancedCandlestick?.comment);
  const fusionChipLabel = convictionScore != null
    ? (normalizedFusionState ? `${convictionScore}/100 · ${fusionStateLabel}` : `${convictionScore}/100 · Convergence partielle`)
    : normalizedFusionState
      ? fusionStateLabel
      : hasAdvancedSignals
        ? 'Convergence partielle'
        : 'Sans convergence tactique';
  const convergenceNarrative = firstNonEmptyText(analysis?.aiNarrative)
    || (hasAdvancedSignals
      ? 'Lecture multi-signal partielle: certains appuis tactiques sont présents, mais la fusion reste incomplète.'
      : 'Aucun signal avancé exploitable pour ce setup. La structure reste visible, mais divergence, momentum et chandelier ne donnent pas encore d’appui tactique.');

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.45 : 1.8,
        borderRadius: 4,
        background: palette.surface,
        border: `1px solid ${palette.border}`,
      }}
    >
      <Stack spacing={1.5}>
        <Stack spacing={1.2}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
              <InsightsRoundedIcon sx={{ color: palette.tone }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                Signal Convergence
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#30444c', lineHeight: 1.75 }}>
              {convergenceNarrative}
            </Typography>
          </Box>

          <Chip
            label={fusionChipLabel}
            sx={{
              alignSelf: 'flex-start',
              maxWidth: '100%',
              height: 'auto',
              bgcolor: '#fffdf8',
              color: palette.tone,
              fontWeight: 900,
              border: `1px solid ${palette.border}`,
              '& .MuiChip-label': {
                display: 'block',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                textAlign: 'left',
                paddingTop: 7,
                paddingBottom: 7,
              },
            }}
          />
        </Stack>

        {convictionScore != null ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#6d7e84', fontWeight: 800, letterSpacing: '0.08em' }}>
                Conviction meter
              </Typography>
              <Typography variant="caption" sx={{ color: palette.tone, fontWeight: 800 }}>
                {convictionScore}/100
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={convictionScore}
              sx={{
                height: 10,
                borderRadius: 99,
                bgcolor: 'rgba(16,37,43,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 99,
                  bgcolor: palette.tone,
                },
              }}
            />
          </Box>
        ) : null}

        {!compact ? (
          <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
            {convergenceItems.length ? convergenceItems.map((item) => (
              <Chip
                key={item}
                label={item}
                sx={{
                  bgcolor: palette.background,
                  color: palette.tone,
                  fontWeight: 800,
                  border: `1px solid ${palette.border}`,
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                  },
                }}
              />
            )) : (
              <Chip label="Structure seule pour l’instant" sx={{ bgcolor: '#fffdf8', color: '#6d7e84', fontWeight: 700 }} />
            )}
          </Stack>
        ) : null}

        {compact ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, bgcolor: '#fffdf8', border: `1px solid ${palette.border}`, minWidth: 0 }}>
              <DetailLine label="Divergence" value={hasDivergenceSignal ? formatDivergenceSummary(analysis) : 'Aucune'} />
            </Paper>
            <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, bgcolor: '#fffdf8', border: `1px solid ${palette.border}`, minWidth: 0 }}>
              <DetailLine label="Momentum" value={hasMomentumSignal ? formatMomentumSummary(analysis) : 'Aucun'} />
            </Paper>
            <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, bgcolor: '#fffdf8', border: `1px solid ${palette.border}`, minWidth: 0 }}>
              <DetailLine label="Chandelier" value={hasCandlestickSignal ? formatCandlestickSummary(analysis) : 'Aucun'} />
            </Paper>
          </Box>
        ) : (
          <Stack spacing={0.8}>
            {hasDivergenceSignal ? (
              <DetailLine
                label="Divergence"
                value={formatDivergenceSummary(analysis)}
              />
            ) : (
              <DetailLine label="Divergence" value="Aucune divergence détectée" />
            )}
            {divergenceIndicatorLabel ? (
              <DetailLine label="Provenance" value={divergenceIndicatorLabel} />
            ) : null}
            {divergenceComment ? (
              <Box
                sx={{
                  mt: 0.4,
                  p: 1.2,
                  borderRadius: 3,
                  bgcolor: '#fffdf8',
                  border: `1px solid ${palette.border}`,
                }}
              >
                <Typography variant="caption" sx={{ color: '#6d7e84', fontWeight: 800, letterSpacing: '0.08em' }}>
                  Lecture divergence
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.55, color: '#30444c', lineHeight: 1.7 }}>
                  {divergenceComment}
                </Typography>
              </Box>
            ) : null}
            {hasMomentumSignal ? (
              <DetailLine
                label="Momentum"
                value={formatMomentumSummary(analysis)}
              />
            ) : (
              <DetailLine label="Momentum" value="Aucun signal momentum" />
            )}
            {momentumComment ? (
              <SignalNarrativeBlock label="Lecture momentum" value={momentumComment} palette={palette} />
            ) : null}
            {hasCandlestickSignal ? (
              <DetailLine
                label="Validation chandelier"
                value={formatCandlestickSummary(analysis)}
              />
            ) : (
              <DetailLine label="Validation chandelier" value="Aucun chandelier tactique" />
            )}
            {advancedCandlestick?.direction ? (
              <DetailLine label="Biais chandelier" value={directionLabel(String(advancedCandlestick.direction).toUpperCase())} />
            ) : null}
            {typeof advancedCandlestick?.confirmationRequired === 'boolean' ? (
              <DetailLine label="Confirmation requise" value={advancedCandlestick.confirmationRequired ? 'Oui' : 'Non'} />
            ) : null}
            {candlestickComment ? (
              <SignalNarrativeBlock label="Lecture chandelier" value={candlestickComment} palette={palette} />
            ) : null}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const ConvictionEnginePanel = ({ analysis, direction, verdict, grade, palette, multiTimeframeDetail, onOpenLexicon }) => {
  const matrix = buildConvictionMatrix(analysis, direction);
  const convictionScore = resolveEffectiveConvictionScore(analysis);
  const convictionLabel = isFiniteNumber(convictionScore) ? `${Math.round(convictionScore)}/100` : 'n/a';
  const setupNote = verdict?.description || 'Aucune synthèse disponible.';
  const matrixLead = [
    grade?.label,
    verdict?.setupQuality ? `Setup ${verdict.setupQuality}` : null,
    multiTimeframeDetail?.dominantTimeframe ? `TF ${multiTimeframeDetail.dominantTimeframe}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.9,
        borderRadius: 4,
        border: `1px solid ${palette.border}`,
        background: palette.surface,
      }}
    >
      <Stack spacing={1.5}>
        <Stack spacing={1.2}>
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6, minWidth: 0 }}>
              <InsightsRoundedIcon sx={{ color: palette.tone, flexShrink: 0 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', minWidth: 0 }}>
                Conviction engine
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#30444c', lineHeight: 1.75, width: '100%', overflowWrap: 'anywhere' }}>
              {setupNote}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ width: '100%' }}>
            <Chip
              label={convictionLabel}
              sx={{ bgcolor: palette.background, color: palette.tone, fontWeight: 900, border: `1px solid ${palette.border}` }}
            />
            <Tooltip title={renderConvictionGradeTooltip(grade, onOpenLexicon)} arrow>
              <Chip
                label={`${grade?.label || 'Setup faible'} · ${grade?.tier || 'C'}`}
                sx={{ bgcolor: grade?.background || '#fffdf8', color: grade?.tone || '#17323c', fontWeight: 900, border: `1px solid ${(grade?.tone || '#17323c')}22` }}
              />
            </Tooltip>
            <Chip
              label={grade?.bias || directionHeadline(direction)}
              sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }}
            />
          </Stack>
        </Stack>

        <Typography variant="caption" sx={{ color: '#6d7e84', fontWeight: 800, letterSpacing: '0.08em' }}>
          {matrixLead || 'Lecture compacte du setup'}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          {matrix.map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 1.2,
                borderRadius: 3,
                border: `1px solid ${item.tone}22`,
                background: item.background,
                minWidth: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: '#627278', fontWeight: 800, letterSpacing: '0.08em' }}>
                {item.label}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: item.tone, fontWeight: 900, lineHeight: 1.35, mt: 0.35 }}>
                {item.value}
              </Typography>
              <Typography variant="caption" sx={{ color: '#5f7279', lineHeight: 1.5, display: 'block', mt: 0.3 }}>
                {item.helper}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};

const MultiTimeframeBreakdown = ({ row, compact = false, sx = {} }) => {
  const detail = row?.multiTimeframeDetail;
  const timeframes = Array.isArray(detail?.timeframes) ? detail.timeframes : [];
  if (!timeframes.length) {
    return null;
  }

  const dominantDirection = detail?.dominantDirection || 'NEUTRAL';
  const summaryTone = directionColor(dominantDirection);
  const summaryBackground = directionSoftBackground(dominantDirection);

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.35 : 1.7,
        borderRadius: 3.5,
        background: '#fcfbf7',
        border: '1px solid rgba(16,37,43,0.08)',
        ...sx,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.2} sx={{ mb: 1.2 }}>
        <Box>
          <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.08em' }}>
            Lecture 1H / 4H / 1D
          </Typography>
          <Typography variant={compact ? 'body2' : 'subtitle2'} sx={{ color: '#30444c', fontWeight: 700 }}>
            {`Biais ${directionLabel(dominantDirection).toLowerCase()}${detail?.dominantTimeframe ? `, dominante ${detail.dominantTimeframe}` : ''}.`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
          {isFiniteNumber(detail?.alignmentScore) ? (
            <Chip
              label={`Alignement ${Math.round(detail.alignmentScore)}/100`}
              size="small"
              sx={{ bgcolor: summaryBackground, color: summaryTone, fontWeight: 800 }}
            />
          ) : null}
          {isFiniteNumber(detail?.convictionScore) ? (
            <Chip
              label={`Conviction MTF ${Math.round(detail.convictionScore)}/100`}
              size="small"
              sx={{ bgcolor: 'rgba(23,63,95,0.08)', color: '#173f5f', fontWeight: 800 }}
            />
          ) : null}
          {isFiniteNumber(detail?.volumeAlignmentScore) ? (
            <Chip
              label={`Volume ${Math.round(detail.volumeAlignmentScore)}/100`}
              size="small"
              sx={{ bgcolor: 'rgba(141,108,22,0.1)', color: '#8d6c16', fontWeight: 800 }}
            />
          ) : null}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {timeframes.map((slice) => {
          const sliceTone = slice.available ? directionColor(slice.direction) : '#6d7e84';
          const sliceBackground = slice.available ? directionSoftBackground(slice.direction) : 'rgba(109,126,132,0.12)';
          const confidenceLabel = isFiniteNumber(slice.confidence) ? `${formatConfidence(slice.confidence)}%` : 'n/a';
          const convictionLabel = isFiniteNumber(slice.convictionScore) ? `${slice.convictionScore}/100` : 'n/a';
          const volumeLabel = slice.volumeTrend
            ? `Volume ${formatVolumeTrendLabel(slice.volumeTrend).toLowerCase()}`
            : slice.volumeConfirmation === true
              ? 'Volume confirme'
              : slice.volumeConfirmation === false
                ? 'Volume non confirme'
                : 'Volume n/a';

          return (
            <Paper
              key={`${row?.symbol || 'row'}-${slice.timeframe}`}
              elevation={0}
              sx={{
                p: compact ? 1.2 : 1.35,
                borderRadius: 3,
                background: '#fffdf8',
                border: `1px solid ${sliceTone}18`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.85 }}>
                <Chip label={slice.timeframe} size="small" sx={{ bgcolor: sliceBackground, color: sliceTone, fontWeight: 800 }} />
                {detail?.dominantTimeframe === slice.timeframe && slice.available ? (
                  <Chip label="Dominante" size="small" sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 800 }} />
                ) : null}
              </Stack>

              <Typography variant={compact ? 'h6' : 'h5'} sx={{ color: sliceTone, fontWeight: 900, lineHeight: 1 }}>
                {slice.available && isFiniteNumber(slice.patternScore) ? slice.patternScore : 'n/a'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#6d7e84', mb: 0.55 }}>
                Score pattern
              </Typography>
              <Typography variant="body2" sx={{ color: '#30444c', fontWeight: 700 }}>
                {slice.available ? `${directionLabel(slice.direction)} · Conf ${confidenceLabel}` : 'Lecture indisponible'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#5f7279', mt: 0.45, lineHeight: 1.6 }}>
                {slice.available ? `Conviction ${convictionLabel} · ${volumeLabel}` : 'Aucune analyse stockee sur cette fenetre.'}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

const StatBadge = ({ label, value, accent = '#17323c', compact = false, tooltip = '' }) => {
  const content = (
    <Paper
      elevation={0}
      sx={{
        px: compact ? 1.35 : 1.8,
        py: compact ? 1 : 1.4,
        borderRadius: compact ? 2.4 : 3,
        border: `1px solid ${accent}22`,
        bgcolor: '#fffdf8',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography variant="caption" sx={{ color: '#6c7a80', fontSize: compact ? '0.66rem' : undefined, lineHeight: compact ? 1.1 : undefined, display: 'block', overflowWrap: 'break-word' }}>{label}</Typography>
      <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ fontWeight: 900, color: accent, lineHeight: compact ? 1.1 : undefined, overflowWrap: 'break-word', wordBreak: 'normal' }}>{value}</Typography>
    </Paper>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip title={tooltip} arrow>
      <Box sx={{ width: '100%' }}>{content}</Box>
    </Tooltip>
  );
};

const SignalPillar = ({ label, value, helper, tone, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 4,
      background: '#fff',
      border: `1px solid ${tone}22`,
      minHeight: 142,
      minWidth: 0,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box sx={{ color: tone, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography variant="overline" sx={{ color: '#697a81', letterSpacing: '0.12em', minWidth: 0, overflowWrap: 'break-word' }}>{label}</Typography>
    </Stack>
    <Typography variant="h4" sx={{ fontWeight: 900, color: tone, mb: 0.8, overflowWrap: 'break-word', wordBreak: 'normal' }}>
      {value}
    </Typography>
    <Typography variant="body2" sx={{ color: '#56686f', lineHeight: 1.7 }}>
      {helper}
    </Typography>
  </Paper>
);

const DetailLine = ({ label, value }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(120px, 44%) minmax(0, 1fr)', sm: 'minmax(144px, 46%) minmax(0, 1fr)' },
      gap: 1.1,
      alignItems: 'start',
      minWidth: 0,
    }}
  >
    <Typography variant="body2" sx={{ color: '#6f7f85', minWidth: 0, overflowWrap: 'break-word' }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700, textAlign: 'right', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'normal' }}>{value}</Typography>
  </Box>
);

const TopSetupSpotlightCard = ({ row, rank, dominant, compactPro = false, onFocus }) => {
  const direction = deriveRowDirection(row);
  const volumeStrength = buildVolumeStrength(row);
  const hasAdvancedSignals = hasAdvancedSignalCoverage(row);
  const convictionScore = resolveEffectiveConvictionScore(row);
  const convictionGrade = buildConvictionGrade(row, direction);
  const signalFusionPalette = buildSignalFusionPalette(row.signalFusionState, convictionScore);

  return (
    <Paper
      elevation={0}
      sx={{
        p: compactPro ? (dominant ? 2.25 : 1.9) : (dominant ? 3 : 2.4),
        borderRadius: 5,
        border: dominant ? '1px solid rgba(243,198,92,0.45)' : '1px solid rgba(16,37,43,0.08)',
        background: dominant ? 'linear-gradient(135deg, #fff7e5 0%, #fff 100%)' : '#fff',
        gridColumn: { xs: 'span 1', lg: dominant ? 'span 6' : 'span 3' },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: compactPro ? { xs: 'auto', lg: 680 } : { xs: 'auto', lg: dominant ? 730 : 630 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={compactPro ? 1 : 1.2} alignItems="flex-start" sx={{ mb: compactPro ? 1 : 1.4 }}>
        <Chip
          label={dominant ? 'Priorité radar' : 'Setup prioritaire'}
          sx={{
            bgcolor: dominant ? '#f3c65c' : '#eef4f6',
            color: '#17323c',
            fontWeight: 800,
          }}
        />
        <Chip label={`#${rank}`} sx={{ bgcolor: '#fffdf8', color: '#17323c', fontWeight: 900, border: '1px solid rgba(16,37,43,0.08)' }} />
      </Stack>

      <Typography variant={compactPro ? (dominant ? 'h4' : 'h5') : (dominant ? 'h3' : 'h4')} sx={{ color: '#17323c', fontWeight: 900, mb: compactPro ? 0.2 : 0.35 }}>
        {row.symbol}
      </Typography>
      {row.name ? (
        <Typography variant={compactPro ? 'caption' : 'body2'} sx={{ color: '#6d7e84', display: 'block', mb: compactPro ? 0.8 : 1.2 }}>
          {row.name}
        </Typography>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={compactPro ? 0.75 : 1} flexWrap="wrap" useFlexGap sx={{ mb: compactPro ? 0.95 : 1.2 }}>
        <Chip
          label={directionHeadline(direction)}
          size="small"
          sx={{
            bgcolor: directionSoftBackground(direction),
            color: directionColor(direction),
            fontWeight: 800,
          }}
        />
        <Chip label={row.timeframe} size="small" sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 700 }} />
        <Chip label={getPatternTitle(row.detectedPattern)} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }} />
        {hasAdvancedSignals ? (
          <Chip label="Signaux avancés" size="small" sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800, border: '1px solid rgba(31,122,77,0.22)' }} />
        ) : null}
        {row?.hasAnnotatedImage ? (
          <Chip label="Image annotée" size="small" sx={{ bgcolor: 'rgba(35,84,110,0.12)', color: '#23546e', fontWeight: 800, border: '1px solid rgba(35,84,110,0.2)' }} />
        ) : null}
        <Chip
          label={`Volume ${volumeStrength.label}`}
          size="small"
          sx={{
            bgcolor: volumeStrength.background,
            color: volumeStrength.tone,
            fontWeight: 800,
          }}
        />
        <Chip
          label={`Grade ${convictionGrade.tier}`}
          size="small"
          sx={{
            bgcolor: convictionGrade.background,
            color: convictionGrade.tone,
            fontWeight: 900,
            border: `1px solid ${convictionGrade.tone}22`,
          }}
        />
        {row.signalFusionState ? (
          <Chip
            label={formatSignalFusionState(row.signalFusionState)}
            size="small"
            sx={{
              bgcolor: signalFusionPalette.background,
              color: signalFusionPalette.tone,
              fontWeight: 800,
              border: `1px solid ${signalFusionPalette.border}`,
            }}
          />
        ) : null}
      </Stack>

      <Typography
        variant={compactPro ? 'body2' : 'body1'}
        sx={{
          color: '#30444c',
          lineHeight: compactPro ? 1.65 : 1.75,
          mb: compactPro ? 1.1 : 1.6,
          display: '-webkit-box',
          WebkitLineClamp: compactPro ? (dominant ? 2 : 3) : (dominant ? 3 : 4),
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {row.aiSummary}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: compactPro
            ? { xs: 'repeat(2, minmax(0, 1fr))', md: dominant ? 'repeat(4, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', xl: dominant ? 'repeat(8, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))' }
            : { xs: 'repeat(2, minmax(0, 1fr))', md: dominant ? 'repeat(8, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))' },
          gap: compactPro ? 0.7 : 1,
          mb: compactPro ? 1.1 : 1.7,
        }}
      >
        {[
          { label: 'Volume', value: volumeStrength.label, tone: volumeStrength.tone },
          { label: 'Confiance', value: `${formatConfidence(row.confidence)}%`, tone: '#173f5f' },
          { label: 'Conviction', value: formatConvictionValue(row), tone: signalFusionPalette.tone },
          { label: 'Grade', value: convictionGrade.tier, tone: convictionGrade.tone },
          { label: 'Objectif', value: formatPercentMove(row.targetPrice, row.breakoutLevel), tone: '#1f7a4d' },
          { label: 'R/R', value: formatRatio(row.riskRewardRatio), tone: '#7b5b10' },
          { label: 'Score', value: row.scoreGlobal, tone: '#17323c' },
          { label: 'Score MTF', value: isFiniteNumber(row.multiTimeframeScore) ? row.multiTimeframeScore : 'n/a', tone: '#1d6f66' },
        ].map((item) => (
          <Paper
            key={`${row.symbol}-${item.label}`}
            elevation={0}
            sx={{
              p: compactPro ? 0.95 : 1.3,
              borderRadius: compactPro ? 2.4 : 3,
              background: '#fffdf8',
              border: `1px solid ${item.tone}18`,
            }}
          >
            <Typography variant="caption" sx={{ color: '#6d7e84', fontSize: compactPro ? '0.65rem' : undefined, lineHeight: compactPro ? 1.1 : undefined }}>{item.label}</Typography>
            <Typography variant={compactPro ? 'subtitle2' : 'h6'} sx={{ color: item.tone, fontWeight: 900, lineHeight: compactPro ? 1.1 : undefined }}>{item.value}</Typography>
          </Paper>
        ))}
      </Box>

      <MultiTimeframeBreakdown row={row} compact sx={{ mb: compactPro ? 0.95 : 1.6 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 'auto' }}>
        <Typography variant="caption" sx={{ color: '#6d7e84', fontWeight: 700 }}>
          {formatDateTime(row.analysisTimestamp)}
        </Typography>
        <Button
          variant="contained"
          onClick={onFocus}
          sx={{
            borderRadius: 99,
            fontWeight: 800,
            bgcolor: '#17323c',
            ':hover': { bgcolor: '#0f2329' },
          }}
        >
          Passer au cockpit
        </Button>
      </Stack>
    </Paper>
  );
};

const ScannerRowCard = ({ row, expanded, highlight, compactPro = false, onToggle, onFocus, onOpenLexicon }) => {
  const direction = deriveRowDirection(row);
  const riskLevel = computeRiskLevel(row.fakeoutRisk, row.riskRewardRatio, row.targetConfidence, row.fakeoutRiskAdjusted);
  const breakdown = buildConfidenceBreakdown(scannerRowToAnalysis(row), direction);
  const volumeStrength = buildVolumeStrength(row);
  const hasAdvancedSignals = hasAdvancedSignalCoverage(row);
  const convictionScore = resolveEffectiveConvictionScore(row);
  const convictionGrade = buildConvictionGrade(row, direction);
  const signalFusionPalette = buildSignalFusionPalette(row.signalFusionState, convictionScore);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: highlight ? '1px solid rgba(227,180,56,0.45)' : '1px solid rgba(16,37,43,0.08)',
        background: highlight ? 'linear-gradient(180deg, #fffaf0 0%, #fff 100%)' : '#fff',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: compactPro ? { xs: 1.7, md: 1.9 } : 2.2, minHeight: compactPro ? { xs: 'auto', xl: 230 } : 'auto' }}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={compactPro ? 1.4 : 2} justifyContent="space-between" alignItems={{ xs: 'stretch', xl: 'center' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={compactPro ? 1.35 : 2} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ flex: 1, minWidth: 0 }}>
            <Paper
              elevation={0}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: highlight ? '#f3c65c' : '#eef4f6',
                color: '#17323c',
                fontWeight: 900,
                fontSize: '1rem',
              }}
            >
              {row.priorityRank}
            </Paper>

            <Box sx={{ minWidth: 0 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Typography variant={compactPro ? 'subtitle1' : 'h6'} sx={{ fontWeight: 900, color: '#17323c' }}>
                  {row.symbol}
                </Typography>
                <Typography variant={compactPro ? 'caption' : 'body2'} sx={{ color: '#6b7a80' }}>
                  {row.name}
                </Typography>
                <Chip label={row.timeframe} size="small" sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 700 }} />
                {highlight && <Chip label="Setup prioritaire" size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800 }} />}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={compactPro ? 0.75 : 1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: compactPro ? 0.45 : 0.7 }}>
                <Chip
                  label={getPatternTitle(row.detectedPattern)}
                  size="small"
                  sx={{
                    bgcolor: directionSoftBackground(direction),
                    color: directionColor(direction),
                    fontWeight: 800,
                  }}
                />
                <Chip label={directionLabel(direction)} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }} />
                {hasAdvancedSignals ? (
                  <Chip label="Signaux avancés" size="small" sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800, border: '1px solid rgba(31,122,77,0.22)' }} />
                ) : null}
                {row.signalFusionState ? (
                  <Chip
                    label={formatSignalFusionState(row.signalFusionState)}
                    size="small"
                    sx={{
                      bgcolor: signalFusionPalette.background,
                      color: signalFusionPalette.tone,
                      fontWeight: 800,
                      border: `1px solid ${signalFusionPalette.border}`,
                    }}
                  />
                ) : null}
                <Tooltip title={renderConvictionGradeTooltip(convictionGrade, onOpenLexicon)} arrow>
                  <Chip
                    label={`Grade ${convictionGrade.tier}`}
                    size="small"
                    sx={{
                      bgcolor: convictionGrade.background,
                      color: convictionGrade.tone,
                      fontWeight: 900,
                      border: `1px solid ${convictionGrade.tone}22`,
                    }}
                  />
                </Tooltip>
                <Typography variant="body2" sx={{ color: '#6f7f85' }}>
                  {formatDateTime(row.analysisTimestamp)}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack spacing={compactPro ? 0.9 : 1.2} sx={{ width: { xs: '100%', xl: 'min(100%, 560px)' }, minWidth: 0, flexShrink: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: compactPro
                  ? { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                  : { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                gap: compactPro ? 0.8 : 1.2,
              }}
            >
              <StatBadge label="Confiance" value={`${formatConfidence(row.confidence)}%`} accent="#173f5f" compact={compactPro} />
              <StatBadge
                label="Conviction"
                value={formatConvictionValue(row)}
                accent={signalFusionPalette.tone}
                compact={compactPro}
              />
              <StatBadge
                label="Grade"
                value={convictionGrade.tier}
                accent={convictionGrade.tone}
                compact={compactPro}
                tooltip={renderConvictionGradeTooltip(convictionGrade, onOpenLexicon)}
              />
              <StatBadge
                label="R/R"
                value={formatRatio(row.riskRewardRatio)}
                accent="#7b5b10"
                compact={compactPro}
                tooltip={formatRiskRewardTooltip(row.riskRewardRatio)}
              />
              <StatBadge label="Hausse / baisse" value={`${row.pumpProbability}% / ${row.dumpProbability}%`} accent={directionColor(direction)} compact={compactPro} />
              <StatBadge label="Volume" value={volumeStrength.label} accent={volumeStrength.tone} compact={compactPro} />
              <StatBadge label="Score" value={row.scoreGlobal} accent="#17323c" compact={compactPro} />
              <StatBadge label="Score MTF" value={isFiniteNumber(row.multiTimeframeScore) ? row.multiTimeframeScore : 'n/a'} accent="#1d6f66" compact={compactPro} />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={onFocus}
                sx={{ borderRadius: 99, fontWeight: 700, bgcolor: '#17323c', ':hover': { bgcolor: '#0f2329' } }}
              >
                Passer au cockpit
              </Button>
              <IconButton onClick={onToggle} sx={{ border: '1px solid rgba(16,37,43,0.08)' }}>
                {expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 2.4, background: '#fcfbf7' }}>
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.4} alignItems="stretch">
            <Box sx={{ flex: 1.15, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>
                Résumé IA / pourquoi ce signal ?
              </Typography>
              <Typography variant="body1" sx={{ color: '#455860', lineHeight: 1.85, mb: 2 }}>
                {row.aiSummary}
              </Typography>
              {row.aiNarrative ? (
                <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.75, mb: 1.4 }}>
                  {row.aiNarrative}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1.3 }}>
                {isFiniteNumber(row.convictionScore) ? (
                  <Chip
                    label={`Conviction ${Math.round(row.convictionScore)}/100`}
                    size="small"
                    sx={{
                      bgcolor: signalFusionPalette.background,
                      color: signalFusionPalette.tone,
                      fontWeight: 800,
                      border: `1px solid ${signalFusionPalette.border}`,
                    }}
                  />
                ) : null}
                {row.divergenceType ? (
                  <Chip label={`Div ${formatDivergenceTypeLabel(row.divergenceType)}`} size="small" sx={{ bgcolor: '#f5f0e6', color: '#17323c', fontWeight: 700 }} />
                ) : null}
                {row.momentumState ? (
                  <Chip label={`Mom ${formatMomentumStateLabel(row.momentumState)}`} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }} />
                ) : null}
              </Stack>
              <Chip
                label={`Niveau de risque: ${riskLevel.label}`}
                sx={{
                  bgcolor: riskLevel.background,
                  color: riskLevel.tone,
                  fontWeight: 800,
                  border: `1px solid ${riskLevel.tone}22`,
                }}
              />
              <Typography variant="body2" sx={{ color: '#68787f', lineHeight: 1.7, mt: 1 }}>
                {riskLevel.description}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2, mb: 1.8 }}>
                <SignalPillar
                  label="Cassure"
                  value={formatPriceLevel(row.breakoutLevel)}
                  helper="Seuil à confirmer pour valider le scénario."
                  tone="#214f77"
                  icon={direction === 'BEARISH' ? <SouthEastRoundedIcon fontSize="small" /> : <NorthEastRoundedIcon fontSize="small" />}
                />
                <SignalPillar
                  label="Cible"
                  value={formatPriceLevel(row.targetPrice)}
                  helper="Projection théorique une fois la cassure enclenchée."
                  tone="#1f7a4d"
                  icon={<NorthEastRoundedIcon fontSize="small" />}
                />
                <SignalPillar
                  label="Stop"
                  value={formatPriceLevel(row.stopInvalidation)}
                  helper="Point d’invalidation à surveiller en priorité."
                  tone="#aa3d33"
                  icon={<SouthEastRoundedIcon fontSize="small" />}
                />
              </Box>

              <Stack spacing={1.3}>
                {breakdown.map((item) => (
                  <Box key={`${row.symbol}-${item.label}`}>
                    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.35 }}>
                      <Typography variant="caption" sx={{ color: '#5e7077', fontWeight: 700 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: item.accent, fontWeight: 800 }}>
                        {item.display}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={item.value}
                      sx={{
                        height: 8,
                        borderRadius: 99,
                        bgcolor: 'rgba(16,37,43,0.08)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 99,
                          bgcolor: item.accent,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>

          <MultiTimeframeBreakdown row={row} sx={{ mt: 2.1 }} />

          {extractPatternDiagnostics(scannerRowToAnalysis(row)) ? (
            <TradeValidationPanel analysis={scannerRowToAnalysis(row)} compact sx={{ mt: 2.2 }} />
          ) : supportsTradeValidation(row.detectedPattern) ? (
            <Alert severity="info" sx={{ mt: 2.2 }}>
              {`Ce replay scanner montre ${tradeValidationPatternLabel(row.detectedPattern)}, mais les sous-scores de validation détaillée n’ont pas été remontés par le moteur sélectionné.`}
            </Alert>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
};

const ValidationMetricCard = ({ label, display, helper, value, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.6,
      borderRadius: 3,
      background: '#fffdf8',
      border: `1px solid ${accent}22`,
    }}
  >
    <Typography variant="overline" sx={{ color: '#667880', letterSpacing: '0.08em' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 900, color: accent, mb: 0.7 }}>
      {display}
    </Typography>
    <LinearProgress
      variant="determinate"
      value={value}
      sx={{
        height: 8,
        borderRadius: 99,
        bgcolor: 'rgba(16,37,43,0.08)',
        '& .MuiLinearProgress-bar': {
          borderRadius: 99,
          bgcolor: accent,
        },
      }}
    />
    <Typography variant="caption" sx={{ display: 'block', color: '#5f7279', mt: 0.7, lineHeight: 1.6 }}>
      {helper}
    </Typography>
  </Paper>
);

const TradeValidationPanel = ({ analysis, compact = false, sx = {} }) => {
  const diagnostics = extractPatternDiagnostics(analysis);
  if (!diagnostics) {
    return null;
  }

  const validation = diagnostics.validation || {};
  const profile = getTradeValidationProfile(analysis);
  const breakdown = buildTradeValidationBreakdown(analysis);
  const meta = buildTradeValidationMeta(analysis);
  const thresholds = buildTradeValidationThresholds(analysis);
  const isValidated = (validation.score ?? 0) >= (validation.threshold ?? 0) && !validation.hardReject;
  const statusTone = isValidated ? '#1f7a4d' : '#aa3d33';
  const statusBackground = isValidated ? 'rgba(31,122,77,0.12)' : 'rgba(170,61,51,0.12)';

  return (
    <Paper
      sx={{
        p: compact ? 2 : 2.5,
        borderRadius: 4,
        background: '#fff',
        border: '1px solid rgba(16,37,43,0.08)',
        ...sx,
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 1.6 }}>
        <Box sx={{ maxWidth: compact ? '100%' : 720 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
            <InsightsRoundedIcon sx={{ color: '#17323c' }} />
            <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ fontWeight: 800, color: '#17323c' }}>
              Validation trade-first
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.7 }}>
            {`Principe produit: ${diagnostics.strategyPrinciple} ${profile.descriptionSuffix}`}
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignSelf="flex-start">
          <Chip
            label={isValidated ? `Score valide ${formatNormalizedScore(validation.score)}` : `Score sous seuil ${formatNormalizedScore(validation.score)}`}
            sx={{
              bgcolor: statusBackground,
              color: statusTone,
              fontWeight: 800,
              border: `1px solid ${statusTone}22`,
            }}
          />
          <Chip label={`Seuil ${formatNormalizedScore(validation.threshold)}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }} />
          <Chip label={`R/R ${formatRatio(validation.riskRewardRatio)}`} sx={{ bgcolor: '#f2ecde', color: '#17323c', fontWeight: 700 }} />
          <Chip label={`Fakeout ${validation.estimatedFakeoutRisk ?? 'n/a'}%`} sx={{ bgcolor: '#f8ede6', color: '#8f6b10', fontWeight: 700 }} />
        </Stack>
      </Stack>

      {meta.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.6 }}>
          {meta.map((item) => (
            <StatBadge
              key={`${analysis?.symbol || analysis?.patternDetected || 'meta'}-${item.label}`}
              label={item.label}
              value={item.value}
              accent={item.label === 'Trade Quality' ? statusTone : '#17323c'}
            />
          ))}
        </Stack>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
          gap: 1.2,
          mb: thresholds.length > 0 ? 1.8 : 0,
        }}
      >
        {breakdown.map((item) => (
          <ValidationMetricCard
            key={`${analysis?.symbol || analysis?.patternDetected || 'validation'}-${item.label}`}
            label={item.label}
            display={item.display}
            helper={item.helper}
            value={item.value}
            accent={item.accent}
          />
        ))}
      </Box>

      {thresholds.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap>
          {thresholds.map((item) => (
            <StatBadge key={`${analysis?.symbol || analysis?.patternDetected || 'threshold'}-${item.label}`} label={item.label} value={item.value} accent="#17323c" />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const InsightPill = ({ label, value, palette }) => (
  <Box
    sx={{
      px: 1.2,
      py: 0.95,
      borderRadius: 2.5,
      minWidth: 110,
      background: palette.background,
      border: `1px solid ${palette.border}`,
    }}
  >
    <Typography variant="caption" sx={{ color: '#627278', fontWeight: 700, letterSpacing: '0.04em' }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" sx={{ color: palette.tone, fontWeight: 900 }}>
      {value}
    </Typography>
  </Box>
);

const MultiTimeframeInsightsSummary = ({ insights, currentTimeframe, compact = false }) => {
  if (!insights) {
    return null;
  }

  const alignmentPalette = resolveInsightPalette(insights.alignmentState, insights.alignmentScore);
  const convictionPalette = resolveInsightPalette(
    insights.convictionScore >= 80 ? 'STRONG' : insights.convictionScore >= 64 ? 'MIXED' : 'WEAK',
    insights.convictionScore,
  );
  const volumePalette = resolveInsightPalette(insights.volumeAlignmentState, insights.volumeAlignmentScore);
  const dominantDirection = insights.dominantDirection || 'NEUTRAL';
  const activeTimeframe = currentTimeframe || insights.dominantTimeframe || '4H';
  const conflictFlags = Array.isArray(insights.conflictFlags) ? insights.conflictFlags.filter(Boolean) : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.6 : 1.9,
        borderRadius: 3.5,
        border: `1px solid ${alignmentPalette.border}`,
        background: alignmentPalette.surface,
      }}
    >
      <Stack spacing={compact ? 1.15 : 1.35}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between">
          <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
            <InsightPill label="Alignment" value={formatInsightScore(insights.alignmentScore)} palette={alignmentPalette} />
            <InsightPill label="Conviction" value={formatInsightScore(insights.convictionScore)} palette={convictionPalette} />
            <InsightPill label="Volume" value={formatInsightScore(insights.volumeAlignmentScore)} palette={volumePalette} />
          </Stack>

          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap alignSelf="flex-start">
            <Chip
              label={`${directionHeadline(dominantDirection)} · TF pilote ${insights.dominantTimeframe || activeTimeframe}`}
              size="small"
              sx={{
                bgcolor: directionSoftBackground(dominantDirection),
                color: directionColor(dominantDirection),
                fontWeight: 800,
              }}
            />
            <Chip
              label={`Fenêtre active ${activeTimeframe}`}
              size="small"
              sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800 }}
            />
          </Stack>
        </Stack>

        <Typography variant={compact ? 'body2' : 'body1'} sx={{ color: '#4d6068', lineHeight: compact ? 1.7 : 1.8 }}>
          {insights.multiTfNarrative || 'Le contexte inter-timeframe n’a pas encore produit de narrative exploitable.'}
        </Typography>

        {conflictFlags.length ? (
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {conflictFlags.map((flag) => (
              <Chip
                key={flag}
                label={flag}
                size="small"
                sx={{ bgcolor: 'rgba(170,61,51,0.1)', color: '#8a342c', fontWeight: 700 }}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};

const CalibrationReplayCard = ({ result, highlight }) => {
  const analysis = normalizePatternAnalysis(result?.analysis);
  if (!analysis) {
    return null;
  }

  const direction = analysis.direction || 'NEUTRAL';
  const diagnostics = extractPatternDiagnostics(analysis);
  const signalFusionPalette = buildSignalFusionPalette(analysis?.signalFusionState, analysis?.convictionScore);
  const hasSignalConvergence = Boolean(
    analysis?.signalFusionState
      || isFiniteNumber(analysis?.convictionScore)
      || analysis?.advancedDivergence
      || analysis?.advancedMomentum
      || analysis?.advancedCandlestick,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: 4,
        border: highlight ? '1px solid rgba(227,180,56,0.45)' : '1px solid rgba(16,37,43,0.08)',
        background: highlight ? 'linear-gradient(180deg, #fffaf0 0%, #fff 100%)' : '#fff',
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="overline" sx={{ color: '#6b7a80', letterSpacing: '0.12em' }}>
            Replay {analysis.timeframe}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#17323c' }}>
              {getPatternTitle(analysis.patternDetected)}
            </Typography>
            <Chip
              label={directionLabel(direction)}
              size="small"
              sx={{
                bgcolor: directionSoftBackground(direction),
                color: directionColor(direction),
                fontWeight: 800,
              }}
            />
            {highlight && <Chip label="Timeframe courant" size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800 }} />}
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} flexWrap="wrap" useFlexGap>
          <StatBadge label="Confiance" value={`${formatConfidence(analysis.confidence)}%`} accent="#173f5f" />
          <StatBadge
            label="R/R"
            value={formatRatio(analysis.riskRewardRatio)}
            accent="#264653"
            tooltip={formatRiskRewardTooltip(analysis.riskRewardRatio)}
          />
          <StatBadge label="Source bougies" value={result.candleSource || 'n/a'} accent="#17323c" />
          <StatBadge label="Moteur" value={analysis.analysisSource || 'n/a'} accent="#17323c" />
        </Stack>
      </Stack>

      <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.7, mb: 1.8 }}>
        {analysis.aiSummary}
      </Typography>

      {hasSignalConvergence ? (
        <Box sx={{ mb: 1.8 }}>
          <SignalConvergencePanel analysis={analysis} palette={signalFusionPalette} />
        </Box>
      ) : null}

      {diagnostics ? (
        <TradeValidationPanel analysis={analysis} compact />
      ) : (
        <Alert severity="info">
          Aucun détail trade-first spécifique sur cette fenêtre. Soit le replay ne retient pas un pattern avec diagnostics détaillés, soit le fallback local a pris le relais sans validation enrichie.
        </Alert>
      )}
    </Paper>
  );
};

const MultiTimeframeVisualCard = ({ timeframe, result, highlight, loading }) => {
  const analysis = normalizePatternAnalysis(result?.analysis);
  const sparkline = toReplaySparkline(result);
  const accent = replaySparklineColor(result);
  const direction = analysis?.direction || 'NEUTRAL';
  const overlays = buildReplayOverlayBadges(result);
  const confidenceValue = formatConfidence(analysis?.confidence ?? 0);
  const patternScoreValue = firstFiniteNumber(analysis?.patternScore);

  return (
    <Paper
      elevation={0}
      sx={{
        p: highlight ? 2.05 : 1.8,
        borderRadius: 3.5,
        border: highlight ? '1px solid rgba(227,180,56,0.5)' : '1px solid rgba(16,37,43,0.08)',
        background: highlight ? 'linear-gradient(180deg, #fff9ee 0%, #fffdf8 100%)' : 'rgba(255,255,255,0.92)',
        minHeight: highlight ? 248 : 220,
        boxShadow: highlight ? '0 22px 42px rgba(90,67,13,0.12)' : '0 10px 24px rgba(23,50,60,0.06)',
        transform: highlight ? 'translateY(-4px)' : 'none',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
      }}
    >
      <Stack spacing={1.2} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="overline" sx={{ color: '#6b7a80', letterSpacing: '0.12em', fontWeight: 800 }}>
              {timeframe}
            </Typography>
            <Typography variant={highlight ? 'h5' : 'h6'} sx={{ color: '#17323c', fontWeight: 900 }}>
              {analysis ? getPatternTitle(analysis.patternDetected) : 'Lecture indisponible'}
            </Typography>
            <Typography variant="caption" sx={{ color: directionColor(direction), fontWeight: 900, letterSpacing: '0.04em' }}>
              {directionHeadline(direction)}
            </Typography>
          </Box>

          <Stack spacing={0.75} alignItems="flex-end">
            <Chip
              label={`Conf ${confidenceValue}%`}
              size="small"
              sx={{
                bgcolor: 'rgba(23,50,60,0.08)',
                color: '#17323c',
                fontWeight: 800,
              }}
            />
            {highlight ? <Chip label="Fenêtre active" size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800 }} /> : null}
          </Stack>
        </Stack>

        {loading ? (
          <Stack spacing={1.2} sx={{ flex: 1, justifyContent: 'center' }}>
            <LinearProgress sx={{ height: 8, borderRadius: 99 }} />
            <Typography variant="body2" sx={{ color: '#5a6d74', textAlign: 'center' }}>
              Chargement du replay {timeframe}…
            </Typography>
          </Stack>
        ) : sparkline.length > 1 ? (
          <>
            <Box
              sx={{
                px: 0.5,
                py: 0.6,
                borderRadius: 2.75,
                background: highlight ? 'linear-gradient(180deg, rgba(250,243,222,0.72) 0%, rgba(255,255,255,0.86) 100%)' : 'rgba(245,239,228,0.72)',
                border: highlight ? '1px solid rgba(227,180,56,0.2)' : '1px solid rgba(16,37,43,0.06)',
              }}
            >
              <Sparklines data={sparkline} width={220} height={78} margin={6}>
                <SparklinesLine color={accent} style={{ strokeWidth: 3, fill: 'none' }} />
              </Sparklines>
            </Box>

            <Stack direction="row" justifyContent="space-between" spacing={1.2} alignItems="center">
              <Typography variant="h6" sx={{ color: accent, fontWeight: 900 }}>
                {formatReplayLatestPrice(result)}
              </Typography>
              <Chip
                label={directionLabel(direction)}
                size="small"
                sx={{
                  bgcolor: directionSoftBackground(direction),
                  color: directionColor(direction),
                  fontWeight: 800,
                }}
              />
            </Stack>

            {(patternScoreValue != null || overlays.length) ? (
              <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                {patternScoreValue != null ? (
                  <Chip
                    label={`Score ${Math.round(patternScoreValue)}`}
                    size="small"
                    sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }}
                  />
                ) : null}
                {overlays.map((overlay) => (
                  <Chip
                    key={`${timeframe}-${overlay.label}`}
                    label={overlay.label}
                    size="small"
                    sx={{
                      bgcolor: overlay.background,
                      color: overlay.tone,
                      fontWeight: 800,
                    }}
                  />
                ))}
              </Stack>
            ) : null}

            <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.65 }}>
              {analysis?.aiSummary || 'Aucun résumé IA disponible sur cette fenêtre.'}
            </Typography>
          </>
        ) : (
          <Stack spacing={1.1} sx={{ flex: 1, justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: '#5a6d74', textAlign: 'center' }}>
              Aucun replay exploitable pour {timeframe}.
            </Typography>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ color: '#6f7f85', fontWeight: 700 }}>
            {formatReplayCandleSourceLabel(result?.candleSource)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6f7f85', fontWeight: 700 }}>
            {sparkline.length ? `${sparkline.length} bougies` : '0 bougie'}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default PatternScannerPage;
